import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createRideAudioController } from '../audio/index.ts';
import { PointerEditor } from '../editor/pointer-editor.ts';
import {
  EditorSession,
  ValidationCoordinator,
  buildPresetCandidate,
  buildValidationProfile,
  assessCandidateProject,
  assessFinishToStation,
  connectEndpoints,
  deleteNode,
  duplicateSection,
  measureFinishToStation,
  projectPlotBounds,
  insertNodeAtSection,
  instantiateTemplate,
  moveNodes,
  renameProject as renameProjectOperation,
  setNodeBank,
  setNodeElevation,
  setSectionBehavior as setSectionBehaviorOperation,
  smoothNodes,
  AUTO_BANK_GLOBAL_TOLERANCE_G,
  AUTO_BANK_MINIMUM_IMPROVEMENT_G,
  generateAutoBankCandidates,
  peakLateralGInIntervals,
  alignNodes,
  rotateNodes,
  removeNodeRollWinding,
  validateTrackTopology,
  markSectionsCustom,
  modularEndPose,
  MODULAR_BANK_STATES,
  MODULAR_PITCH_STATES,
  placeModularStation,
  poseForward,
  removeLastModularPiece,
  trackTypeCompatibility,
  newStableId,
  type EditorSelectionState,
  type EditorMode,
  type EditorOverlay,
  type EditorTool,
  type ElementPresetId,
  type ModularTrackShapeId,
  type StationPlacement,
  type TrackType,
  type ValidationProfile,
} from '../editor/index.ts';
import {
  AUTHORED_TRACK_CATALOG,
  appendAuthoredCandidate,
  authoredEndPose,
  authoredSequenceIssue,
  authoredStationGridDelta,
  createAuthoredCandidate,
  fixedPointToWorld,
  getAuthoredDescriptor,
  planAuthoredAutocomplete,
  planAuthoredFinish,
  removeLastAuthoredPiece,
  worldToFixedPoint,
  type AuthoredTrackType,
} from '../builder-v2/index.ts';
import {
  blankCoasterFixture,
  extremeWarningCoasterFixture,
  liftDropCoasterFixture,
  ovalCoasterFixture,
  stallHillCoasterFixture,
  systemsCircuitCoasterFixture,
} from '../fixtures/index.ts';
import {
  FIXED_PHYSICS_STEP,
  createTrackBoundState,
  type SimulationFrame,
  type TrackBoundState,
} from '../physics/index.ts';
import {
  createNamedProjectRepository,
  type DraftPersistenceScope,
  type NamedProjectRepository,
  type StoredDraft,
  type StoredDraftSummary,
} from '../persistence/index.ts';
import { LocalMediaAnalyticsSink } from '../media/analytics.ts';
import { detectMediaCapabilities } from '../media/capabilities.ts';
import type {
  HighlightEditV2,
  MediaArtifact,
  MediaArtifactKind,
  MediaCapabilityProfile,
  MediaExportStatus,
  MediaShareContext,
  ReplayArtifactRepository,
  RideReplayTimelineV1,
} from '../media/index.ts';
import { sampleReplayTraceAtTime } from '../media/sampling.ts';
import type {
  ReactionCameraDevice,
  ReactionCaptureOptions,
  ReactionCapturePhase,
  ReactionCamEligibility,
  ReactionOverlayPlacement,
  ReactionRecordingV1,
} from '../reaction/index.ts';
import type { ReactionCaptureController } from '../reaction/capture.ts';
import type { ReactionAnalyticsEvent } from '../reaction/analytics.ts';
import { createVisualProjectFingerprint } from '../publishing/workspace.ts';
import { createTrackSupportContext, TrainVisual, TrackVisual } from '../ride/track-visual.ts';
import { RideCameraController } from '../ride/camera-controller.ts';
import { RideRunner, type RideSummary } from '../ride/runner.ts';
import {
  BunchaSceneService,
  createCoasterMaterials,
  disposeMaterialSet,
  recommendQualityTier,
} from '../scene/index.ts';
import type { EnvironmentThemeId } from '../scene/index.ts';
import {
  HERO_MASTER_SIZE,
  HERO_CARD_SIZE,
  HERO_OPEN_GRAPH_SIZE,
  fitCoverSourceRect,
} from '../hero/index.ts';
import {
  loadCoasterProject,
  serializeCoasterProject,
  type CoasterProject,
  type AuthoredGridPose,
  type TrackBehavior,
  type TrackEndpointPose,
  type TrackNode,
  type TrackPieceParameters,
} from '../schema/index.ts';
import { TrackCompiler, type TableTrackSampler } from '../track/index.ts';
import type { SemanticSectionType } from '../track/index.ts';
import {
  FullRunValidationCoordinator,
  toAuthoritativeValidationSummary,
  validateFullRide,
  type FullRunValidationResult,
  buildRideCheckPresentation,
} from '../validation/index.ts';
import { transitionMode, type AppMode } from './mode.ts';
import {
  coasterFamilyForProject,
  compileProjectTrackResult,
  initialTrainReferenceDistance,
  type CompiledProjectTrack,
} from './ride-track.ts';
import {
  classifySimulatedIntensity,
  simulatedIntensityScore,
  type SimulatedIntensityLevel,
  type SimulatedIntensitySummary,
} from './ride-intensity.ts';
import type {
  CoasterRuntime,
  RuntimeDiagnostics,
  RuntimeIntensity,
  RuntimeRideResult,
  RuntimeSnapshot,
  RuntimeTelemetry,
  RuntimePreviewState,
  HeroCaptureAsset,
  HeroCaptureBundle,
  PublishingDetails,
  ProjectLoadIntent,
  RecoveredDraftIdentity,
  RuntimeBuildOptions,
  RuntimeBuildSelection,
  TestSpeed,
} from './runtime-types.ts';

const MPH_PER_MPS = 2.2369362921;
const EDITOR_CAMERA_FOV = 42;
const MODULAR_BUILD_SHAPES: readonly ModularTrackShapeId[] = [
  'straight',
  'turn_left',
  'turn_right',
  'slope_up',
  'slope_down',
  'bank_left',
  'bank_right',
  'bank_level',
  'loop_left',
  'loop_right',
  'roll_left',
  'roll_right',
  'corkscrew_left',
  'corkscrew_right',
];
const TRACK_TYPES: readonly TrackType[] = ['normal', 'lift', 'launch', 'brake', 'photo'];

interface InternalRouteGuidePlan {
  readonly project: CoasterProject;
  readonly descriptorIds: readonly string[];
  readonly labels: readonly string[];
  readonly distanceM: number;
}

function initialMediaShareContext(project: CoasterProject): MediaShareContext {
  return {
    creatorDisplayName: project.metadata.creator.displayName,
    publicCoasterId: null,
    publicUrl: null,
  };
}

function downloadArtifact(artifact: MediaArtifact): void {
  const url = URL.createObjectURL(artifact.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = artifact.fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function imageClipboardAvailable(): boolean {
  return (
    typeof ClipboardItem !== 'undefined' &&
    Boolean(navigator.clipboard && typeof navigator.clipboard.write === 'function')
  );
}

async function imageArtifactAsPng(artifact: MediaArtifact): Promise<Blob> {
  if (artifact.mimeType === 'image/png') return artifact.blob;
  const bitmap = await createImageBitmap(artifact.blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = artifact.width;
    canvas.height = artifact.height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('The image clipboard is unavailable.');
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const png = await canvasBlob(canvas, 'image/png');
    canvas.width = 1;
    canvas.height = 1;
    if (!png) throw new Error('The image could not be prepared for copying.');
    return png;
  } finally {
    bitmap.close();
  }
}

function normalizedDraftTitle(value: string): string {
  return value.trim().normalize('NFKC').toLocaleLowerCase();
}

function nextNumberedTitle(base: string, drafts: readonly StoredDraftSummary[]): string {
  const normalizedBase = normalizedDraftTitle(base);
  let highest = 0;
  for (const draft of drafts) {
    const match = draft.title
      .trim()
      .normalize('NFKC')
      .match(new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(\\d+)$`, 'i'));
    if (match && normalizedDraftTitle(draft.title).startsWith(`${normalizedBase} `))
      highest = Math.max(highest, Number(match[1]));
  }
  return `${base} ${highest + 1}`.slice(0, 80);
}

function pieceDefinitionLabel(definitionId: string): string {
  const labels: Record<string, string> = {
    straight: 'Straight',
    turn_left: 'Left Turn',
    turn_right: 'Right Turn',
    level_to_slope: 'Slope Transition',
    slope_to_level: 'Level Transition',
    bank_in: 'Bank In',
    bank_out: 'Bank Out',
    hill: 'Hill',
    drop: 'Drop',
    station: 'Station',
    lift: 'Lift Hill',
    launch: 'Launch',
    brake: 'Brakes',
    photo: 'Photo Section',
    loop: 'Vertical Loop',
    loop_left: 'Loop Left',
    loop_right: 'Loop Right',
    roll_left: 'Left Roll',
    roll_right: 'Right Roll',
    corkscrew_left: 'Left Corkscrew',
    corkscrew_right: 'Right Corkscrew',
    custom: 'Custom Piece',
  };
  return labels[definitionId] ?? 'Track Piece';
}

function canvasBlob(canvas: HTMLCanvasElement, type: 'image/webp' | 'image/png', quality?: number) {
  return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
}

async function renderHeroDerivative(
  source: Blob,
  width: number,
  height: number,
): Promise<HeroCaptureAsset> {
  const bitmap = await createImageBitmap(source);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('The hero image canvas is unavailable.');
    const crop = fitCoverSourceRect(bitmap.width, bitmap.height, width, height);
    context.drawImage(bitmap, crop.x, crop.y, crop.width, crop.height, 0, 0, width, height);
    const webp = await canvasBlob(canvas, 'image/webp', 0.9);
    if (webp?.type === 'image/webp')
      return { blob: webp, width, height, contentType: 'image/webp' };
    const png = await canvasBlob(canvas, 'image/png');
    if (!png) throw new Error('The hero image could not be encoded.');
    return { blob: png, width, height, contentType: 'image/png' };
  } finally {
    bitmap.close();
  }
}

interface ProjectPreview {
  readonly kind: RuntimePreviewState['kind'];
  readonly presetId: ElementPresetId | null;
  readonly label: string;
  readonly command: string;
  readonly acceptLabel: string;
  readonly project: CoasterProject;
  readonly message: string;
  readonly severity: RuntimePreviewState['severity'];
  readonly canAccept: boolean;
  readonly inline: boolean;
  readonly issues: RuntimePreviewState['issues'];
  readonly affectedNodeIds: readonly string[];
  readonly affectedSectionIds: readonly string[];
  readonly minPlotMarginM: number | null;
  readonly finishImpact?: RuntimePreviewState['finishImpact'];
  readonly generatedLengthM?: number | null;
  readonly addedNodeCount?: number | null;
  readonly autoBankComparison?: RuntimePreviewState['autoBankComparison'];
  readonly focusNodeId?: string;
  readonly authoredDescriptorId?: string;
}

const INITIAL_INTENSITY: RuntimeIntensity = {
  ...classifySimulatedIntensity({ vertical: 1, lateral: 0, longitudinal: 0 }),
  progressPercent: 0,
};

function fixtureFor(key: 'systems' | 'stall' | 'extreme'): CoasterProject {
  return key === 'stall'
    ? stallHillCoasterFixture
    : key === 'extreme'
      ? extremeWarningCoasterFixture
      : systemsCircuitCoasterFixture;
}

function sectionLabel(type: SemanticSectionType | null): string {
  switch (type) {
    case 'station':
      return 'Station';
    case 'lift':
      return 'Lift';
    case 'launch':
      return 'Launch';
    case 'brake':
      return 'Brake';
    case 'normal':
      return 'Coasting';
    default:
      return 'Coasting';
  }
}

function compositePieceLabel(shapeLabel: string, trackType: TrackType): string {
  if (trackType === 'normal') return shapeLabel;
  const typeLabel =
    trackType === 'lift'
      ? 'Lift'
      : trackType === 'launch'
        ? 'Launch'
        : trackType === 'brake'
          ? 'Brakes'
          : 'Photo';
  return `${typeLabel} ${shapeLabel}`;
}

function cloneProject(project: CoasterProject): CoasterProject {
  const loaded = loadCoasterProject(structuredClone(project));
  if (!loaded.ok) throw new Error(loaded.error.message);
  return loaded.project;
}

function recoveredDraftIdentity(draft: StoredDraft): RecoveredDraftIdentity {
  return {
    projectId: draft.projectId,
    title: draft.title,
    savedAt: draft.savedAt,
    creatorDisplayName: draft.project.metadata.creator.displayName,
    relationship: draft.project.lineage ? 'remix' : 'original',
  };
}

function nodeSelection(
  nodeIds: readonly string[],
  primaryNodeId = nodeIds.at(-1) ?? null,
): EditorSelectionState {
  return {
    nodeIds: [...nodeIds],
    primaryNodeId,
    sectionId: null,
    sceneryIds: [],
    primarySceneryId: null,
  };
}

function sectionSelection(sectionId: string | null): EditorSelectionState {
  return {
    nodeIds: [],
    primaryNodeId: null,
    sectionId,
    sceneryIds: [],
    primarySceneryId: null,
  };
}

function percentile(values: readonly number[], fraction: number): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * fraction))] ?? 0;
}

function asColor(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

function finishEligible(guidance: ReturnType<typeof measureFinishToStation>): boolean {
  return guidance.directJoinEligible || guidance.generatedFinishEligible;
}

function finishImpact(
  before: CoasterProject,
  after: CoasterProject,
): RuntimePreviewState['finishImpact'] {
  if (before.track.closed || after.track.closed || before.track.nodes.length < 2) return null;
  const beforeGuidance = measureFinishToStation(before);
  const afterGuidance = measureFinishToStation(after);
  return {
    distanceBeforeM: beforeGuidance.distanceM,
    distanceAfterM: afterGuidance.distanceM,
    headingBeforeDegrees: beforeGuidance.turnDegrees,
    headingAfterDegrees: afterGuidance.turnDegrees,
    finishEligibleBefore: finishEligible(beforeGuidance),
    finishEligibleAfter: finishEligible(afterGuidance),
  };
}

function compiledTrackLength(project: CoasterProject): number | null {
  if (project.track.nodes.length < 4) return null;
  try {
    return compileProjectTrackResult(project, new TrackCompiler()).sampler.length;
  } catch {
    return null;
  }
}

function qualityTier(): 'low' | 'medium' | 'high' {
  const navigatorWithMemory = navigator as Navigator & { deviceMemory?: number };
  return recommendQualityTier({
    mobile: matchMedia('(pointer: coarse)').matches || innerWidth < 720,
    reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    deviceMemoryGb: navigatorWithMemory.deviceMemory,
  });
}

function initialReactionEligibility(options: {
  readonly platformAvailable: boolean;
  readonly eligibleAccount?: boolean;
}): ReactionCamEligibility {
  if (!options.platformAvailable)
    return {
      state: 'platform_unavailable',
      reason: 'Reaction Cam is not enabled for this runtime.',
    };
  if (options.eligibleAccount === false)
    return {
      state: 'ineligible_account',
      reason: 'Reaction Cam is not available for this account.',
    };
  if (!globalThis.isSecureContext)
    return {
      state: 'insecure_context',
      reason: 'Reaction Cam requires a secure HTTPS connection.',
    };
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined')
    return {
      state: 'unsupported_browser',
      reason: 'This browser cannot record Reaction Cam video.',
    };
  return { state: 'available', reason: 'Reaction Cam is available.' };
}

class ProductionCoasterRuntime implements CoasterRuntime {
  private readonly listeners = new Set<(snapshot: RuntimeSnapshot) => void>();
  private readonly scene: BunchaSceneService;
  private readonly compiler = new TrackCompiler();
  private readonly audio = createRideAudioController();
  private readonly repository: NamedProjectRepository;
  private mediaRepository: ReplayArtifactRepository | null = null;
  private readonly mediaPersistenceScope?: DraftPersistenceScope;
  private readonly mediaAnalytics = new LocalMediaAnalyticsSink();
  private readonly reactionAnalyticsEvents: ReactionAnalyticsEvent[] = [];
  private readonly validationCoordinator = new ValidationCoordinator();
  private readonly fullValidationCoordinator = new FullRunValidationCoordinator();
  private readonly autoBankValidationCoordinator = new FullRunValidationCoordinator();
  private readonly session = new EditorSession(cloneProject(systemsCircuitCoasterFixture));
  private readonly controls: OrbitControls;
  private readonly content = new THREE.Group();
  private readonly handleRoot = new THREE.Group();
  private readonly debugRoot = new THREE.Group();
  private readonly authoredSceneryRoot = new THREE.Group();
  private readonly validationRoot = new THREE.Group();
  private readonly buildGuideRoot = new THREE.Group();
  private readonly coasterMaterials;
  private readonly ghostMaterials;
  private readonly pointerEditor: PointerEditor;
  private readonly resizeObserver: ResizeObserver;
  private activeFixture: 'systems' | 'stall' | 'extreme' = 'systems';
  private resetProject = cloneProject(systemsCircuitCoasterFixture);
  private lastValidProject = cloneProject(systemsCircuitCoasterFixture);
  private compiledTrack: CompiledProjectTrack | null = null;
  private sampler: TableTrackSampler | null = null;
  private trackVisual: TrackVisual | null = null;
  private presetPreviewVisual: TrackVisual | null = null;
  private trainVisual!: TrainVisual;
  private cameraController!: RideCameraController;
  private rideRunner: RideRunner | null = null;
  private lastRideSummary: RideSummary | null = null;
  private mediaTimeline: RideReplayTimelineV1 | null = null;
  private mediaTimelineSource: 'live' | 'restored' | null = null;
  private mediaEdits: readonly [HighlightEditV2, HighlightEditV2, HighlightEditV2] | null = null;
  private mediaShareContext: MediaShareContext = initialMediaShareContext(
    systemsCircuitCoasterFixture,
  );
  private readonly mediaCapabilities: MediaCapabilityProfile = detectMediaCapabilities();
  private readonly mediaArtifacts = new Map<string, MediaArtifact>();
  private readonly mediaArtifactUrls = new Map<string, string>();
  private mediaOpen = false;
  private mediaSelectedAlternative: 0 | 1 | 2 = 0;
  private mediaSelectedPosterId: string | null = null;
  private readonly mediaPosterThumbnailUrls = new Map<string, string>();
  private mediaPosterGenerationToken = 0;
  private mediaShareOutcome: 'idle' | 'completed' | 'cancelled' | 'failed' = 'idle';
  private mediaStatus: MediaExportStatus = 'idle';
  private mediaProgress = 0;
  private mediaIncludeAudio = location.hash !== '#/debug';
  private mediaMessage = 'Complete a ride to create a highlight.';
  private mediaExportAbort: AbortController | null = null;
  private mediaRestoreToken = 0;
  private mediaGenerationToken = 0;
  private mediaPersistenceQueue: Promise<void> = Promise.resolve();
  private mediaRequiresNewRide = false;
  private reactionEligibility: ReactionCamEligibility;
  private reactionController: ReactionCaptureController | null = null;
  private reactionSetupOpen = false;
  private reactionPhase: ReactionCapturePhase = 'idle';
  private reactionOptions: ReactionCaptureOptions = {
    includeMicrophone: false,
    exportMirrored: false,
    placement: 'top-right',
  };
  private reactionDevices: readonly ReactionCameraDevice[] = [];
  private reactionSelectedDeviceId: string | null = null;
  private reactionPreviewStream: MediaStream | null = null;
  private reactionMicrophoneIncluded = false;
  private reactionCountdown = 0;
  private reactionCountdownToken = 0;
  private reactionCaptureToken = 0;
  private reactionRecordingStartedAtMs = 0;
  private reactionRecording: ReactionRecordingV1 | null = null;
  private reactionRawUrl: string | null = null;
  private reactionMessage = 'Reaction Cam is optional.';
  private reactionInterruptionReason: string | null = null;
  private reactionRideCamera: 'first-person' | 'third-person' = 'first-person';
  private reactionIncludeInExport = true;
  private reactionIncludeMicrophoneInExport = true;
  private readonly reactionSynthetic: boolean;
  private fullValidation: FullRunValidationResult | null = null;
  private fullValidationRevision = -1;
  private fullValidationRunAt: string | null = null;
  private fullValidationPending = false;
  private fullValidationTimer: number | null = null;
  private fullValidationToken = 0;
  private replayElapsedSeconds = 0;
  private replayPaused = false;
  private rideEventCursor = 0;
  private audioSectionType: SemanticSectionType | null = null;
  private audioSectionId: string | undefined;
  private audioIntensityLevel: SimulatedIntensityLevel = 'comfortable';
  private simulationState: TrackBoundState = createTrackBoundState(0, 2);
  private simulationFrame: SimulationFrame | null = null;
  private mode: AppMode = 'menu';
  private status = 'Ready to build.';
  private debugVisible = location.hash === '#/debug';
  private persistenceAvailable = true;
  private editCommitTimer: number | null = null;
  private gestureOpen = false;
  private saveSequence = 0;
  private saveQueue: Promise<void> = Promise.resolve();
  private drafts: StoredDraftSummary[] = [];
  private lastOpenedProjectId: string | null = null;
  private recoveredDraft: RecoveredDraftIdentity | null = null;
  private persistenceMode: 'editable' | 'readonly' = 'editable';
  private validationProfile: ValidationProfile = buildValidationProfile(
    systemsCircuitCoasterFixture,
    0,
  );
  private presetPreview: ProjectPreview | null = null;
  private stationPlacement: StationPlacement = 'near';
  private stationPlacementActive = false;
  private stationPlacementPosition = { x: -32, y: 4, z: 16 };
  private stationPlacementHeading: 0 | 2 | 4 | 6 = 0;
  private stationPlacementValid = true;
  private stationPlacementReason: string | null = null;
  private authoredDescriptorId = 'straight.2t';
  private authoredTrackType: Exclude<AuthoredTrackType, 'station'> = 'normal';
  private buildOptions: RuntimeBuildOptions = {
    length: 'medium',
    turnDegrees: 45,
    radius: 'medium',
    pitchTargetDegrees: 30,
    bankTargetDegrees: -30,
  };
  private buildSelection: RuntimeBuildSelection = {
    shapeId: 'straight',
    parameters: { length: 'medium' },
    trackType: 'normal',
  };
  private routeGuideEnabled = true;
  private routeGuidePlanRevision = -1;
  private routeGuidePlan: InternalRouteGuidePlan | null = null;
  private routeGuideStatus: RuntimeSnapshot['routeGuideStatus'] = 'idle';
  private routeGuideMessage = 'Build at least three pieces to calculate a route home.';
  private routeGuideToken = 0;
  private routeGuideTimer: number | null = null;
  private lastPlacedPresetId: ElementPresetId | null = null;
  private lastPlacementRevision: number | null = null;
  private finishPreviewToken = 0;
  private finishChecking = false;
  private autoBankPending = false;
  private autoBankToken = 0;
  private focusedLateralIssue: {
    readonly revision: number;
    readonly nodeIdsKey: string;
  } | null = null;
  private tutorialVisible = true;
  private tutorialStep: 1 | 2 | 3 = 1;
  private tutorialOperatingPieceEdited = false;
  private supportsVisible = true;
  private sceneryVisible = true;
  private snappingEnabled = true;
  private hoveredEditorTarget: { kind: 'node' | 'section'; id: string } | null = null;
  private rebuildMs = 0;
  private changedSpans = 0;
  private geometryRevision = 0;
  private peakSpeedMps = 0;
  private peakVerticalG = 1;
  private peakVerticalProgressPercent = 0;
  private minVerticalG = 1;
  private minVerticalProgressPercent = 0;
  private peakLateralG = 0;
  private peakLateralProgressPercent = 0;
  private peakIntensity: RuntimeIntensity = { ...INITIAL_INTENSITY };
  private peakIntensityScore = 0;
  private readonly visitedSectionTypes = new Set<SemanticSectionType>();
  private ridePhase: 'idle' | 'running' | 'completed' | 'stalled' | 'arrival_failed' = 'idle';
  private rideDistanceM = 0;
  private rideElapsedSeconds = 0;
  private photoFlashUntil = 0;
  private lastRide: RuntimeRideResult | null = null;
  private accumulator = 0;
  private previousTime = performance.now();
  private animationFrame = 0;
  private pendingRebuild: number | null = null;
  private frameTimes: number[] = [];
  private inputLatencies: number[] = [];
  private pendingInputStartedAt: number | null = null;
  private pendingPreviewRefreshHandles = false;
  private rideCycles = 0;
  private diagnosticRideCompletionActive = false;
  private diagnosticRideCompletionToken = 0;
  private disposed = false;
  private testSpeed: TestSpeed = 1;

  private get project(): CoasterProject {
    return this.session.project;
  }

  private get selectedNodeId(): string | null {
    return this.session.selection.primaryNodeId;
  }

  constructor(
    private readonly canvas: HTMLCanvasElement,
    options: {
      persistenceScope?: DraftPersistenceScope;
      reactionCamPlatformAvailable?: boolean;
      reactionCamEligibleAccount?: boolean;
      reactionCamSynthetic?: boolean;
    } = {},
  ) {
    this.repository = createNamedProjectRepository({ scope: options.persistenceScope });
    this.mediaPersistenceScope = options.persistenceScope;
    this.reactionSynthetic = options.reactionCamSynthetic === true;
    this.reactionEligibility = initialReactionEligibility({
      platformAvailable: options.reactionCamPlatformAvailable === true,
      eligibleAccount: options.reactionCamEligibleAccount,
    });
    this.scene = new BunchaSceneService({
      canvas,
      qualityTier: qualityTier(),
      themeId: 'park_model',
    });
    this.cameraController = new RideCameraController(this.scene.camera);
    this.cameraController.setSettings({
      reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
    this.scene.camera.position.set(76, 58, 82);
    this.scene.camera.lookAt(0, 12, 0);
    this.controls = new OrbitControls(this.scene.camera, canvas);
    this.controls.target.set(0, 12, 0);
    this.controls.enableDamping = true;
    this.controls.minDistance = 25;
    this.controls.maxDistance = 220;
    this.controls.maxPolarAngle = Math.PI * 0.48;
    this.frameEditorOverview();

    this.coasterMaterials = createCoasterMaterials({
      trackColor: asColor(this.project.appearance.trackColor),
      tieColor: 0x283744,
      supportColor: asColor(this.project.appearance.supportColor),
      trainColor: asColor(this.project.appearance.trainColor),
      stationColor: asColor(this.project.appearance.stationColor),
      stationRoofColor: 0x385768,
    });
    this.ghostMaterials = createCoasterMaterials({
      trackColor: 0x7ef5e0,
      tieColor: 0xc7fff5,
      supportColor: 0x58b9ac,
      trainColor: 0x7ef5e0,
      stationColor: 0xbafbef,
      stationRoofColor: 0x7ef5e0,
    });
    Object.values(this.ghostMaterials).forEach((material) => {
      material.transparent = true;
      material.opacity = 0.58;
      material.depthWrite = false;
      material.polygonOffset = true;
      material.polygonOffsetFactor = -2;
    });

    const initialCompiled = this.compileTrack([]);
    this.acceptCompiledTrack(initialCompiled);
    this.trackVisual = new TrackVisual(
      initialCompiled,
      this.coasterMaterials,
      createTrackSupportContext(this.project),
    );
    this.trainVisual = new TrainVisual(this.coasterMaterials.train, this.project.train.carCount);
    this.authoredSceneryRoot.name = 'authored-scenery';
    this.validationRoot.name = 'validation-overlays';
    this.buildGuideRoot.name = 'build-guides';
    this.content.add(
      this.trackVisual.root,
      this.trainVisual.root,
      this.handleRoot,
      this.authoredSceneryRoot,
      this.validationRoot,
      this.buildGuideRoot,
      this.debugRoot,
    );
    this.scene.replaceContentRoot(this.content);
    this.rebuildHandles();
    this.rebuildBuildGuides();
    this.rebuildDebugFrames();
    this.placeTrain(initialTrainReferenceDistance(this.project, this.sampler ?? undefined));

    this.pointerEditor = new PointerEditor(canvas, this.scene.camera, this.handleRoot, {
      canEdit: () => this.mode === 'editor',
      getPlacementGroundY: () =>
        this.stationPlacementActive ? this.project.plot.groundElevationMeters + 4 : null,
      onPlacementPointer: (position, commit) => {
        this.updateStationPlacement(position.x, position.z);
        if (commit) this.commitStationPlacement();
      },
      getPickMode: () => {
        if (this.stationPlacementActive) return 'none';
        const editor = this.session.snapshot();
        if (editor.activeTool === 'scenery') return 'scenery';
        if (editor.activeTool !== 'build') return 'none';
        return editor.editorMode === 'advanced'
          ? 'both'
          : editor.selectionTarget === 'shape'
            ? 'node'
            : 'section';
      },
      getTrackRoot: () => this.trackVisual?.root ?? null,
      getSectionPickTargets: () => this.trackVisual?.sectionPickTargets() ?? [],
      getSceneryRoot: () => this.authoredSceneryRoot,
      onSelect: (nodeId, additive) => {
        const index = this.project.track.nodes.findIndex((node) => node.id === nodeId);
        this.selectNode(index, additive);
        this.beginEdit(true);
        this.controls.enabled = false;
      },
      onSelectSection: (sectionId) => {
        const index = this.project.track.sections.findIndex((section) => section.id === sectionId);
        if (index >= 0) this.selectSection(index);
      },
      onSelectScenery: (sceneryId) => {
        const index = this.project.scenery.findIndex((item) => item.id === sceneryId);
        if (index >= 0) this.selectScenery(index);
      },
      onHover: (target) => {
        if (
          this.hoveredEditorTarget?.kind === target?.kind &&
          this.hoveredEditorTarget?.id === target?.id
        )
          return;
        this.hoveredEditorTarget = target;
        this.refreshSelectionVisuals();
        if (!target) {
          const editor = this.session.snapshot();
          if (editor.activeTool !== 'build') {
            this.emit();
            return;
          }
          const selectionTarget = editor.selectionTarget;
          this.status =
            selectionTarget === 'shape'
              ? 'Freeform Edit: drag a cyan point to reshape the track.'
              : 'Edit Piece: click the rails to change this piece.';
          this.emit();
          return;
        }
        if (target.kind === 'node') {
          const index = this.project.track.nodes.findIndex((node) => node.id === target.id);
          if (index >= 0) this.status = `Shape Point ${index + 1} — drag to reshape.`;
        } else {
          const index = this.project.track.sections.findIndex(
            (section) => section.id === target.id,
          );
          const behavior = this.project.track.sections[index]?.behavior.kind;
          if (index >= 0)
            this.status = `Track Piece ${index + 1}${behavior ? ` (${behavior})` : ''} — click to tune.`;
        }
        this.emit();
      },
      onMarquee: (bounds) => {
        const world = new THREE.Vector3();
        const projected = new THREE.Vector3();
        const canvasBounds = this.canvas.getBoundingClientRect();
        const selectedIndices = this.handleRoot.children
          .map((handle) => {
            handle.getWorldPosition(world);
            projected.copy(world).project(this.scene.camera);
            const clientX = canvasBounds.left + ((projected.x + 1) / 2) * canvasBounds.width;
            const clientY = canvasBounds.top + ((1 - projected.y) / 2) * canvasBounds.height;
            const nodeId = String(handle.userData.nodeId);
            const index = this.project.track.nodes.findIndex((node) => node.id === nodeId);
            return { clientX, clientY, index };
          })
          .filter(
            ({ clientX, clientY, index }) =>
              index >= 0 &&
              clientX >= bounds.left &&
              clientX <= bounds.right &&
              clientY >= bounds.top &&
              clientY <= bounds.bottom,
          )
          .map(({ index }) => index)
          .sort((a, b) => a - b);
        if (!selectedIndices.length) return;
        const first = selectedIndices[0]!;
        const last = selectedIndices.at(-1)!;
        const nodeIds = this.project.track.nodes.slice(first, last + 1).map((node) => node.id);
        this.session.setSelection(nodeSelection(nodeIds));
        this.refreshSelectionVisuals();
        this.status = `${nodeIds.length} neighboring points selected.`;
        this.emit();
      },
      onMove: (nodeId, position) => {
        this.moveNode(nodeId, position);
      },
      onCommit: (nodeId, position) => {
        this.moveNode(nodeId, position);
        this.commitEdit();
      },
      onEnd: (changed) => {
        if (!changed) this.cancelEdit();
        this.controls.enabled = true;
      },
    });

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas.parentElement ?? canvas);
    this.resize();
    this.setDebugVisible(this.debugVisible);
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.animationFrame = requestAnimationFrame(this.animate);
    void this.initializeRepository();
  }

  subscribe(listener: (snapshot: RuntimeSnapshot) => void): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  private buildShapeChoice(shapeId: ModularTrackShapeId) {
    const pose = modularEndPose(this.project);
    if (!pose) return { id: shapeId, allowed: false, reason: 'Place the station first.' };
    if ((shapeId === 'turn_left' || shapeId === 'turn_right') && pose.pitchDegrees !== 0)
      return {
        id: shapeId,
        allowed: false,
        reason: 'Use Slope Up or Slope Down until the track is level.',
      };
    if (
      (shapeId.startsWith('loop_') ||
        shapeId.startsWith('roll_') ||
        shapeId.startsWith('corkscrew_')) &&
      (pose.pitchDegrees !== 0 || pose.bankDegrees !== 0)
    )
      return {
        id: shapeId,
        allowed: false,
        reason: 'Inversions require level, unbanked track.',
      };
    if (shapeId === 'slope_up' || shapeId === 'slope_down') {
      const legalTarget = MODULAR_PITCH_STATES.some(
        (target) => target !== pose.pitchDegrees && Math.abs(target - pose.pitchDegrees) <= 90,
      );
      if (!legalTarget)
        return { id: shapeId, allowed: false, reason: 'No supported slope target is available.' };
    }
    if (shapeId === 'bank_left' || shapeId === 'bank_right' || shapeId === 'bank_level') {
      const legalTarget = MODULAR_BANK_STATES.some(
        (target) => target !== pose.bankDegrees && Math.abs(target - pose.bankDegrees) <= 90,
      );
      if (!legalTarget)
        return { id: shapeId, allowed: false, reason: 'No supported bank target is available.' };
    }
    return { id: shapeId, allowed: true, reason: null };
  }

  private routeGuidePlanForRevision(): InternalRouteGuidePlan | null {
    if (
      !this.routeGuideEnabled ||
      (this.project.track.geometryKind !== 'modular_piecewise_bezier' &&
        this.project.track.geometryKind !== 'authored_grid_v2') ||
      this.project.track.closed ||
      this.project.track.nodes.length < 4
    ) {
      this.routeGuideStatus = 'idle';
      return null;
    }
    const revision = this.session.snapshot().revision;
    if (this.routeGuidePlanRevision !== revision) {
      this.routeGuidePlanRevision = revision;
      this.routeGuidePlan = null;
      this.routeGuideStatus = 'calculating';
      this.routeGuideMessage = 'Finding a standard-piece route to the station…';
      this.routeGuideToken += 1;
      const token = this.routeGuideToken;
      const project = cloneProject(this.project);
      if (this.routeGuideTimer !== null) window.clearTimeout(this.routeGuideTimer);
      this.routeGuideTimer = window.setTimeout(() => {
        this.routeGuideTimer = null;
        if (
          this.disposed ||
          token !== this.routeGuideToken ||
          revision !== this.session.snapshot().revision
        )
          return;
        let plan: InternalRouteGuidePlan | null = null;
        let message: string;
        if (project.track.geometryKind === 'authored_grid_v2') {
          const baseNodeIds = new Set(project.track.nodes.map((node) => node.id));
          const baseSectionIds = new Set(project.track.sections.map((section) => section.id));
          const result = planAuthoredFinish(project, {
            maxPieces: 12,
            maxStates: 25_000,
            timeBudgetMs: 100,
            validateProject: (candidate) => {
              const affectedNodeIds = candidate.track.nodes
                .filter((node) => !baseNodeIds.has(node.id))
                .map((node) => node.id);
              const affectedSectionIds = candidate.track.sections
                .filter((section) => !baseSectionIds.has(section.id))
                .map((section) => section.id);
              const preflight = assessCandidateProject(
                project,
                candidate,
                affectedNodeIds,
                affectedSectionIds,
              );
              return (
                preflight.issues.find((issue) => issue.severity === 'blocker')?.message ?? null
              );
            },
          });
          if (result.ok) {
            const labels = result.value.descriptorIds.map(
              (descriptorId) =>
                AUTHORED_TRACK_CATALOG.find((descriptor) => descriptor.id === descriptorId)
                  ?.label ?? descriptorId,
            );
            plan = {
              project: result.value.project,
              descriptorIds: result.value.descriptorIds,
              labels,
              distanceM: measureFinishToStation(project).distanceM,
            };
            message = `A ${labels.length}-piece standard route reaches the station.`;
          } else message = result.message;
        } else {
          const result = assessFinishToStation(project);
          if (result.ok && result.routeSteps?.length) {
            plan = {
              project: result.project,
              descriptorIds: result.routeSteps.map((step) => step.shapeId),
              labels: result.routeSteps.map((step) => step.label),
              distanceM: result.routeLengthM ?? result.guidance.distanceM,
            };
            message = `A ${result.routeSteps.length}-piece standard route reaches the station.`;
          } else message = result.message;
        }
        if (
          this.disposed ||
          token !== this.routeGuideToken ||
          revision !== this.session.snapshot().revision
        )
          return;
        this.routeGuidePlan = plan;
        this.routeGuideStatus = plan ? 'ready' : 'unavailable';
        this.routeGuideMessage = message;
        this.rebuildBuildGuides();
        this.emit();
      }, 0);
    }
    return this.routeGuidePlan;
  }

  private invalidateRouteGuide(): void {
    this.routeGuideToken += 1;
    if (this.routeGuideTimer !== null) {
      window.clearTimeout(this.routeGuideTimer);
      this.routeGuideTimer = null;
    }
    this.routeGuidePlanRevision = -1;
    this.routeGuidePlan = null;
    this.routeGuideStatus = 'idle';
    this.routeGuideMessage = this.routeGuideEnabled
      ? 'Build at least three pieces to calculate a route home.'
      : 'Route Guide is off.';
  }

  snapshot(): RuntimeSnapshot {
    const editor = this.session.snapshot();
    const validationPending = this.validationProfile.revision !== editor.revision;
    const validationProfile: ValidationProfile = validationPending
      ? {
          revision: editor.revision,
          issues: [],
          samples: [],
          blockerCount: 0,
          warningCount: 0,
          testAllowed: false,
          summary: 'Running Ride Check',
        }
      : this.validationProfile;
    const selected = this.selectedNode();
    const selectedWindingValues = this.project.track.nodes
      .filter((node) => editor.selection.nodeIds.includes(node.id))
      .map((node) => node.rollWindingDegrees);
    const selectedNodeIndex = selected ? this.project.track.nodes.indexOf(selected) : null;
    const selectedSectionIndex = editor.selection.sectionId
      ? this.project.track.sections.findIndex(
          (section) => section.id === editor.selection.sectionId,
        )
      : null;
    const selectedSection =
      selectedSectionIndex !== null && selectedSectionIndex >= 0
        ? this.project.track.sections[selectedSectionIndex]!
        : null;
    const selectedPiece = selectedSection
      ? this.project.editor.pieceGroups.find((piece) =>
          piece.sectionIds.includes(selectedSection.id),
        )
      : null;
    const selectedAuthoredPieceIndex =
      selectedSection && this.project.track.geometryKind === 'authored_grid_v2'
        ? this.project.track.pieces.findIndex((piece) =>
            piece.sectionIds.includes(selectedSection.id),
          )
        : -1;
    const selectedAuthoredPiece =
      selectedAuthoredPieceIndex >= 0 && this.project.track.geometryKind === 'authored_grid_v2'
        ? this.project.track.pieces[selectedAuthoredPieceIndex]!
        : null;
    const firstBlocker = validationProfile.issues.find((issue) => issue.severity === 'blocker');
    const modularOpenBuild =
      (this.project.track.geometryKind === 'modular_piecewise_bezier' ||
        this.project.track.geometryKind === 'authored_grid_v2') &&
      !this.project.track.closed &&
      this.project.track.nodes.length > 0;
    const buildEndNode =
      editor.topology.status === 'open' || modularOpenBuild
        ? this.project.track.nodes.at(-1)
        : null;
    const finishGuidance = buildEndNode ? measureFinishToStation(this.project) : null;
    const toEditorPose = (pose: ReturnType<typeof authoredEndPose>): TrackEndpointPose | null =>
      pose
        ? {
            position: fixedPointToWorld(pose),
            headingStep: pose.headingStep,
            pitchDegrees: (pose.pitchDegrees === 25
              ? 30
              : pose.pitchDegrees === -25
                ? -30
                : pose.pitchDegrees) as TrackEndpointPose['pitchDegrees'],
            bankDegrees: (pose.bank === 'left'
              ? -45
              : pose.bank === 'right'
                ? 45
                : 0) as TrackEndpointPose['bankDegrees'],
            rollWindingDegrees: pose.windingQuarterTurns * 90,
          }
        : null;
    const authoredProjectEnd = authoredEndPose(this.project);
    const endPose = modularEndPose(this.project) ?? toEditorPose(authoredProjectEnd);
    const authoredStation =
      this.project.track.geometryKind === 'authored_grid_v2'
        ? this.project.track.pieces[0]?.entryPose
        : null;
    const stationPose =
      this.project.track.geometryKind === 'authored_grid_v2'
        ? toEditorPose(authoredStation ?? null)
        : (this.project.editor.pieceGroups[0]?.entryPose ?? null);
    const previewPose =
      this.presetPreview?.kind === 'piece'
        ? (this.presetPreview.project.editor.pieceGroups.at(-1)?.exitPose ??
          toEditorPose(authoredEndPose(this.presetPreview.project)))
        : null;
    const buildShapeChoices = MODULAR_BUILD_SHAPES.map((shapeId) => this.buildShapeChoice(shapeId));
    const buildTypeChoices = TRACK_TYPES.map((trackType) => {
      const compatibility = endPose
        ? trackTypeCompatibility(
            this.buildSelection.shapeId,
            endPose,
            trackType,
            this.buildSelection.parameters,
          )
        : { allowed: false, reason: 'Place the station first.' };
      return { id: trackType, ...compatibility };
    });
    // Route recommendations are calculated for committed project revisions.
    // Hiding them during a candidate preview prevents a valid route for the
    // previous endpoint from being presented as valid for the ghost endpoint.
    const routePlan =
      this.presetPreview?.kind === 'piece' ? null : this.routeGuidePlanForRevision();
    const authoredGridBuild = this.project.track.geometryKind === 'authored_grid_v2';
    const authoredEntry = authoredEndPose(this.project);
    const authoredCatalog = AUTHORED_TRACK_CATALOG.map((descriptor) => {
      const sequenceIssue = authoredSequenceIssue(this.project, descriptor.id);
      const requestedTrackType = this.authoredTrackType;
      const effectiveTrackType = descriptor.allowedTrackTypes.includes(requestedTrackType)
        ? requestedTrackType
        : 'normal';
      const trackTypeResetReason =
        requestedTrackType !== effectiveTrackType
          ? `${descriptor.label} cannot use ${requestedTrackType} track. Track Type will change to Normal.`
          : null;
      const trackType = descriptor.id === 'station.2t' ? 'station' : effectiveTrackType;
      const result = authoredEntry
        ? createAuthoredCandidate(descriptor.id, authoredEntry, trackType)
        : descriptor.id === 'station.2t'
          ? null
          : {
              ok: false as const,
              code: 'station_required',
              message: 'Place the station first.',
            };
      const recommendedIndex = routePlan?.descriptorIds.indexOf(descriptor.id) ?? -1;
      return {
        id: descriptor.id,
        label: descriptor.label,
        category: descriptor.category,
        description: descriptor.description,
        selected:
          this.presetPreview?.kind === 'piece' &&
          this.presetPreview.authoredDescriptorId === descriptor.id,
        allowed: !sequenceIssue && (result === null || result.ok),
        reason: sequenceIssue ?? (result === null || result.ok ? null : result.message),
        allowedTrackTypes: descriptor.allowedTrackTypes.filter((type) => type !== 'station'),
        effectiveTrackType,
        trackTypeResetReason,
        recommendedRank: recommendedIndex < 0 ? null : recommendedIndex + 1,
      };
    });
    const routeGuideSuggestions = routePlan?.labels.slice(0, 3) ?? [];
    const constructionState =
      editor.topology.status === 'invalid'
        ? ('invalid' as const)
        : this.project.track.nodes.length === 0
          ? ('empty' as const)
          : editor.topology.status === 'open' || modularOpenBuild
            ? finishGuidance?.generatedFinishEligible || finishGuidance?.directJoinEligible
              ? ('open_finishable' as const)
              : ('open_building' as const)
            : editor.topology.status === 'incomplete'
              ? ('empty' as const)
              : validationPending
                ? ('closed_checking' as const)
                : validationProfile.blockerCount
                  ? ('closed_blocked' as const)
                  : validationProfile.warningCount
                    ? ('closed_warnings' as const)
                    : ('closed_ready' as const);
    const blankTutorial =
      this.project.metadata.tags.includes('started_blank') ||
      this.project.metadata.title === 'My New Coaster' ||
      this.project.metadata.title === 'Blank Plot';
    const tutorialStep = blankTutorial
      ? this.project.track.nodes.length === 0
        ? 1
        : this.project.track.closed
          ? 3
          : 2
      : this.tutorialStep;
    const plotBounds = projectPlotBounds(this.project);
    const fullValidationCurrent =
      this.fullValidationRevision === editor.revision ? this.fullValidation : null;
    const fullValidationRequired = editor.topology.status === 'closed';
    const fullTestBlocked = fullValidationCurrent?.testPolicy === 'blocked';
    const testAllowed =
      validationProfile.testAllowed &&
      (!fullValidationRequired || (!this.fullValidationPending && !fullTestBlocked));
    const fullValidationStatus = !fullValidationCurrent
      ? 'not_run'
      : fullValidationCurrent.technicalEligible
        ? fullValidationCurrent.issues.some(
            (issue) => issue.severity === 'warning' || issue.severity === 'severe',
          )
          ? 'warnings'
          : 'valid'
        : 'blocked';
    const rideCheck = buildRideCheckPresentation(
      validationProfile,
      fullValidationCurrent,
      validationPending || this.fullValidationPending,
    );
    const cameraSettings = this.cameraController.snapshot;
    const audioSnapshot = this.audio.snapshot;
    const replayDuration =
      this.lastRideSummary?.durationSeconds ?? this.mediaTimeline?.durationSeconds ?? 0;
    const selectedMediaEdit = this.mediaEdits?.[this.mediaSelectedAlternative] ?? null;
    const selectedPoster = selectedMediaEdit?.posterCandidates.find(
      (candidate) => candidate.id === this.mediaSelectedPosterId,
    );
    return {
      mode: this.mode,
      ready: true,
      status: this.status,
      editorRevision: editor.revision,
      selectedNodeId: this.selectedNodeId,
      selectedNodeIds: editor.selection.nodeIds,
      selectedNodeLabel:
        selectedNodeIndex === null
          ? null
          : buildEndNode?.id === selected?.id
            ? 'Track Exit'
            : editor.editorMode === 'advanced'
              ? `Shape Handle ${selectedNodeIndex + 1}`
              : 'Shape Point',
      selectedNodeIndex,
      nodeIds: this.project.track.nodes.map((node) => node.id),
      nodeCount: this.project.track.nodes.length,
      selectedHeightM: selected?.position.y ?? null,
      selectedPosition: selected ? { ...selected.position } : null,
      selectedBankDegrees: selected?.bankDegrees ?? null,
      selectedRollWindingDegrees: selected?.rollWindingDegrees ?? null,
      selectedRollWindingMixed:
        selectedWindingValues.length > 1 &&
        selectedWindingValues.some((value) => Math.abs(value - selectedWindingValues[0]!) > 0.001),
      selectedSectionId: selectedSection?.id ?? null,
      selectedSectionIndex:
        selectedSectionIndex !== null && selectedSectionIndex >= 0 ? selectedSectionIndex : null,
      selectedSectionLabel:
        selectedSectionIndex !== null && selectedSectionIndex >= 0
          ? selectedAuthoredPiece
            ? `${
                getAuthoredDescriptor(selectedAuthoredPiece.descriptorId)?.label ?? 'Track Piece'
              } · Piece ${selectedAuthoredPieceIndex + 1}`
            : `${pieceDefinitionLabel(selectedPiece?.definitionId ?? 'custom')} · Piece ${
                selectedSectionIndex + 1
              }`
          : null,
      selectedSectionBehavior: selectedSection ? structuredClone(selectedSection.behavior) : null,
      autoBankPending: this.autoBankPending,
      sectionKinds: this.project.track.sections.map((section) => section.behavior.kind),
      canUndo: editor.history.canUndo,
      canRedo: editor.history.canRedo,
      canDeleteLastPiece:
        this.project.track.geometryKind === 'authored_grid_v2'
          ? this.project.track.pieces.length > 1
          : this.project.track.geometryKind === 'modular_piecewise_bezier'
            ? this.project.editor.pieceGroups.length > 1
            : this.lastPlacedPresetId !== null && this.lastPlacementRevision === editor.revision,
      finishChecking: this.finishChecking,
      stationPlacement: this.stationPlacement,
      stationPlacementMode: {
        active: this.stationPlacementActive,
        position: { ...this.stationPlacementPosition },
        headingStep: this.stationPlacementHeading,
        valid: this.stationPlacementValid,
        reason: this.stationPlacementReason,
      },
      fixtureId: this.project.projectId,
      fixtureTitle: this.project.metadata.title,
      activeTool: editor.activeTool,
      editorMode: editor.editorMode,
      advancedLocked:
        (this.project.track.geometryKind === 'modular_piecewise_bezier' ||
          this.project.track.geometryKind === 'authored_grid_v2') &&
        !this.project.track.closed,
      modularBuild:
        this.project.track.geometryKind === 'modular_piecewise_bezier' || authoredGridBuild,
      authoredGridBuild,
      authoredCatalog,
      authoredSelection: {
        descriptorId: this.authoredDescriptorId,
        trackType: this.authoredTrackType,
      },
      buildOptions: { ...this.buildOptions },
      buildSelection: structuredClone(this.buildSelection),
      buildShapeChoices,
      buildTypeChoices,
      routeGuideEnabled: this.routeGuideEnabled,
      routeGuideStatus: this.presetPreview?.kind === 'piece' ? 'idle' : this.routeGuideStatus,
      routeGuideSuggestions,
      routeGuideMessage:
        this.presetPreview?.kind === 'piece'
          ? 'Place or clear the preview to refresh the route.'
          : this.routeGuideMessage,
      routeGuidePlan: routePlan
        ? {
            pieceCount: routePlan.labels.length,
            distanceM: routePlan.distanceM,
            steps: routePlan.labels,
            descriptorIds: routePlan.descriptorIds,
          }
        : null,
      selectionTarget: editor.selectionTarget,
      snappingEnabled: this.snappingEnabled,
      activeOverlay: editor.activeOverlay,
      saveState: editor.saveState,
      hasSavedDraft: this.drafts.some((draft) => draft.projectId === this.project.projectId),
      hasUnsavedChanges:
        this.persistenceMode === 'editable' &&
        (editor.saveState === 'editing' || editor.saveState === 'error'),
      topologyStatus: editor.topology.status,
      constructionState,
      buildEnd:
        buildEndNode && finishGuidance && endPose && stationPose
          ? {
              nodeId: buildEndNode.id,
              position: { ...buildEndNode.position },
              guidance: finishGuidance,
              pose: structuredClone(endPose),
              stationPose: structuredClone(stationPose),
              heightDifferenceM: stationPose.position.y - endPose.position.y,
              headingDifferenceDegrees:
                Math.min(
                  Math.abs(stationPose.headingStep - endPose.headingStep),
                  8 - Math.abs(stationPose.headingStep - endPose.headingStep),
                ) * 45,
              gridDelta:
                authoredProjectEnd && authoredStation
                  ? authoredStationGridDelta(authoredProjectEnd, authoredStation)
                  : null,
              previewGridDelta:
                this.presetPreview?.project.track.geometryKind === 'authored_grid_v2' &&
                authoredStation
                  ? (() => {
                      const previewEnd = authoredEndPose(this.presetPreview!.project);
                      return previewEnd
                        ? authoredStationGridDelta(previewEnd, authoredStation)
                        : null;
                    })()
                  : null,
              previewPose: previewPose ? structuredClone(previewPose) : null,
            }
          : null,
      plotBounds,
      testAllowed,
      testDisabledReason: testAllowed
        ? null
        : validationPending
          ? 'Ride Check is running.'
          : this.fullValidationPending
            ? 'Running the full test simulation.'
            : fullTestBlocked
              ? (fullValidationCurrent?.issues.find((issue) => issue.severity === 'blocker')
                  ?.message ?? 'Fix the ride simulation error before testing.')
              : (firstBlocker?.message ?? 'Finish and connect the track before test riding.'),
      validation: validationProfile,
      validationPending,
      preview: this.presetPreview
        ? {
            kind: this.presetPreview.kind,
            presetId: this.presetPreview.presetId,
            label: this.presetPreview.label,
            acceptLabel: this.presetPreview.acceptLabel,
            severity: this.presetPreview.severity,
            canAccept: this.presetPreview.canAccept,
            inline: this.presetPreview.inline,
            issues: this.presetPreview.issues,
            affectedNodeIds: this.presetPreview.affectedNodeIds,
            affectedSectionIds: this.presetPreview.affectedSectionIds,
            minPlotMarginM: this.presetPreview.minPlotMarginM,
            finishImpact: this.presetPreview.finishImpact ?? null,
            generatedLengthM: this.presetPreview.generatedLengthM ?? null,
            addedNodeCount: this.presetPreview.addedNodeCount ?? null,
            autoBankComparison: this.presetPreview.autoBankComparison ?? null,
          }
        : null,
      appearance: {
        themeId: this.project.appearance.environmentThemeId as EnvironmentThemeId,
        trackColor: this.project.appearance.trackColor,
        supportColor: this.project.appearance.supportColor,
        trainColor: this.project.appearance.trainColor,
      },
      supportsVisible: this.supportsVisible,
      sceneryVisible: this.sceneryVisible,
      sceneryCount: this.project.scenery.length,
      selectedSceneryId: this.session.selection.primarySceneryId,
      selectedSceneryIndex: this.session.selection.primarySceneryId
        ? this.project.scenery.findIndex(
            (item) => item.id === this.session.selection.primarySceneryId,
          )
        : null,
      selectedSceneryRotationDegrees:
        this.project.scenery.find((item) => item.id === this.session.selection.primarySceneryId)
          ?.rotationDegrees.y ?? null,
      drafts: this.drafts,
      lastOpenedProjectId: this.lastOpenedProjectId,
      recoveredDraft: this.recoveredDraft,
      tutorial: {
        visible: this.tutorialVisible,
        step: tutorialStep,
        kind: blankTutorial ? 'blank' : 'template',
      },
      telemetry: this.telemetry(),
      ride: {
        phase: this.ridePhase,
        progressPercent: this.rideProgressPercent(),
        elapsedSeconds: this.rideElapsedSeconds,
        dispatchSecondsRemaining: this.dispatchSecondsRemaining(),
        peakVerticalG: this.peakVerticalG,
        peakVerticalProgressPercent: this.peakVerticalProgressPercent,
        minVerticalG: this.minVerticalG,
        minVerticalProgressPercent: this.minVerticalProgressPercent,
        peakLateralG: this.peakLateralG,
        peakLateralProgressPercent: this.peakLateralProgressPercent,
        intensity: this.peakIntensity,
        visitedSectionTypes: [...this.visitedSectionTypes],
        photoFlash: performance.now() < this.photoFlashUntil,
      },
      lastRide: this.lastRide,
      fullValidation: {
        pending: this.fullValidationPending,
        status: fullValidationStatus,
        outcome: fullValidationCurrent?.metrics.outcome ?? 'not_run',
        testPolicy: fullValidationCurrent?.testPolicy ?? 'blocked',
        publishReady: fullValidationCurrent?.publishReady ?? false,
        technicalEligible: fullValidationCurrent?.technicalEligible ?? false,
        intensity: fullValidationCurrent
          ? {
              level: fullValidationCurrent.intensity.level,
              label: fullValidationCurrent.intensity.label,
              message: fullValidationCurrent.intensity.message,
              progressPercent:
                this.sampler?.length && fullValidationCurrent.intensity.strongestDistanceM >= 0
                  ? THREE.MathUtils.clamp(
                      (fullValidationCurrent.intensity.strongestDistanceM / this.sampler.length) *
                        100,
                      0,
                      100,
                    )
                  : 0,
            }
          : null,
        topSpeedMph:
          fullValidationCurrent === null
            ? null
            : fullValidationCurrent.metrics.topSpeedMps * MPH_PER_MPS,
        maxVerticalG: fullValidationCurrent?.metrics.maxVerticalG ?? null,
        minVerticalG: fullValidationCurrent?.metrics.minVerticalG ?? null,
        maxAbsLateralG: fullValidationCurrent?.metrics.maxAbsLateralG ?? null,
        maxAbsLongitudinalG: fullValidationCurrent?.metrics.maxAbsLongitudinalG ?? null,
        issues:
          fullValidationCurrent?.issues.map((issue) => ({
            id: issue.id,
            code: issue.code,
            category: issue.category,
            ...(issue.intensityTier ? { intensityTier: issue.intensityTier } : {}),
            severity: issue.severity,
            message: issue.message,
            ...(issue.sectionId ? { sectionId: issue.sectionId } : {}),
            ...(issue.distanceM !== undefined ? { distanceM: issue.distanceM } : {}),
            ...(issue.measured !== undefined ? { measured: issue.measured } : {}),
            ...(issue.threshold !== undefined ? { threshold: issue.threshold } : {}),
            ...(issue.unit ? { unit: issue.unit } : {}),
          })) ?? [],
      },
      rideCheck,
      replay: {
        available: Boolean(this.lastRideSummary?.trace.length || this.mediaTimeline?.trace.length),
        paused: this.replayPaused,
        elapsedSeconds: this.replayElapsedSeconds,
        durationSeconds: replayDuration,
        progressPercent:
          replayDuration > 0
            ? THREE.MathUtils.clamp((this.replayElapsedSeconds / replayDuration) * 100, 0, 100)
            : 0,
      },
      media: {
        available: Boolean(this.mediaTimeline),
        requiresNewRide: this.mediaRequiresNewRide,
        open: this.mediaOpen,
        timelineSource: this.mediaTimelineSource,
        selectedAlternative: this.mediaSelectedAlternative,
        editLabels: ['Best Moments', 'Biggest Thrills', 'Front Row'],
        videoAvailable:
          selectedMediaEdit?.shots.filter((shot) => shot.id.includes(':action:')).length === 3,
        photoSource: selectedPoster?.moment.source ?? null,
        selectedPosterId: this.mediaSelectedPosterId,
        posterCandidates:
          selectedMediaEdit?.posterCandidates.map((candidate) => ({
            id: candidate.id,
            label: candidate.label,
            source: candidate.source,
            thumbnailUrl: this.mediaPosterThumbnailUrls.get(candidate.id) ?? null,
          })) ?? [],
        creatorPosterSkipped:
          Boolean(this.mediaTimeline?.events.some((event) => event.kind === 'photo')) &&
          !(
            selectedMediaEdit?.posterCandidates.some(
              (candidate) => candidate.source === 'creator_marker',
            ) ?? false
          ),
        photoUrl: this.currentMediaArtifactUrl('photo'),
        cardUrl: this.currentMediaArtifactUrl('share_card'),
        videoUrl: this.currentMediaArtifactUrl('highlight_video'),
        loopUrl: this.currentMediaArtifactUrl('animated_loop'),
        status: this.mediaStatus,
        progress: this.mediaProgress,
        includeAudio: this.mediaIncludeAudio,
        includeReaction: this.reactionIncludeInExport,
        includeReactionMicrophone: this.reactionIncludeMicrophoneInExport,
        reactionAvailable: this.reactionAvailableForExport(),
        reactionMicrophoneAvailable: Boolean(this.reactionRecording?.microphoneIncluded),
        reactionRawUrl: this.reactionRawUrl,
        reactionPlacement: this.reactionOptions.placement,
        reactionMirrored: this.reactionOptions.exportMirrored,
        imageCopyAvailable: imageClipboardAvailable(),
        publicLinkAvailable: Boolean(this.mediaShareContext.publicUrl),
        formatLabel: this.mediaCapabilities.preferredRecorder?.extension.toUpperCase() ?? null,
        message: this.mediaMessage,
        shareOutcome: this.mediaShareOutcome,
      },
      reaction: {
        eligibility: this.reactionEligibility,
        setupOpen: this.reactionSetupOpen,
        phase: this.reactionPhase,
        includeMicrophone: this.reactionOptions.includeMicrophone,
        microphoneIncluded: this.reactionMicrophoneIncluded,
        exportMirrored: this.reactionOptions.exportMirrored,
        placement: this.reactionOptions.placement,
        devices: this.reactionDevices,
        selectedDeviceId: this.reactionSelectedDeviceId,
        previewStream: this.reactionPreviewStream,
        countdown: this.reactionCountdown,
        recordingElapsedSeconds:
          this.reactionPhase === 'recording' && this.reactionRecordingStartedAtMs > 0
            ? Math.max(0, (performance.now() - this.reactionRecordingStartedAtMs) / 1000)
            : 0,
        rawReviewUrl: this.reactionRawUrl,
        rawAvailable: Boolean(this.reactionRecording),
        availableForExport: this.reactionAvailableForExport(),
        rideSpeedLocked: this.reactionRideSpeedLocked(),
        message: this.reactionMessage,
        interruptionReason: this.reactionInterruptionReason,
      },
      settings: {
        cameraFov: cameraSettings.fieldOfView,
        cameraShake: cameraSettings.shake,
        reducedMotion: cameraSettings.reducedMotion,
        masterVolume: audioSnapshot.settings.masterVolume,
        muted: audioSnapshot.settings.muted,
        audioState: audioSnapshot.state,
        testSpeed: this.testSpeed,
      },
      diagnostics: this.diagnostics(),
      debugVisible: this.debugVisible,
      persistenceAvailable: this.persistenceAvailable,
      persistenceTier: this.repository.durability,
    };
  }

  openEditor(): void {
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = this.session.snapshot().editorMode === 'advanced';
    this.buildGuideRoot.visible = true;
    this.validationRoot.visible = true;
    this.debugRoot.visible = this.debugVisible;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setSceneCameraFov(EDITOR_CAMERA_FOV);
    this.frameEditorOverview();
    this.rebuildBuildGuides();
    if (this.session.snapshot().editorMode === 'advanced')
      this.selectNodeById(
        this.project.track.nodes[Math.min(2, this.project.track.nodes.length - 1)]?.id ?? null,
      );
    else if (this.project.track.sections.length) this.selectSection(0);
    this.status = 'Choose a track piece to edit, or add the next piece.';
    this.emit();
  }

  returnToMenu(): void {
    if (this.mode !== 'editor') return;
    this.commitEdit();
    this.cancelPreview();
    void this.discardReactionSession();
    this.mode = transitionMode(this.mode, 'menu');
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.controls.enabled = true;
    this.status = 'Choose a coaster to keep building.';
    this.emit();
  }

  startTemplate(template: 'systems' | 'lift-drop' | 'oval' | 'blank'): void {
    const fixture =
      template === 'systems'
        ? systemsCircuitCoasterFixture
        : template === 'lift-drop'
          ? liftDropCoasterFixture
          : template === 'oval'
            ? ovalCoasterFixture
            : blankCoasterFixture;
    const title =
      template === 'systems'
        ? nextNumberedTitle('First Coaster', this.drafts)
        : template === 'blank'
          ? nextNumberedTitle('New Coaster', this.drafts)
          : nextNumberedTitle(fixture.metadata.title, this.drafts);
    const project = instantiateTemplate(fixture, title);
    this.persistenceMode = 'editable';
    this.recoveredDraft = null;
    if (template === 'blank' && !project.metadata.tags.includes('started_blank'))
      project.metadata.tags.push('started_blank');
    this.resetProject = cloneProject(project);
    this.replaceProject(project);
    this.session.setSelectionTarget('piece');
    this.session.setSaveState('editing');
    if (template === 'blank') {
      this.session.setEditorMode('basic');
      this.session.setTool('build');
      this.tutorialStep = 1;
      this.rebuildHandles();
    } else if (template === 'systems') {
      this.tutorialStep = 1;
      this.tutorialOperatingPieceEdited = false;
      this.tutorialVisible = true;
    }
    this.mode = 'menu';
    this.openEditor();
    this.status =
      template === 'blank'
        ? 'Add a station to start your track.'
        : 'Click the rails to tune a complete track piece.';
    this.emit();
  }

  async openDraft(projectId: string): Promise<void> {
    const result = await this.repository.load(projectId);
    let opened: StoredDraft;
    if (!result.ok) {
      const recovery = await this.repository.loadRecovery(projectId);
      if (!recovery.ok) return this.showOperationFailure(result.error.message);
      this.replaceProject(recovery.value.project);
      opened = recovery.value;
      this.status = 'Recovered the last valid version.';
    } else {
      this.replaceProject(result.value.project);
      opened = result.value;
      this.status = 'Draft opened.';
    }
    this.persistenceMode = 'editable';
    this.lastOpenedProjectId = projectId;
    this.recoveredDraft = recoveredDraftIdentity(opened);
    this.resetProject = cloneProject(this.project);
    this.mode = 'menu';
    this.openEditor();
    this.status = `${opened.title} opened for ${opened.project.metadata.creator.displayName} · ${opened.project.lineage ? 'independent remix' : 'original draft'}.`;
    void this.repository.setPreference('lastOpenedProjectId', projectId);
    this.emit();
  }

  async duplicateDraft(projectId: string): Promise<void> {
    const loaded = await this.repository.load(projectId);
    if (!loaded.ok) return this.showOperationFailure(loaded.error.message);
    const duplicate = instantiateTemplate(
      loaded.value.project,
      nextNumberedTitle(`${loaded.value.project.metadata.title} Copy`, this.drafts),
    );
    const saved = await this.repository.save(duplicate);
    if (!saved.ok) return this.showOperationFailure(saved.error.message);
    await this.refreshDrafts();
    this.status = 'Draft duplicated.';
    this.emit();
  }

  async deleteDraft(projectId: string): Promise<void> {
    const deletedDraft = this.drafts.find((draft) => draft.projectId === projectId);
    const result = await this.repository.delete(projectId);
    if (!result.ok) return this.showOperationFailure(result.error.message);
    if (deletedDraft)
      await this.mediaStore()
        .then((repository) => repository.delete(deletedDraft.projectId, deletedDraft.revisionId))
        .catch(() => undefined);
    if (this.lastOpenedProjectId === projectId) {
      this.lastOpenedProjectId = null;
      this.recoveredDraft = null;
      await this.repository.setPreference('lastOpenedProjectId', null);
    }
    await this.refreshDrafts();
    if (
      this.lastOpenedProjectId &&
      !this.drafts.some((draft) => draft.projectId === this.lastOpenedProjectId && !draft.corrupt)
    ) {
      this.lastOpenedProjectId = null;
      await this.repository.setPreference('lastOpenedProjectId', null);
    }
    this.status = 'Draft deleted.';
    this.emit();
  }

  renameProject(title: string): boolean {
    const result = renameProjectOperation(this.project, title);
    if (result.ok) {
      this.executeProjectOperation('Rename coaster', result.project, result.message);
      return true;
    }
    this.session.setSaveState('error');
    this.showOperationFailure(result.message);
    return false;
  }

  async saveNow(): Promise<boolean> {
    const saved = await this.flushAutosave();
    const timeline = this.mediaTimeline;
    if (saved && timeline?.projectId === this.project.projectId)
      await this.queueMediaPersistence((repository) => repository.save(timeline)).catch(
        () => undefined,
      );
    return saved;
  }

  openReactionCamSetup(camera: 'first-person' | 'third-person' = 'first-person'): void {
    if (this.ridePhase === 'running' || this.reactionEligibility.state !== 'available') {
      this.status = this.reactionEligibility.reason;
      this.emit();
      return;
    }
    if (
      this.mode !== 'editor' &&
      this.mode !== 'ride-first-person' &&
      this.mode !== 'ride-third-person'
    )
      return;
    this.reactionRideCamera = camera;
    this.reactionSetupOpen = true;
    this.reactionInterruptionReason = null;
    this.reactionPhase = this.reactionPreviewStream ? 'previewing' : 'idle';
    this.reactionMessage = 'Processed on this device. Nothing uploads until you choose Share.';
    this.emitReactionAnalytics({ name: 'reaction_setup_opened' });
    this.emit();
  }

  closeReactionCamSetup(): void {
    this.reactionCountdownToken += 1;
    this.reactionCountdown = 0;
    this.reactionSetupOpen = false;
    if (
      this.reactionPhase === 'requesting_permission' ||
      this.reactionPhase === 'previewing' ||
      this.reactionPhase === 'countdown'
    ) {
      void this.reactionController?.discard();
      this.reactionPreviewStream = null;
      this.reactionMicrophoneIncluded = false;
      this.reactionPhase = 'idle';
    }
    this.emit();
  }

  setReactionMicrophoneEnabled(value: boolean): void {
    if (this.reactionPhase === 'recording' || this.reactionPhase === 'finalizing') return;
    this.reactionOptions = { ...this.reactionOptions, includeMicrophone: value };
    this.reactionMessage = this.reactionPreviewStream
      ? 'Select Update Preview to apply the microphone change.'
      : value
        ? 'Microphone will be requested separately when you enable the preview.'
        : 'Microphone is off.';
    this.emit();
  }

  setReactionExportMirrored(value: boolean): void {
    if (this.reactionOptions.exportMirrored !== value) this.clearReactionMediaArtifacts();
    this.reactionOptions = { ...this.reactionOptions, exportMirrored: value };
    if (this.reactionRecording)
      this.reactionRecording = { ...this.reactionRecording, exportMirrored: value };
    this.emit();
  }

  setReactionPlacement(value: ReactionOverlayPlacement): void {
    if (this.reactionOptions.placement !== value) this.clearReactionMediaArtifacts();
    this.reactionOptions = { ...this.reactionOptions, placement: value };
    if (this.reactionRecording)
      this.reactionRecording = { ...this.reactionRecording, placement: value };
    this.emit();
  }

  async enableReactionPreview(): Promise<void> {
    if (this.reactionEligibility.state !== 'available') return;
    this.reactionPhase = 'requesting_permission';
    this.reactionMessage = 'Waiting for camera permission…';
    this.reactionInterruptionReason = null;
    this.emitReactionAnalytics({ name: 'reaction_permission_requested' });
    this.emit();
    try {
      const controller = await this.ensureReactionController();
      const result = await controller.enablePreview({
        ...this.reactionOptions,
        ...(this.reactionSelectedDeviceId ? { cameraDeviceId: this.reactionSelectedDeviceId } : {}),
      });
      this.reactionPreviewStream = result.stream;
      this.reactionDevices = result.devices;
      this.reactionMicrophoneIncluded = result.microphoneIncluded;
      const activeDevice = result.stream.getVideoTracks()[0]?.getSettings().deviceId;
      if (activeDevice) this.reactionSelectedDeviceId = activeDevice;
      this.reactionPhase = 'previewing';
      this.reactionMessage =
        result.microphoneWarning ??
        'Preview is local. Choose Start Reaction Ride when you are ready.';
    } catch (cause) {
      const name = cause instanceof DOMException ? cause.name : 'Error';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        this.reactionEligibility = {
          state: 'blocked_by_policy',
          reason: 'Camera access was denied or blocked by this browser.',
        };
        this.emitReactionAnalytics({
          name: 'reaction_permission_denied',
          failureCode: name,
        });
        this.reactionMessage =
          'Camera access was not granted. You can still ride normally without Reaction Cam.';
      } else {
        this.reactionMessage =
          cause instanceof Error ? cause.message : 'Reaction Cam preview could not start.';
      }
      this.reactionPhase = 'error';
      this.reactionPreviewStream = null;
    }
    this.emit();
  }

  async switchReactionCamera(deviceId: string): Promise<void> {
    if (!this.reactionController || this.reactionPhase !== 'previewing') return;
    this.reactionPhase = 'requesting_permission';
    this.reactionMessage = 'Switching camera…';
    this.emit();
    try {
      const result = await this.reactionController.switchCamera(deviceId, {
        ...this.reactionOptions,
        cameraDeviceId: deviceId,
      });
      this.reactionSelectedDeviceId = deviceId;
      this.reactionPreviewStream = result.stream;
      this.reactionDevices = result.devices;
      this.reactionMicrophoneIncluded = result.microphoneIncluded;
      this.reactionPhase = 'previewing';
      this.reactionMessage = 'Camera switched. Preview remains local.';
    } catch (cause) {
      this.reactionPhase = 'error';
      this.reactionMessage = cause instanceof Error ? cause.message : 'Camera could not switch.';
    }
    this.emit();
  }

  async startReactionRide(): Promise<void> {
    if (
      !this.reactionController ||
      !this.reactionPreviewStream ||
      this.reactionPhase !== 'previewing'
    )
      return;
    // Reaction footage is recorded in real wall-clock time while replay traces use canonical
    // simulation time. Phase 6 therefore records Reaction Cam rides at 1× only.
    if (this.testSpeed !== 1) {
      this.testSpeed = 1;
      this.persistRideSettings();
    }
    const token = ++this.reactionCountdownToken;
    this.reactionPhase = 'countdown';
    for (let value = 3; value >= 1; value -= 1) {
      if (token !== this.reactionCountdownToken || !this.reactionSetupOpen) return;
      this.reactionCountdown = value;
      this.reactionMessage = `Starting in ${value}…`;
      this.emit();
      if (this.reactionSynthetic) await Promise.resolve();
      else await new Promise((resolve) => window.setTimeout(resolve, 1_000));
    }
    if (token !== this.reactionCountdownToken || !this.reactionSetupOpen) return;
    try {
      this.reactionRecordingStartedAtMs = await this.reactionController.startRecording();
      this.clearReactionRecording();
      this.reactionPhase = 'recording';
      this.reactionCountdown = 0;
      this.reactionSetupOpen = false;
      this.reactionMessage = 'Reaction Cam recording at 1×. Nothing is uploaded.';
      const rideOriginMs = performance.now();
      this.reactionController.markRideStarted(rideOriginMs);
      this.emitReactionAnalytics({
        name: 'reaction_recording_started',
        microphoneIncluded: this.reactionMicrophoneIncluded,
      });
      this.beginRide(this.reactionRideCamera, rideOriginMs);
    } catch (cause) {
      this.reactionPhase = 'error';
      this.reactionMessage = cause instanceof Error ? cause.message : 'Recording could not start.';
      await this.reactionController.discard();
      this.reactionPreviewStream = null;
    }
    this.emit();
  }

  async stopAndDiscardReaction(): Promise<void> {
    this.reactionCountdownToken += 1;
    await this.reactionController?.discard();
    this.reactionPreviewStream = null;
    this.reactionSetupOpen = false;
    this.reactionCountdown = 0;
    this.reactionPhase = 'discarded';
    this.reactionMessage = 'Reaction discarded. The coaster ride continues normally.';
    this.emitReactionAnalytics({ name: 'reaction_discarded' });
    this.emit();
  }

  async discardReactionRecording(): Promise<void> {
    await this.reactionController?.discard();
    this.clearReactionRecording();
    this.clearReactionMediaArtifacts();
    this.reactionPreviewStream = null;
    this.reactionSetupOpen = false;
    this.reactionPhase = 'discarded';
    this.reactionMessage = 'Reaction Cam media deleted from this session.';
    this.emitReactionAnalytics({ name: 'reaction_discarded' });
    this.emit();
  }

  setMediaReactionEnabled(value: boolean): void {
    this.reactionIncludeInExport = value && this.reactionAvailableForExport();
    this.mediaStatus = this.currentMediaArtifact('highlight_video') ? 'ready' : 'idle';
    this.mediaMessage = this.reactionIncludeInExport
      ? 'Reaction Cam will appear in newly generated videos and loops.'
      : 'New exports will use the coaster replay without Reaction Cam.';
    this.emit();
  }

  setMediaReactionMicrophoneEnabled(value: boolean): void {
    if (this.reactionIncludeMicrophoneInExport !== value) this.clearReactionMediaArtifacts();
    this.reactionIncludeMicrophoneInExport =
      value && Boolean(this.reactionRecording?.microphoneIncluded);
    this.mediaMessage = this.reactionIncludeMicrophoneInExport
      ? 'Reaction microphone will be mixed with ride sound.'
      : 'Reaction microphone is excluded from new exports.';
    this.emit();
  }

  startRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'editor') return;
    this.commitEdit();
    if (!this.snapshot().testAllowed) {
      this.status = this.snapshot().testDisabledReason ?? 'Finish the track before test riding.';
      this.emit();
      return;
    }
    if (this.reactionSetupOpen || this.reactionPreviewStream) this.closeReactionCamSetup();
    this.clearReactionRecording();
    this.clearReactionMediaArtifacts();
    void this.audio.unlock();
    if (
      this.tutorialStep === 2 &&
      this.tutorialOperatingPieceEdited &&
      !this.project.metadata.tags.includes('started_blank')
    ) {
      this.tutorialStep = 3;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
    this.beginRide(camera);
  }

  restartRide(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.clearReactionRecording();
    this.clearReactionMediaArtifacts();
    this.beginRide(camera);
  }

  private beginRide(
    camera: 'first-person' | 'third-person',
    monotonicOriginMs = performance.now(),
  ): void {
    if (!this.sampler) return;
    // Build the ride sampler from the committed project at ride entry. Editor previews and
    // incremental geometry rebuilds may legitimately reuse compiled curve data, but semantic
    // section behavior (lift, launch, brake, station) must never come from an older preview.
    // A fresh compiler keeps the live ride and the authoritative Ride Check on the same input.
    const rideTrack = compileProjectTrackResult(this.project, new TrackCompiler());
    this.acceptCompiledTrack(rideTrack);
    this.releaseRideSession(true);
    this.mode = transitionMode(this.mode, 'test');
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.handleRoot.visible = false;
    this.buildGuideRoot.visible = false;
    this.validationRoot.visible = false;
    this.debugRoot.visible = false;
    this.controls.enabled = false;
    this.simulationState = createTrackBoundState(
      initialTrainReferenceDistance(this.project, this.sampler),
      0,
    );
    this.rideRunner = new RideRunner({
      sampler: this.sampler,
      family: coasterFamilyForProject(this.project),
      initialDistance: this.simulationState.sRef,
      maxDurationSeconds: 180,
    });
    this.rideEventCursor = 0;
    this.audioSectionType = null;
    this.audioSectionId = undefined;
    this.audioIntensityLevel = 'comfortable';
    this.replayElapsedSeconds = 0;
    this.replayPaused = false;
    this.simulationFrame = null;
    this.lastRideSummary = null;
    this.peakSpeedMps = 0;
    this.peakVerticalG = 1;
    this.peakVerticalProgressPercent = 0;
    this.minVerticalG = 1;
    this.minVerticalProgressPercent = 0;
    this.peakLateralG = 0;
    this.peakLateralProgressPercent = 0;
    this.peakIntensity = { ...INITIAL_INTENSITY };
    this.peakIntensityScore = 0;
    this.visitedSectionTypes.clear();
    this.ridePhase = 'running';
    this.rideCycles += 1;
    this.rideDistanceM = 0;
    this.rideElapsedSeconds = 0;
    this.accumulator = 0;
    // Use the same origin recorded by Reaction Cam so the first fixed simulation step and the
    // reaction media timeline cannot begin from separate performance.now() samples.
    this.previousTime = monotonicOriginMs;
    this.cameraController.snapNextFrame();
    this.audio.emit({ type: 'station_hold' });
    this.status =
      this.fullValidation?.testPolicy === 'diagnostic_only'
        ? 'Diagnostic ride: this track has a technical issue to fix.'
        : 'Ride in progress.';
    this.emit();
  }

  setRideCamera(camera: 'first-person' | 'third-person'): void {
    if (this.mode !== 'ride-first-person' && this.mode !== 'ride-third-person') return;
    this.mode = transitionMode(
      this.mode,
      camera === 'first-person' ? 'ride-first-person' : 'ride-third-person',
    );
    this.cameraController.snapNextFrame();
    this.emit();
  }

  startReplay(): void {
    const trace = this.lastRideSummary?.trace ?? this.mediaTimeline?.trace;
    if (
      (this.mode !== 'editor' &&
        this.mode !== 'ride-first-person' &&
        this.mode !== 'ride-third-person') ||
      !trace?.length
    )
      return;
    if (this.mediaOpen) this.closeMediaStudio();
    this.mode = transitionMode(this.mode, 'replay');
    this.replayElapsedSeconds = 0;
    this.replayPaused = false;
    this.cameraController.snapNextFrame();
    this.audio.emit({ type: 'ui_confirm' });
    this.status = 'Cinematic replay.';
    this.emit();
  }

  toggleReplayPaused(): void {
    if (this.mode !== 'replay') return;
    this.replayPaused = !this.replayPaused;
    this.emit();
  }

  setCameraFov(value: number): void {
    this.cameraController.setSettings({ fieldOfView: value });
    this.persistRideSettings();
    this.emit();
  }

  setCameraShake(value: number): void {
    this.cameraController.setSettings({ shake: value });
    this.persistRideSettings();
    this.emit();
  }

  setReducedMotion(value: boolean): void {
    this.cameraController.setSettings({ reducedMotion: value });
    this.persistRideSettings();
    this.emit();
  }

  setMasterVolume(value: number): void {
    this.audio.setSettings({ masterVolume: value });
    this.persistRideSettings();
    this.emit();
  }

  setMuted(value: boolean): void {
    this.audio.setSettings({ muted: value });
    this.persistRideSettings();
    this.emit();
  }

  setTestSpeed(value: TestSpeed): void {
    if (![0.5, 1, 2, 4].includes(value)) return;
    if (this.reactionRideSpeedLocked() && value !== 1) {
      this.status = 'Reaction Cam rides run at 1× so your reactions stay synchronized.';
      this.emit();
      return;
    }
    this.testSpeed = value;
    this.persistRideSettings();
    this.emit();
  }

  returnToEditor(): void {
    if (
      this.mode !== 'ride-first-person' &&
      this.mode !== 'ride-third-person' &&
      this.mode !== 'replay' &&
      this.mode !== 'test'
    )
      return;
    if (
      this.reactionPhase === 'recording' ||
      this.reactionPhase === 'countdown' ||
      this.reactionPhase === 'previewing'
    )
      void this.stopAndDiscardReaction();
    this.mode = transitionMode(this.mode, 'editor');
    this.handleRoot.visible = this.session.snapshot().editorMode === 'advanced';
    this.buildGuideRoot.visible = true;
    this.validationRoot.visible = true;
    this.debugRoot.visible = this.debugVisible;
    this.controls.enabled = true;
    this.scene.camera.up.set(0, 1, 0);
    this.setSceneCameraFov(EDITOR_CAMERA_FOV);
    this.frameEditorOverview();
    this.rideRunner?.stop();
    this.audio.update({ speedMetersPerSecond: 0, sectionType: null, running: false });
    this.rebuildBuildGuides();
    if (!this.lastRide || this.ridePhase === 'running') this.finishRideResult('ended');
    const resultLabel =
      this.lastRide?.outcome === 'completed'
        ? 'Track complete.'
        : this.lastRide?.outcome === 'stalled'
          ? 'Ride stalled.'
          : 'Test ended.';
    this.ridePhase = 'idle';
    this.status = `${resultLabel} Top speed: ${(this.peakSpeedMps * MPH_PER_MPS).toFixed(1)} mph.`;
    const completedRunner = this.rideRunner;
    this.emit();
    requestAnimationFrame(() => {
      if (this.disposed || this.mode !== 'editor' || this.rideRunner !== completedRunner) return;
      this.releaseRideSession(true);
      this.emit();
    });
  }

  private releaseRideSession(clearSummary: boolean): void {
    this.diagnosticRideCompletionToken += 1;
    this.diagnosticRideCompletionActive = false;
    this.rideRunner?.stop();
    this.rideRunner = null;
    this.simulationFrame = null;
    this.simulationState = createTrackBoundState(0, 0);
    this.rideEventCursor = 0;
    this.audioSectionType = null;
    this.audioSectionId = undefined;
    this.visitedSectionTypes.clear();
    this.accumulator = 0;
    if (clearSummary) {
      this.lastRideSummary = null;
      this.replayElapsedSeconds = 0;
      this.replayPaused = false;
    }
    this.audio.update({ speedMetersPerSecond: 0, sectionType: null, running: false });
  }

  private frameEditorOverview(): void {
    const { x: plotWidth, z: plotDepth } = this.project.plot.sizeMeters;
    const { x: originX, y: originY, z: originZ } = this.project.plot.origin;
    const target = new THREE.Vector3(originX, originY + 5, originZ);
    this.controls.target.copy(target);
    this.scene.camera.position.set(
      target.x + plotWidth * 0.78,
      target.y + Math.max(80, plotDepth * 0.85),
      target.z + plotDepth * 1.28,
    );
    this.scene.camera.lookAt(target);
    this.controls.update();
  }

  resetFixture(): void {
    if (this.mode !== 'editor') return;
    this.cancelEdit();
    const reset = cloneProject(this.resetProject);
    reset.projectId = this.project.projectId;
    reset.revisionId = this.project.revisionId;
    reset.metadata = {
      ...reset.metadata,
      title: this.project.metadata.title,
      createdAt: this.project.metadata.createdAt,
      updatedAt: new Date().toISOString(),
      creator: this.project.metadata.creator,
    };
    this.session.execute(
      'Reset track',
      reset,
      nodeSelection([reset.track.nodes[2]?.id].filter(Boolean) as string[]),
    );
    this.rebuildProjectVisual();
    this.status = 'Track reset.';
    this.scheduleAutosave();
    this.emit();
  }

  loadDebugFixture(fixture: 'systems' | 'stall' | 'extreme'): void {
    if (
      !this.debugVisible ||
      this.mode === 'ride-first-person' ||
      this.mode === 'ride-third-person'
    )
      return;
    this.cancelEdit();
    this.activeFixture = fixture;
    this.replaceProject(cloneProject(fixtureFor(fixture)));
    this.selectNodeById(this.project.track.nodes[2]?.id ?? null);
    this.status =
      fixture === 'stall'
        ? 'Stall Test loaded.'
        : fixture === 'extreme'
          ? 'Extreme Warning Test loaded.'
          : 'Systems Circuit loaded.';
    this.emit();
  }

  loadDebugAuthoredPiece(descriptorId: string): void {
    if (
      !this.debugVisible ||
      this.mode === 'ride-first-person' ||
      this.mode === 'ride-third-person'
    )
      return;
    const descriptor = AUTHORED_TRACK_CATALOG.find((item) => item.id === descriptorId);
    if (!descriptor) {
      this.status = 'That authored catalog piece is unavailable.';
      this.emit();
      return;
    }
    const bank = descriptor.entry.banks.includes('level')
      ? 'level'
      : (descriptor.entry.banks[0] ?? 'level');
    const orientation = descriptor.entry.orientations.includes('upright')
      ? 'upright'
      : (descriptor.entry.orientations[0] ?? 'upright');
    const candidate = createAuthoredCandidate(
      descriptor.id,
      {
        x: 0,
        y: 256,
        z: 0,
        headingStep: 0,
        pitchDegrees: descriptor.entry.pitches[0] ?? 0,
        bank,
        orientation,
        windingQuarterTurns: orientation === 'inverted' ? 2 : 0,
      },
      descriptor.id === 'station.2t' ? 'station' : 'normal',
    );
    if (!candidate.ok) {
      this.status = `Catalog QA failed: ${candidate.message}`;
      this.emit();
      return;
    }
    const project = cloneProject(blankCoasterFixture);
    project.projectId = `debug.authored.${descriptor.id}`;
    project.revisionId = `debug.authored.${descriptor.id}.v1`;
    project.metadata = {
      ...project.metadata,
      title: descriptor.label,
      updatedAt: new Date().toISOString(),
    };
    const appended = appendAuthoredCandidate(project, candidate.value);
    if (!appended.ok) {
      this.status = `Catalog QA failed: ${appended.message}`;
      this.emit();
      return;
    }
    this.cancelEdit();
    this.replaceProject(appended.value);
    if (this.mode === 'menu') this.openEditor();
    this.trainVisual.root.visible = false;
    this.frameAuthoredDebugPiece(descriptor.family);
    this.status = `Catalog QA: ${descriptor.label}`;
    this.emit();
  }

  loadDebugAuthoredSequence(sequence: 'bank-left' | 'bank-right' | 'lift-ownership'): void {
    if (
      !this.debugVisible ||
      this.mode === 'ride-first-person' ||
      this.mode === 'ride-third-person'
    )
      return;
    const side = sequence === 'bank-right' ? 'right' : 'left';
    const definitions: readonly { id: string; type: AuthoredTrackType }[] | null =
      sequence === 'lift-ownership'
        ? [
            { id: 'station.2t', type: 'station' },
            { id: 'slope.0.25', type: 'lift' },
            { id: 'slope.25.hold.1t', type: 'lift' },
            { id: 'slope.25.0', type: 'lift' },
            { id: 'straight.2t', type: 'normal' },
            { id: 'turn.left.90.medium', type: 'normal' },
          ]
        : [
            { id: `bank.level.${side}.2t`, type: 'normal' },
            { id: `turn.${side}.90.medium`, type: 'normal' },
            { id: `bank.${side}.level.2t`, type: 'normal' },
          ];
    let project = cloneProject(blankCoasterFixture);
    project.projectId = `debug.authored.sequence.${sequence}`;
    project.revisionId = `debug.authored.sequence.${sequence}.v1`;
    project.metadata = {
      ...project.metadata,
      title:
        sequence === 'lift-ownership'
          ? 'Lift Ownership QA'
          : `${side === 'left' ? 'Left' : 'Right'} Banked Turn QA`,
      updatedAt: new Date().toISOString(),
    };
    let entry: AuthoredGridPose = {
      x: 0,
      y: 128,
      z: 0,
      headingStep: 0,
      pitchDegrees: 0 as const,
      bank: 'level' as const,
      orientation: 'upright' as const,
      windingQuarterTurns: 0,
    };
    for (const definition of definitions) {
      const candidate = createAuthoredCandidate(definition.id, entry, definition.type);
      if (!candidate.ok) {
        this.status = `Sequence QA failed: ${candidate.message}`;
        this.emit();
        return;
      }
      const appended = appendAuthoredCandidate(project, candidate.value);
      if (!appended.ok) {
        this.status = `Sequence QA failed: ${appended.message}`;
        this.emit();
        return;
      }
      project = appended.value;
      entry = candidate.value.exitPose;
    }
    this.cancelEdit();
    this.replaceProject(project);
    if (this.mode === 'menu') this.openEditor();
    this.trainVisual.root.visible = false;
    this.frameAuthoredDebugPiece(sequence === 'lift-ownership' ? 'slope' : 'turn');
    this.status =
      sequence === 'lift-ownership'
        ? 'Catalog QA: lift attachment ownership'
        : `Catalog QA: bank in, ${side} turn, bank out`;
    this.emit();
  }

  private frameAuthoredDebugPiece(family: string): void {
    const points: THREE.Vector3[] = [];
    this.project.track.nodes.forEach((node) => {
      points.push(new THREE.Vector3(node.position.x, node.position.y, node.position.z));
    });
    if (this.project.track.geometryKind === 'authored_grid_v2')
      this.project.track.pieces.forEach((piece) => {
        piece.spans.forEach((span) => {
          const control1 = fixedPointToWorld(span.control1);
          const control2 = fixedPointToWorld(span.control2);
          points.push(
            new THREE.Vector3(control1.x, control1.y, control1.z),
            new THREE.Vector3(control2.x, control2.y, control2.z),
          );
        });
      });
    if (!points.length) return;
    const bounds = new THREE.Box3().setFromPoints(points);
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const extent = Math.max(size.x, size.y, size.z, 8);
    this.controls.target.copy(center);
    const sideElevationFamily = family === 'vertical_loop' || family === 'half_loop';
    this.scene.camera.position
      .copy(center)
      .add(
        sideElevationFamily
          ? new THREE.Vector3(0, Math.max(extent * 0.08, 2), extent * 3)
          : new THREE.Vector3(extent * 1.35, Math.max(extent * 0.75, 8), extent * 1.35),
      );
    this.scene.camera.lookAt(center);
    this.controls.update();
  }

  completeRideForDiagnostics(): void {
    if (
      !this.debugVisible ||
      this.ridePhase !== 'running' ||
      !this.rideRunner ||
      this.diagnosticRideCompletionActive
    )
      return;
    const runner = this.rideRunner;
    const maximumSteps = Math.ceil(180 / FIXED_PHYSICS_STEP) + 1;
    const token = ++this.diagnosticRideCompletionToken;
    let completedSteps = 0;
    this.diagnosticRideCompletionActive = true;
    this.status = 'Skipping to the ride result…';
    this.emit();
    const runBatch = () => {
      if (
        this.disposed ||
        token !== this.diagnosticRideCompletionToken ||
        runner !== this.rideRunner ||
        this.ridePhase !== 'running'
      ) {
        if (token === this.diagnosticRideCompletionToken)
          this.diagnosticRideCompletionActive = false;
        return;
      }
      const batchEnd = Math.min(maximumSteps, completedSteps + 2_400);
      while (completedSteps < batchEnd && runner.result === 'running') {
        this.simulationFrame = runner.step(FIXED_PHYSICS_STEP);
        this.simulationState = this.simulationFrame.referenceState;
        this.peakSpeedMps = Math.max(this.peakSpeedMps, this.simulationState.velocity);
        this.updateRideMetrics(this.simulationFrame);
        this.syncRideEvents();
        completedSteps += 1;
      }
      if (runner.result === 'running' && completedSteps < maximumSteps) {
        requestAnimationFrame(runBatch);
        return;
      }
      this.diagnosticRideCompletionActive = false;
      this.lastRideSummary = runner.summary();
      this.endRide(
        runner.result === 'completed'
          ? 'completed'
          : runner.result === 'arrival_failed'
            ? 'arrival_failed'
            : 'stalled',
      );
    };
    requestAnimationFrame(runBatch);
  }

  undo(): void {
    if (this.mode !== 'editor') return;
    this.lastPlacedPresetId = null;
    this.lastPlacementRevision = null;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.cancelEdit();
    if (!this.session.undo()) return;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.invalidateRouteGuide();
    this.rebuildProjectVisual();
    this.status = 'Last change undone.';
    this.scheduleAutosave();
    this.emit();
  }

  redo(): void {
    if (this.mode !== 'editor') return;
    this.lastPlacedPresetId = null;
    this.lastPlacementRevision = null;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.cancelEdit();
    if (!this.session.redo()) return;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.invalidateRouteGuide();
    this.rebuildProjectVisual();
    this.status = 'Change restored.';
    this.scheduleAutosave();
    this.emit();
  }

  selectNode(index: number, additive = false): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    const nodeId = this.project.track.nodes[index]?.id ?? null;
    if (!nodeId) return;
    const existing = this.session.selection.nodeIds;
    const ids = additive
      ? existing.includes(nodeId)
        ? existing.filter((id) => id !== nodeId)
        : [...existing, nodeId]
      : [nodeId];
    this.session.setSelection(
      nodeSelection(ids, ids.includes(nodeId) ? nodeId : (ids.at(-1) ?? null)),
    );
    this.refreshSelectionVisuals();
    this.emit();
  }

  selectSection(index: number): void {
    if (this.mode !== 'editor') return;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    const clicked = this.project.track.sections[index];
    const piece = clicked
      ? this.project.editor.pieceGroups.find((candidate) =>
          candidate.sectionIds.includes(clicked.id),
        )
      : null;
    const representative = piece
      ? (piece.sectionIds
          .map((id) => this.project.track.sections.find((section) => section.id === id))
          .find((section) => section?.behavior.kind !== 'normal') ?? clicked)
      : clicked;
    this.session.setSelection(sectionSelection(representative?.id ?? null));
    this.refreshSelectionVisuals();
    this.emit();
  }

  beginEdit(continuous = false): void {
    if (this.mode !== 'editor') return;
    void continuous;
    if (this.gestureOpen) return;
    this.gestureOpen = true;
    this.session.beginGesture('Edit track');
  }

  commitEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    if (!this.gestureOpen) return;
    try {
      this.rebuildProjectVisual();
    } catch {
      this.session.cancelGesture();
      this.rebuildProjectVisual();
      this.gestureOpen = false;
      this.status = "That edit couldn't produce a continuous track.";
      this.emit();
      return;
    }
    this.session.commitGesture();
    this.gestureOpen = false;
    const completedFirstChallenge =
      this.tutorialStep === 1 && !this.project.metadata.tags.includes('started_blank');
    if (completedFirstChallenge) {
      this.tutorialStep = 2;
      this.tutorialOperatingPieceEdited = false;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
    this.status = completedFirstChallenge
      ? 'Track updated. Next: edit a track piece and take a test ride.'
      : 'Track updated.';
    this.scheduleAutosave();
    this.emit();
  }

  setElevation(value: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const selected = this.selectedNode();
    if (selected && Math.abs(selected.position.y - value) < 0.0001) return;
    this.markInteraction();
    this.beginEdit();
    const result = setNodeElevation(this.project, this.session.selection.nodeIds, value);
    if (result.ok) this.previewProject(result.project, true);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    this.editCommitTimer = window.setTimeout(() => this.commitEdit(), 220);
  }

  setBank(value: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const selected = this.selectedNode();
    if (selected && Math.abs(selected.bankDegrees - value) < 0.0001) return;
    this.markInteraction();
    this.beginEdit();
    const result = setNodeBank(this.project, this.session.selection.nodeIds, value);
    if (result.ok) this.previewProject(result.project, false);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    this.editCommitTimer = window.setTimeout(() => this.commitEdit(), 220);
  }

  previewRemoveWinding(): void {
    if (this.mode !== 'editor') return;
    const nodeIds = this.session.selection.nodeIds;
    const result = removeNodeRollWinding(this.project, nodeIds);
    if (!result.ok) return this.showOperationFailure(result.message);
    this.showProjectPreview({
      kind: 'remove_winding',
      presetId: null,
      label: 'Remove Inversion Winding',
      command: 'Remove inversion winding',
      acceptLabel: 'Remove Winding',
      project: result.project,
      message: result.message,
      severity: 'warning',
      canAccept: true,
      inline: false,
      issues: [
        {
          code: 'remove_inversion_winding',
          severity: 'warning',
          message:
            'This removes authored roll rotation from every selected point. Review the ghost track before applying.',
        },
      ],
      affectedNodeIds: nodeIds,
      affectedSectionIds: [],
      minPlotMarginM: null,
    });
  }

  setNodeCoordinate(axis: 'x' | 'y' | 'z', value: number): void {
    const selected = this.selectedNode();
    if (!selected || !Number.isFinite(value)) return;
    if (axis === 'y') {
      const target = Math.max(1, Math.min(65, value));
      if (Math.abs(selected.position.y - target) < 0.0001) return;
      const result = setNodeElevation(this.project, this.session.selection.nodeIds, target);
      if (result.ok)
        this.executeProjectOperation(
          'Set track height',
          result.project,
          result.message,
          `coordinate:y:${[...this.session.selection.nodeIds].sort().join('|')}`,
        );
      return;
    }
    const target = this.snappingEnabled ? Math.round(value) : value;
    if (Math.abs(selected.position[axis] - target) < 0.0001) return;
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds,
      {
        x: axis === 'x' ? target - selected.position.x : 0,
        y: 0,
        z: axis === 'z' ? target - selected.position.z : 0,
      },
      this.snappingEnabled,
    );
    if (result.ok)
      this.executeProjectOperation(
        `Set track ${axis.toUpperCase()}`,
        result.project,
        result.message,
        `coordinate:${axis}:${[...this.session.selection.nodeIds].sort().join('|')}`,
      );
  }

  setSnapping(enabled: boolean): void {
    this.snappingEnabled = enabled;
    this.status = enabled ? 'Snapping on.' : 'Snapping off.';
    this.emit();
  }

  traverseSelection(direction: -1 | 1): void {
    if (!this.project.track.nodes.length) return;
    const current = this.session.selection.primaryNodeId
      ? this.project.track.nodes.findIndex(
          (node) => node.id === this.session.selection.primaryNodeId,
        )
      : -1;
    const next =
      (current + direction + this.project.track.nodes.length) % this.project.track.nodes.length;
    this.selectNode(next);
  }

  focusSelection(): void {
    const selected = this.selectedNode();
    if (!selected) return;
    const target = new THREE.Vector3(selected.position.x, selected.position.y, selected.position.z);
    this.controls.target.copy(target);
    this.scene.camera.position.copy(target).add(new THREE.Vector3(24, 18, 24));
    this.scene.camera.lookAt(target);
    this.controls.update();
    this.status = `${this.snapshot().selectedNodeLabel ?? 'Track point'} framed.`;
    this.emit();
  }

  nudgeSelected(horizontal: number, forwardAmount: number): void {
    if (!this.selectedNodeId || this.mode !== 'editor') return;
    const forward = new THREE.Vector3();
    this.scene.camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
    forward.normalize();
    const right = new THREE.Vector3().crossVectors(forward, new THREE.Vector3(0, 1, 0)).normalize();
    const offset = right.multiplyScalar(horizontal).addScaledVector(forward, forwardAmount);
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds,
      {
        x: offset.x,
        y: 0,
        z: offset.z,
      },
      this.snappingEnabled,
    );
    if (result.ok)
      this.executeProjectOperation('Move track points', result.project, result.message);
  }

  setSectionBehavior(kind: TrackBehavior['kind']): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId || this.mode !== 'editor') return;
    if (
      this.project.track.geometryKind === 'modular_piecewise_bezier' &&
      this.session.snapshot().editorMode === 'basic'
    ) {
      this.status = 'Placed Track Types are fixed. Undo or delete the piece to replace it.';
      this.emit();
      return;
    }
    const current = this.project.track.sections.find((section) => section.id === sectionId);
    const behavior: TrackBehavior =
      kind === 'station'
        ? { kind, holdSeconds: 1.5, dispatchSpeedMps: 2.5 }
        : kind === 'lift'
          ? { kind, targetSpeedMps: 5 }
          : kind === 'launch'
            ? { kind, targetSpeedMps: 20, accelerationMps2: 7 }
            : kind === 'brake'
              ? { kind, targetSpeedMps: 6, decelerationMps2: 5 }
              : { kind };
    const result = setSectionBehaviorOperation(this.project, sectionId, behavior);
    if (result.ok) {
      this.executeProjectOperation('Change section type', result.project, result.message);
      if (
        current &&
        JSON.stringify(current.behavior) !== JSON.stringify(behavior) &&
        ['lift', 'launch', 'brake'].includes(behavior.kind)
      )
        this.markTutorialOperatingPieceEdit();
    }
  }

  setSectionParameter(parameter: string, value: number): void {
    const sectionId = this.session.selection.sectionId;
    const current = this.project.track.sections.find((section) => section.id === sectionId);
    if (!current || this.mode !== 'editor') return;
    const behavior = structuredClone(current.behavior) as TrackBehavior & Record<string, number>;
    if (!(parameter in behavior)) return;
    if (Math.abs((behavior[parameter] ?? 0) - value) < 0.0001) return;
    behavior[parameter] = value;
    const result = setSectionBehaviorOperation(this.project, current.id, behavior);
    if (result.ok) {
      this.executeProjectOperation(
        'Tune section',
        result.project,
        'Section settings updated.',
        `section:${current.id}:${parameter}`,
      );
      if (['lift', 'launch', 'brake'].includes(behavior.kind))
        this.markTutorialOperatingPieceEdit();
    }
  }

  insertSelectedSection(): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId) {
      this.status = 'Select a track section first.';
      this.emit();
      return;
    }
    const compiledSection = this.sampler?.table.sections.find(
      (section) => section.id === sectionId,
    );
    const sampled =
      compiledSection && this.sampler
        ? this.sampler.sampleByDistance(
            (compiledSection.startDistance + compiledSection.endDistance) * 0.5,
          ).position
        : undefined;
    const result = insertNodeAtSection(this.project, sectionId, sampled);
    if (result.ok)
      this.executeProjectOperation('Insert track point', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  deleteSelected(): void {
    const nodeId = this.session.selection.primaryNodeId;
    if (!nodeId) {
      this.status = 'Select a track point to delete.';
      this.emit();
      return;
    }
    const result = deleteNode(this.project, nodeId);
    if (result.ok) {
      const nextId =
        result.project.track.nodes[
          Math.max(0, this.project.track.nodes.findIndex((node) => node.id === nodeId) - 1)
        ]?.id;
      this.executeProjectOperation(
        'Delete track point',
        result.project,
        result.message,
        undefined,
        nodeSelection(nextId ? [nextId] : []),
      );
    } else this.showOperationFailure(result.message);
  }

  duplicateSelectedSection(): void {
    const sectionId = this.session.selection.sectionId;
    if (!sectionId) return this.showOperationFailure('Select a track section first.');
    const result = duplicateSection(this.project, sectionId);
    if (result.ok)
      this.executeProjectOperation('Duplicate section', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  smoothSelected(): void {
    const result = smoothNodes(this.project, this.session.selection.nodeIds);
    if (result.ok)
      this.showProjectPreview({
        kind: 'reshape',
        presetId: null,
        label: 'Smooth Selection',
        command: 'Smooth selection',
        acceptLabel: 'Apply Smooth',
        project: result.project,
        message: result.message,
        severity: 'clear',
        canAccept: true,
        inline: false,
        issues: [],
        affectedNodeIds: this.session.selection.nodeIds,
        affectedSectionIds: [],
        minPlotMarginM: null,
      });
    else this.showOperationFailure(result.message);
  }

  autoBankSelected(): void {
    void this.evaluateAutoBankSelection();
  }

  private async evaluateAutoBankSelection(): Promise<void> {
    if (this.mode !== 'editor' || this.autoBankPending) return;
    const nodeIds = [...this.session.selection.nodeIds];
    if (!nodeIds.length) return this.showOperationFailure('Select track points to auto-bank.');
    const candidates = generateAutoBankCandidates(this.project, nodeIds);
    if (!candidates.length)
      return this.showOperationFailure('These points do not have an adjustable bank profile.');

    const revision = this.session.snapshot().revision;
    const nodeIdsKey = [...nodeIds].sort().join('|');
    const token = ++this.autoBankToken;
    const project = structuredClone(this.project);
    const intervals = this.selectedTrackIntervals(nodeIds);
    const focusedHotspot =
      this.focusedLateralIssue?.revision === revision &&
      this.focusedLateralIssue.nodeIdsKey === nodeIdsKey;
    this.autoBankPending = true;
    this.status = 'Analyzing safer banking…';
    this.emit();

    try {
      const baseline = await this.autoBankValidationCoordinator.validate(project);
      if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
      if (baseline.metrics.outcome !== 'completed' || !baseline.technicalEligible) {
        this.status = 'Complete the ride successfully before using Auto-bank.';
        return;
      }
      const baselineGlobal = baseline.metrics.maxAbsLateralG;
      const baselineHotspot = focusedHotspot
        ? baselineGlobal
        : peakLateralGInIntervals(baseline.summary, intervals);
      const evaluated: Array<{
        candidate: (typeof candidates)[number];
        result: FullRunValidationResult;
        hotspot: number;
      }> = [];

      for (const candidate of candidates) {
        const result = await this.autoBankValidationCoordinator.validate(candidate.project);
        if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
        const hotspot = focusedHotspot
          ? result.metrics.maxAbsLateralG
          : peakLateralGInIntervals(result.summary, intervals);
        if (
          result.metrics.outcome === 'completed' &&
          result.technicalEligible &&
          !result.issues.some(
            (issue) => issue.category === 'technical' && issue.severity === 'blocker',
          ) &&
          result.metrics.maxAbsLateralG <= baselineGlobal + AUTO_BANK_GLOBAL_TOLERANCE_G &&
          baselineHotspot - hotspot >= AUTO_BANK_MINIMUM_IMPROVEMENT_G
        )
          evaluated.push({ candidate, result, hotspot });
      }

      if (!this.autoBankRequestIsCurrent(token, revision, nodeIdsKey)) return;
      evaluated.sort(
        (a, b) =>
          a.hotspot - b.hotspot ||
          a.result.metrics.maxAbsLateralG - b.result.metrics.maxAbsLateralG ||
          a.candidate.totalBankChangeDegrees - b.candidate.totalBankChangeDegrees ||
          a.candidate.scale - b.candidate.scale,
      );
      const best = evaluated[0];
      if (!best) {
        this.status =
          'Auto-bank couldn’t safely improve this section. Try reshaping the turn or adjusting the bank manually.';
        return;
      }

      const selected = new Set(nodeIds);
      const beforeBanks = project.track.nodes
        .filter((node) => selected.has(node.id))
        .map((node) => Math.abs(node.bankDegrees));
      const afterBanks = best.candidate.project.track.nodes
        .filter((node) => selected.has(node.id))
        .map((node) => Math.abs(node.bankDegrees));
      this.showProjectPreview({
        kind: 'auto_bank',
        presetId: null,
        label: 'Auto-bank Improvement',
        command: 'Auto-bank selection',
        acceptLabel: 'Apply Auto-bank',
        project: best.candidate.project,
        message: `Auto-bank reduced the highlighted lateral force from ${baselineHotspot.toFixed(2)} G to ${best.hotspot.toFixed(2)} G.`,
        severity: 'clear',
        canAccept: true,
        inline: false,
        issues: [],
        affectedNodeIds: nodeIds,
        affectedSectionIds: [],
        minPlotMarginM: null,
        autoBankComparison: {
          hotspotBeforeG: baselineHotspot,
          hotspotAfterG: best.hotspot,
          globalBeforeG: baselineGlobal,
          globalAfterG: best.result.metrics.maxAbsLateralG,
          maximumBankBeforeDegrees: Math.max(0, ...beforeBanks),
          maximumBankAfterDegrees: Math.max(0, ...afterBanks),
        },
      });
    } catch (error) {
      if (this.autoBankRequestIsCurrent(token, revision, nodeIdsKey))
        this.status =
          error instanceof Error && error.message.includes('superseded')
            ? 'Auto-bank stopped because the track changed.'
            : 'Auto-bank analysis could not finish. Your track was not changed.';
    } finally {
      if (token === this.autoBankToken) {
        this.autoBankPending = false;
        this.emit();
      }
    }
  }

  private selectedTrackIntervals(nodeIds: readonly string[]): readonly {
    startDistance: number;
    endDistance: number;
  }[] {
    if (!this.compiledTrack) return [];
    const indices = new Set(
      nodeIds
        .map((id) => this.project.track.nodes.findIndex((node) => node.id === id))
        .filter((index) => index >= 0),
    );
    return this.compiledTrack.spans
      .filter((span) => span.controlPointIndices.some((index) => indices.has(index)))
      .map((span) => ({ startDistance: span.startDistance, endDistance: span.endDistance }));
  }

  private autoBankRequestIsCurrent(token: number, revision: number, nodeIdsKey: string): boolean {
    return (
      token === this.autoBankToken &&
      this.mode === 'editor' &&
      this.session.snapshot().revision === revision &&
      [...this.session.selection.nodeIds].sort().join('|') === nodeIdsKey
    );
  }

  private cancelAutoBankAnalysis(): void {
    if (!this.autoBankPending) return;
    this.autoBankToken += 1;
    this.autoBankPending = false;
    this.autoBankValidationCoordinator.cancel();
  }

  alignSelected(axis: 'x' | 'y' | 'z'): void {
    const result = alignNodes(this.project, this.session.selection.nodeIds, axis);
    if (result.ok) this.executeProjectOperation(`Align ${axis}`, result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  rotateSelected(degrees: number): void {
    const result = rotateNodes(this.project, this.session.selection.nodeIds, degrees);
    if (result.ok) this.executeProjectOperation('Rotate selection', result.project, result.message);
    else this.showOperationFailure(result.message);
  }

  setTool(tool: EditorTool): void {
    this.session.setTool(tool);
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    this.refreshSelectionVisuals();
    this.emit();
  }

  setEditorMode(mode: EditorMode): void {
    if (
      mode === 'advanced' &&
      (this.project.track.geometryKind === 'modular_piecewise_bezier' ||
        this.project.track.geometryKind === 'authored_grid_v2') &&
      !this.project.track.closed
    ) {
      this.status =
        'Complete the modular circuit before using Freeform Edit. This keeps every piece aligned.';
      this.emit();
      return;
    }
    this.session.setEditorMode(mode);
    if (mode === 'basic') this.session.setSelectionTarget('piece');
    this.handleRoot.visible = this.mode === 'editor' && mode === 'advanced';
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    this.rebuildHandles();
    this.refreshSelectionVisuals();
    this.emit();
  }

  private selectedBuildParameters(preset: ElementPresetId): TrackPieceParameters {
    if (preset === 'straight') return { length: this.buildOptions.length };
    if (preset === 'turn_left' || preset === 'turn_right')
      return {
        turnDegrees: this.buildOptions.turnDegrees,
        radius: this.buildOptions.radius,
      };
    if (preset === 'slope_up' || preset === 'slope_down')
      return { targetPitchDegrees: this.buildOptions.pitchTargetDegrees };
    if (preset === 'bank_left' || preset === 'bank_right' || preset === 'bank_level')
      return { targetBankDegrees: this.buildOptions.bankTargetDegrees };
    return {};
  }

  private compositeSelectionFor(preset: ElementPresetId): RuntimeBuildSelection | null {
    const systemType =
      preset === 'lift' || preset === 'launch' || preset === 'brake' || preset === 'photo'
        ? preset
        : null;
    const pose = modularEndPose(this.project);
    let shapeId = (systemType ? 'straight' : preset) as ModularTrackShapeId;
    if ((preset === 'slope_up' || preset === 'slope_down') && pose) {
      let target = this.buildOptions.pitchTargetDegrees;
      if (target === pose.pitchDegrees || Math.abs(target - pose.pitchDegrees) > 90) {
        target =
          MODULAR_PITCH_STATES.find(
            (candidate) => candidate > pose.pitchDegrees && candidate - pose.pitchDegrees <= 90,
          ) ??
          [...MODULAR_PITCH_STATES]
            .reverse()
            .find(
              (candidate) => candidate < pose.pitchDegrees && pose.pitchDegrees - candidate <= 90,
            )!;
        this.buildOptions = { ...this.buildOptions, pitchTargetDegrees: target };
      }
      shapeId = target > pose.pitchDegrees ? 'slope_up' : 'slope_down';
    }
    if ((preset === 'bank_left' || preset === 'bank_right' || preset === 'bank_level') && pose) {
      let target = this.buildOptions.bankTargetDegrees;
      if (target === pose.bankDegrees || Math.abs(target - pose.bankDegrees) > 90) {
        target =
          MODULAR_BANK_STATES.find(
            (candidate) => candidate > pose.bankDegrees && candidate - pose.bankDegrees <= 90,
          ) ??
          [...MODULAR_BANK_STATES]
            .reverse()
            .find(
              (candidate) => candidate < pose.bankDegrees && pose.bankDegrees - candidate <= 90,
            )!;
        this.buildOptions = { ...this.buildOptions, bankTargetDegrees: target };
      }
      shapeId =
        target === 0 ? 'bank_level' : target < pose.bankDegrees ? 'bank_left' : 'bank_right';
    }
    if (!MODULAR_BUILD_SHAPES.includes(shapeId)) return null;
    return {
      shapeId,
      parameters: this.selectedBuildParameters(shapeId),
      trackType: systemType ?? this.buildSelection.trackType,
    };
  }

  private refreshCompositePreview(): void {
    if (
      this.project.track.geometryKind !== 'modular_piecewise_bezier' ||
      this.project.track.closed ||
      !this.project.track.nodes.length
    ) {
      this.emit();
      return;
    }
    const shape = this.buildSelection.shapeId;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.selectBuildPiece(shape);
  }

  private refreshBuildPreview(): void {
    const preview = this.presetPreview;
    const preset = preview?.presetId;
    if (!preset || preview.kind !== 'piece') {
      this.emit();
      return;
    }
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.selectBuildPiece(preset);
  }

  setBuildLength(value: RuntimeBuildOptions['length']): void {
    if (this.buildOptions.length === value) return;
    this.buildOptions = { ...this.buildOptions, length: value };
    this.buildSelection = {
      ...this.buildSelection,
      parameters: { ...this.buildSelection.parameters, length: value },
    };
    this.refreshCompositePreview();
  }

  setBuildTurnDegrees(value: RuntimeBuildOptions['turnDegrees']): void {
    if (this.buildOptions.turnDegrees === value) return;
    this.buildOptions = { ...this.buildOptions, turnDegrees: value };
    this.buildSelection = {
      ...this.buildSelection,
      parameters: { ...this.buildSelection.parameters, turnDegrees: value },
    };
    this.refreshCompositePreview();
  }

  setBuildRadius(value: RuntimeBuildOptions['radius']): void {
    if (this.buildOptions.radius === value) return;
    this.buildOptions = { ...this.buildOptions, radius: value };
    this.buildSelection = {
      ...this.buildSelection,
      parameters: { ...this.buildSelection.parameters, radius: value },
    };
    this.refreshCompositePreview();
  }

  setBuildPitchTarget(value: RuntimeBuildOptions['pitchTargetDegrees']): void {
    const entry = modularEndPose(this.project);
    if (!entry || value === entry.pitchDegrees || Math.abs(value - entry.pitchDegrees) > 90) return;
    this.buildOptions = { ...this.buildOptions, pitchTargetDegrees: value };
    this.buildSelection = {
      ...this.buildSelection,
      shapeId: value > entry.pitchDegrees ? 'slope_up' : 'slope_down',
      parameters: { targetPitchDegrees: value },
    };
    this.refreshCompositePreview();
  }

  setBuildBankTarget(value: RuntimeBuildOptions['bankTargetDegrees']): void {
    const entry = modularEndPose(this.project);
    if (!entry || value === entry.bankDegrees || Math.abs(value - entry.bankDegrees) > 90) return;
    this.buildOptions = { ...this.buildOptions, bankTargetDegrees: value };
    this.buildSelection = {
      ...this.buildSelection,
      shapeId: value === 0 ? 'bank_level' : value < entry.bankDegrees ? 'bank_left' : 'bank_right',
      parameters: { targetBankDegrees: value },
      trackType: 'normal',
    };
    this.refreshCompositePreview();
  }

  setBuildTrackType(value: TrackType): void {
    if (this.buildSelection.trackType === value) return;
    const entry = modularEndPose(this.project);
    if (!entry) return;
    const compatibility = trackTypeCompatibility(
      this.buildSelection.shapeId,
      entry,
      value,
      this.buildSelection.parameters,
    );
    if (!compatibility.allowed) {
      this.status = compatibility.reason ?? 'That Track Type is unavailable here.';
      this.emit();
      return;
    }
    this.buildSelection = { ...this.buildSelection, trackType: value };
    this.refreshCompositePreview();
  }

  setSelectionTarget(target: 'shape' | 'piece'): void {
    if (this.mode !== 'editor') return;
    if (this.session.snapshot().editorMode === 'basic' && target === 'shape') return;
    this.session.setSelectionTarget(target);
    this.hoveredEditorTarget = null;
    this.pointerEditor.clearHover();
    if (target === 'shape' && this.session.selection.sectionId) {
      const fallback = this.project.track.nodes[Math.min(2, this.project.track.nodes.length - 1)];
      this.session.setSelection(nodeSelection(fallback ? [fallback.id] : []));
    } else if (target === 'piece' && this.session.selection.nodeIds.length) {
      this.session.setSelection(sectionSelection(null));
    }
    this.refreshSelectionVisuals();
    this.status =
      target === 'shape'
        ? 'Freeform Edit: drag a cyan point to reshape the track.'
        : 'Edit Piece: click the rails to change lift, launch, brakes, or station settings.';
    this.emit();
  }

  setOverlay(overlay: EditorOverlay): void {
    this.session.setOverlay(overlay);
    this.rebuildValidationOverlay();
    this.emit();
  }

  selectBuildPiece(preset: ElementPresetId): void {
    if (
      preset === 'station' &&
      (this.project.track.geometryKind === 'modular_piecewise_bezier' ||
        this.project.track.geometryKind === 'authored_grid_v2') &&
      this.project.track.nodes.length === 0
    ) {
      this.startStationPlacement();
      return;
    }
    const composite =
      this.project.track.geometryKind === 'modular_piecewise_bezier'
        ? this.compositeSelectionFor(preset)
        : null;
    if (
      this.presetPreview?.presetId === preset &&
      (this.presetPreview.kind === 'station' || this.presetPreview.kind === 'piece')
    ) {
      this.acceptPreview();
      return;
    }
    if (composite) {
      const entry = modularEndPose(this.project);
      let trackType = composite.trackType;
      if (entry) {
        const compatibility = trackTypeCompatibility(
          composite.shapeId,
          entry,
          trackType,
          composite.parameters,
        );
        if (!compatibility.allowed) {
          trackType = 'normal';
          this.status = `${compatibility.reason} Track Type reset to Normal.`;
        }
      }
      this.buildSelection = {
        ...composite,
        trackType,
      };
    }
    const candidate = buildPresetCandidate(this.project, preset, this.session.selection.sectionId, {
      stationPlacement: this.stationPlacement,
      parameters: composite?.parameters ?? this.selectedBuildParameters(preset),
      ...(composite ? { trackType: this.buildSelection.trackType } : {}),
    });
    if ('ok' in candidate) {
      if (!candidate.ok) this.showOperationFailure(candidate.message);
      return;
    }
    const preflightIssues = candidate.preflight.issues.map((issue) => ({
      code: issue.code,
      severity: issue.severity,
      message: issue.message,
    }));
    const guidanceIssues = candidate.warnings.map((message, index) => ({
      code: `preset_guidance_${index + 1}`,
      severity: 'warning' as const,
      message,
    }));
    const impact = finishImpact(this.project, candidate.project);
    const losesFinishRange =
      impact?.finishEligibleBefore === true && impact.finishEligibleAfter === false;
    const materiallyWorsensFinish = Boolean(
      impact &&
      (impact.distanceAfterM >= impact.distanceBeforeM + 5 ||
        impact.headingAfterDegrees >= impact.headingBeforeDegrees + 20),
    );
    const finishIssues =
      losesFinishRange || materiallyWorsensFinish
        ? [
            {
              code: losesFinishRange ? 'finish_range_lost' : 'finish_route_worsened',
              severity: 'warning' as const,
              message: losesFinishRange
                ? `This piece moves the track exit outside Finish range (${impact!.distanceBeforeM.toFixed(0)} m → ${impact!.distanceAfterM.toFixed(0)} m).`
                : `This piece moves the track exit farther from the station (${impact!.distanceBeforeM.toFixed(0)} m → ${impact!.distanceAfterM.toFixed(0)} m).`,
            },
          ]
        : [];
    const issues = [...preflightIssues, ...guidanceIssues, ...finishIssues];
    const severity = candidate.preflight.canAccept
      ? issues.length
        ? ('warning' as const)
        : ('clear' as const)
      : ('blocker' as const);
    const kind =
      preset === 'station'
        ? ('station' as const)
        : this.project.track.closed
          ? ('reshape' as const)
          : ('piece' as const);
    const previewLabel =
      composite && preset !== 'station'
        ? compositePieceLabel(candidate.preset.label, this.buildSelection.trackType)
        : candidate.preset.label;
    this.showProjectPreview({
      kind,
      presetId: candidate.preset.id,
      label: previewLabel,
      command: `Add ${previewLabel}`,
      acceptLabel:
        preset === 'station'
          ? 'Place Station'
          : this.project.track.closed
            ? `Apply ${previewLabel}`
            : `Build ${previewLabel}`,
      project: candidate.project,
      message: candidate.message,
      severity,
      canAccept: candidate.preflight.canAccept,
      inline: kind === 'piece',
      issues,
      affectedNodeIds: candidate.affectedNodeIds,
      affectedSectionIds: candidate.affectedSectionIds,
      minPlotMarginM: Number.isFinite(candidate.preflight.minPlotMarginM)
        ? candidate.preflight.minPlotMarginM
        : null,
      finishImpact: impact,
      ...(candidate.focusNodeId ? { focusNodeId: candidate.focusNodeId } : {}),
    });
  }

  acceptPreview(): void {
    if (this.stationPlacementActive) {
      this.commitStationPlacement();
      return;
    }
    if (!this.presetPreview?.canAccept) return;
    const preview = this.presetPreview;
    const revisionBefore = this.session.snapshot().revision;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.executeProjectOperation(
      preview.command,
      preview.project,
      preview.message,
      undefined,
      preview.focusNodeId ? nodeSelection([preview.focusNodeId]) : this.session.selection,
    );
    const revisionAfter = this.session.snapshot().revision;
    if (
      revisionAfter > revisionBefore &&
      preview.presetId &&
      (preview.kind === 'station' || preview.kind === 'piece')
    ) {
      this.lastPlacedPresetId = preview.presetId;
      this.lastPlacementRevision = revisionAfter;
    }
    if (preview.focusNodeId && !this.project.track.closed) this.focusBuildEnd();
    if (preview.kind === 'finish' || preview.kind === 'direct_join') {
      this.tutorialVisible = false;
      this.emit();
    }
    if (
      preview.presetId &&
      this.tutorialStep === 1 &&
      !this.project.metadata.tags.includes('started_blank')
    ) {
      this.tutorialStep = 2;
      this.tutorialOperatingPieceEdited = false;
      void this.repository.setPreference('tutorial', {
        visible: this.tutorialVisible,
        step: this.tutorialStep,
      });
    }
    if (
      revisionAfter > revisionBefore &&
      preview.kind === 'piece' &&
      preview.authoredDescriptorId &&
      !this.project.track.closed
    ) {
      this.selectAuthoredPiece(preview.authoredDescriptorId);
      const repeatedPreview = this.presetPreview as ProjectPreview | null;
      if (repeatedPreview?.canAccept)
        this.status = `${preview.label} added. The next identical piece is previewed.`;
      else {
        const reason = repeatedPreview?.issues.find(
          (issue) => issue.severity === 'blocker',
        )?.message;
        this.presetPreview = null;
        this.clearPresetPreviewVisual();
        this.status = `${preview.label} added. ${reason ?? 'Choose another authored piece.'}`;
      }
      this.emit();
      return;
    }
    if (
      revisionAfter > revisionBefore &&
      preview.kind === 'piece' &&
      preview.presetId &&
      !this.project.track.closed
    ) {
      const transitionPiece = [
        'slope_up',
        'slope_down',
        'bank_left',
        'bank_right',
        'bank_level',
      ].includes(preview.presetId);
      if (!transitionPiece) {
        this.selectBuildPiece(preview.presetId);
        const repeatedPreview = this.presetPreview as ProjectPreview | null;
        if (repeatedPreview?.canAccept) {
          this.status = `${preview.label} added. Next ${preview.label} preview ready.`;
        } else {
          const reason = repeatedPreview?.issues.find(
            (issue) => issue.severity === 'blocker',
          )?.message;
          this.presetPreview = null;
          this.clearPresetPreviewVisual();
          this.status = `${preview.label} added. ${reason ?? 'Choose a different piece next.'}`;
        }
      } else {
        this.presetPreview = null;
        this.clearPresetPreviewVisual();
        this.status = `${preview.label} added. Choose the next Track Shape.`;
      }
      this.emit();
    }
  }

  startStationPlacement(): void {
    if (
      this.mode !== 'editor' ||
      (this.project.track.geometryKind !== 'modular_piecewise_bezier' &&
        this.project.track.geometryKind !== 'authored_grid_v2') ||
      this.project.track.nodes.length > 0
    )
      return;
    this.stationPlacementActive = true;
    this.controls.enabled = false;
    this.canvas.classList.add('is-placing-station');
    this.updateStationPlacement(this.stationPlacementPosition.x, this.stationPlacementPosition.z);
    this.status =
      'Move the station on the grid. Click to place, Q/E to rotate, or Escape to cancel.';
    this.emit();
  }

  private updateStationPlacement(x: number, z: number): void {
    if (!this.stationPlacementActive) return;
    const position = {
      x: Math.round(x / 4) * 4,
      y: this.project.plot.groundElevationMeters + 4,
      z: Math.round(z / 4) * 4,
    };
    this.stationPlacementPosition = position;
    if (this.project.track.geometryKind === 'authored_grid_v2') {
      const fixed = worldToFixedPoint(position);
      const candidate = createAuthoredCandidate(
        'station.2t',
        {
          ...fixed,
          headingStep: this.stationPlacementHeading,
          pitchDegrees: 0,
          bank: 'level',
          orientation: 'upright',
          windingQuarterTurns: 0,
        },
        'station',
      );
      if (!candidate.ok) {
        this.stationPlacementValid = false;
        this.stationPlacementReason = candidate.message;
        this.presetPreview = null;
        this.clearPresetPreviewVisual();
        this.emit();
        return;
      }
      const appended = appendAuthoredCandidate(this.project, candidate.value);
      if (!appended.ok) {
        this.stationPlacementValid = false;
        this.stationPlacementReason = appended.message;
        this.presetPreview = null;
        this.clearPresetPreviewVisual();
        this.emit();
        return;
      }
      const preflight = assessCandidateProject(
        this.project,
        appended.value,
        appended.value.track.nodes.map((node) => node.id),
        appended.value.track.sections.map((section) => section.id),
      );
      const issues = preflight.issues.map((issue) => ({
        code: issue.code,
        severity: issue.severity,
        message: issue.message,
      }));
      this.stationPlacementValid = preflight.canAccept;
      this.stationPlacementReason =
        issues.find((issue) => issue.severity === 'blocker')?.message ?? null;
      this.showProjectPreview({
        kind: 'station',
        presetId: 'station',
        authoredDescriptorId: 'station.2t',
        label: 'Station',
        command: 'Place station',
        acceptLabel: 'Place Station',
        project: appended.value,
        message: 'Station placed. Build from the highlighted exit.',
        severity: preflight.canAccept ? (issues.length ? 'warning' : 'clear') : 'blocker',
        canAccept: preflight.canAccept,
        inline: true,
        issues,
        affectedNodeIds: appended.value.track.nodes.map((node) => node.id),
        affectedSectionIds: appended.value.track.sections.map((section) => section.id),
        minPlotMarginM: Number.isFinite(preflight.minPlotMarginM) ? preflight.minPlotMarginM : null,
        focusNodeId: appended.value.track.nodes.at(-1)?.id,
      });
      return;
    }
    const result = placeModularStation(this.project, position, this.stationPlacementHeading);
    if (!result.ok) {
      this.stationPlacementValid = false;
      this.stationPlacementReason = result.message;
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
      this.emit();
      return;
    }
    const affectedNodeIds = result.project.track.nodes.map((node) => node.id);
    const affectedSectionIds = result.project.track.sections.map((section) => section.id);
    const preflight = assessCandidateProject(
      this.project,
      result.project,
      affectedNodeIds,
      affectedSectionIds,
    );
    const issues = preflight.issues.map((issue) => ({
      code: issue.code,
      severity: issue.severity,
      message: issue.message,
    }));
    this.stationPlacementValid = preflight.canAccept;
    this.stationPlacementReason =
      issues.find((issue) => issue.severity === 'blocker')?.message ?? null;
    this.showProjectPreview({
      kind: 'station',
      presetId: 'station',
      label: 'Station',
      command: 'Place station',
      acceptLabel: 'Place Station',
      project: result.project,
      message: 'Station placed. Build from the highlighted exit.',
      severity: preflight.canAccept ? (issues.length ? 'warning' : 'clear') : 'blocker',
      canAccept: preflight.canAccept,
      inline: true,
      issues,
      affectedNodeIds,
      affectedSectionIds,
      minPlotMarginM: Number.isFinite(preflight.minPlotMarginM) ? preflight.minPlotMarginM : null,
      focusNodeId: result.project.track.nodes.at(-1)?.id,
    });
  }

  rotateStationPlacement(direction: -1 | 1): void {
    if (!this.stationPlacementActive) return;
    const headings = [0, 2, 4, 6] as const;
    const index = headings.indexOf(this.stationPlacementHeading);
    this.stationPlacementHeading =
      headings[(index + direction + headings.length) % headings.length]!;
    this.updateStationPlacement(this.stationPlacementPosition.x, this.stationPlacementPosition.z);
  }

  nudgeStationPlacement(dx: number, dz: number): void {
    if (!this.stationPlacementActive) return;
    this.updateStationPlacement(
      this.stationPlacementPosition.x + dx * 4,
      this.stationPlacementPosition.z + dz * 4,
    );
  }

  commitStationPlacement(): void {
    if (
      !this.stationPlacementActive ||
      !this.stationPlacementValid ||
      !this.presetPreview?.canAccept
    )
      return;
    this.stationPlacementActive = false;
    this.controls.enabled = true;
    this.canvas.classList.remove('is-placing-station');
    this.acceptPreview();
    this.buildSelection = {
      shapeId: 'straight',
      parameters: { length: 'medium' },
      trackType: 'normal',
    };
    this.buildOptions = {
      ...this.buildOptions,
      length: 'medium',
      turnDegrees: 45,
      radius: 'medium',
      pitchTargetDegrees: 30,
      bankTargetDegrees: -30,
    };
    if (!this.project.track.closed) {
      if (this.project.track.geometryKind === 'authored_grid_v2')
        this.selectAuthoredPiece('straight.2t');
      else this.selectBuildPiece('straight');
    }
    this.status = 'Station placed. Choose the next Track Shape.';
    this.emit();
  }

  cancelStationPlacement(): void {
    if (!this.stationPlacementActive) return;
    this.stationPlacementActive = false;
    this.controls.enabled = true;
    this.canvas.classList.remove('is-placing-station');
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.rebuildBuildGuides();
    this.status = 'Station placement cancelled.';
    this.emit();
  }

  setStationPlacement(value: StationPlacement): void {
    if (this.project.track.nodes.length > 0 || this.stationPlacement === value) return;
    this.stationPlacement = value;
    if (this.presetPreview?.presetId === 'station') {
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
      this.selectBuildPiece('station');
    } else this.emit();
  }

  selectAuthoredPiece(descriptorId: string): void {
    if (
      this.mode !== 'editor' ||
      this.project.track.geometryKind !== 'authored_grid_v2' ||
      this.project.track.closed
    )
      return;
    if (descriptorId === 'station.2t' && this.project.track.pieces.length === 0) {
      this.startStationPlacement();
      return;
    }
    const entry = authoredEndPose(this.project);
    if (!entry) {
      this.status = 'Place the station first.';
      this.emit();
      return;
    }
    if (
      this.presetPreview?.kind === 'piece' &&
      this.presetPreview.authoredDescriptorId === descriptorId
    ) {
      this.acceptPreview();
      return;
    }
    const descriptor = AUTHORED_TRACK_CATALOG.find((item) => item.id === descriptorId);
    if (!descriptor) {
      this.showOperationFailure('That authored track piece is unavailable.');
      return;
    }
    const sequenceIssue = authoredSequenceIssue(this.project, descriptorId);
    if (sequenceIssue) {
      this.showOperationFailure(sequenceIssue);
      return;
    }
    const requestedTrackType = this.authoredTrackType;
    let trackType: Exclude<AuthoredTrackType, 'station'> = this.authoredTrackType;
    if (!descriptor.allowedTrackTypes.includes(trackType)) {
      trackType = 'normal';
      this.authoredTrackType = 'normal';
    }
    const candidate = createAuthoredCandidate(descriptorId, entry, trackType);
    if (!candidate.ok) {
      this.showOperationFailure(candidate.message);
      return;
    }
    const appended = appendAuthoredCandidate(this.project, candidate.value);
    if (!appended.ok) {
      this.showOperationFailure(appended.message);
      return;
    }
    const baseNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
    const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
    const affectedNodeIds = appended.value.track.nodes
      .filter((node) => !baseNodeIds.has(node.id))
      .map((node) => node.id);
    const affectedSectionIds = appended.value.track.sections
      .filter((section) => !baseSectionIds.has(section.id))
      .map((section) => section.id);
    const preflight = assessCandidateProject(
      this.project,
      appended.value,
      affectedNodeIds,
      affectedSectionIds,
    );
    const issues = preflight.issues.map((issue) => ({
      code: issue.code,
      severity: issue.severity,
      message: issue.message,
    }));
    this.authoredDescriptorId = descriptorId;
    this.showProjectPreview({
      kind: 'piece',
      presetId: null,
      authoredDescriptorId: descriptorId,
      label: descriptor.label,
      command: `Add ${descriptor.label}`,
      acceptLabel: `Build ${descriptor.label}`,
      project: appended.value,
      message: `${descriptor.label} added without changing earlier track.`,
      severity: preflight.canAccept ? (issues.length ? 'warning' : 'clear') : 'blocker',
      canAccept: preflight.canAccept,
      inline: true,
      issues,
      affectedNodeIds,
      affectedSectionIds,
      minPlotMarginM: Number.isFinite(preflight.minPlotMarginM) ? preflight.minPlotMarginM : null,
      focusNodeId: appended.value.track.nodes.at(-1)?.id,
    });
    if (requestedTrackType !== trackType) {
      this.status = `Track Type changed to Normal because ${descriptor.label} cannot use ${requestedTrackType} track. ${descriptor.label} preview ready.`;
      this.emit();
    }
  }

  setAuthoredTrackType(value: 'normal' | 'lift' | 'launch' | 'brake' | 'photo'): void {
    if (this.project.track.geometryKind !== 'authored_grid_v2' || this.authoredTrackType === value)
      return;
    const descriptor = AUTHORED_TRACK_CATALOG.find((item) => item.id === this.authoredDescriptorId);
    if (!descriptor?.allowedTrackTypes.includes(value)) {
      this.status = `${descriptor?.label ?? 'This piece'} cannot use ${value} track.`;
      this.emit();
      return;
    }
    this.authoredTrackType = value;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.selectAuthoredPiece(this.authoredDescriptorId);
  }

  setRouteGuideEnabled(value: boolean): void {
    this.routeGuideEnabled = value;
    this.invalidateRouteGuide();
    this.rebuildBuildGuides();
    this.emit();
  }

  deleteLastBuiltPiece(): void {
    const revision = this.session.snapshot().revision;
    if (this.mode === 'editor' && this.project.track.geometryKind === 'authored_grid_v2') {
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
      this.cancelAutoBankAnalysis();
      this.focusedLateralIssue = null;
      this.cancelEdit();
      const result = removeLastAuthoredPiece(this.project);
      if (!result.ok) {
        this.showOperationFailure(result.message);
        return;
      }
      this.executeProjectOperation(
        'Delete last authored piece',
        result.value,
        'Last piece removed. Every earlier fixed piece is unchanged.',
        undefined,
        sectionSelection(null),
      );
      this.emit();
      return;
    }
    if (this.mode === 'editor' && this.project.track.geometryKind === 'modular_piecewise_bezier') {
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
      this.lastPlacedPresetId = null;
      this.lastPlacementRevision = null;
      this.cancelAutoBankAnalysis();
      this.focusedLateralIssue = null;
      this.cancelEdit();
      const result = removeLastModularPiece(this.project);
      if (!result.ok) {
        this.showOperationFailure(result.message);
        return;
      }
      this.executeProjectOperation(
        'Delete last track piece',
        result.project,
        result.message,
        undefined,
        sectionSelection(null),
      );
      this.status = 'Last piece removed. Earlier track is unchanged.';
      this.emit();
      return;
    }
    if (
      this.mode !== 'editor' ||
      this.lastPlacedPresetId === null ||
      this.lastPlacementRevision !== revision
    )
      return;
    const preset = this.presetPreview?.presetId ?? this.lastPlacedPresetId;
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.lastPlacedPresetId = null;
    this.lastPlacementRevision = null;
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.cancelEdit();
    if (!this.session.undo()) return;
    this.rebuildProjectVisual();
    this.scheduleAutosave();
    if (this.project.track.nodes.length > 0 && !this.project.track.closed)
      this.selectBuildPiece(preset);
    this.status = 'Last placed piece deleted.';
    this.emit();
  }

  cancelPreview(): void {
    if (this.stationPlacementActive) {
      this.cancelStationPlacement();
      return;
    }
    this.presetPreview = null;
    this.clearPresetPreviewVisual();
    this.rebuildBuildGuides();
    this.status = 'Preview cancelled.';
    this.emit();
  }

  connectEndpoints(): void {
    const result = connectEndpoints(this.project);
    if (result.ok) {
      const beforeLength = compiledTrackLength(this.project);
      const afterLength = compiledTrackLength(result.project);
      const baseNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
      const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
      this.showProjectPreview({
        kind: 'direct_join',
        presetId: null,
        label: 'Direct Join to Station',
        command: 'Direct Join to Station',
        acceptLabel: 'Finish Track',
        project: result.project,
        message: result.message,
        severity: 'warning',
        canAccept: true,
        inline: false,
        issues: [
          {
            code: 'direct_join_guidance',
            severity: 'warning',
            message: 'Direct Join is a precision option. Check the highlighted closing span.',
          },
        ],
        affectedNodeIds: result.project.track.nodes
          .filter((node) => !baseNodeIds.has(node.id))
          .map((node) => node.id),
        affectedSectionIds: result.project.track.sections
          .filter((section) => !baseSectionIds.has(section.id))
          .map((section) => section.id),
        minPlotMarginM: null,
        generatedLengthM:
          beforeLength !== null && afterLength !== null
            ? Math.max(0, afterLength - beforeLength)
            : null,
        addedNodeCount: result.project.track.nodes.length - this.project.track.nodes.length,
      });
    } else this.showOperationFailure(result.message);
  }

  finishToStation(): void {
    if (this.finishChecking) return;
    if (this.project.track.geometryKind === 'authored_grid_v2') {
      const plan = planAuthoredAutocomplete(this.project, {
        maxPieces: 12,
        maxStates: 25_000,
        timeBudgetMs: matchMedia('(pointer: coarse)').matches ? 250 : 100,
      });
      if (!plan.ok) {
        this.showOperationFailure(plan.message);
        return;
      }
      const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
      const addedSectionIds = plan.value.project.track.sections
        .filter((section) => !baseSectionIds.has(section.id))
        .map((section) => section.id);
      const preflight = assessCandidateProject(
        this.project,
        plan.value.project,
        plan.value.project.track.nodes.map((node) => node.id),
        addedSectionIds,
      );
      if (!preflight.canAccept) {
        this.showOperationFailure(
          preflight.issues.find((issue) => issue.severity === 'blocker')?.message ??
            'The autocomplete route reaches the station but does not have safe track clearance.',
        );
        return;
      }
      this.finishChecking = true;
      this.status = 'Checking the autocomplete route and ride physics…';
      this.emit();
      void validateFullRide(plan.value.project)
        .then((validation) => {
          if (this.disposed) return;
          this.finishChecking = false;
          const completed = validation.metrics.outcome === 'completed';
          const routeSummary =
            plan.value.mode === 'standard'
              ? `Connected with ${plan.value.descriptorIds.length} standard pieces.`
              : `Used ${plan.value.descriptorIds.length} standard pieces, then a constrained final alignment curve.`;
          this.showProjectPreview({
            kind: 'finish',
            presetId: null,
            label: 'Autocomplete to Station',
            command: 'Autocomplete to Station',
            acceptLabel: 'Build Autocomplete',
            project: plan.value.project,
            message: completed
              ? routeSummary
              : `${routeSummary} Ride Check still reports ${validation.metrics.outcome}; fix the highlighted physics issue after connecting.`,
            severity: completed ? 'clear' : 'warning',
            canAccept: true,
            inline: false,
            issues: completed
              ? []
              : [
                  {
                    code: 'existing_ride_issue',
                    severity: 'warning',
                    message:
                      validation.issues[0]?.message ??
                      'The exact connection fits, but the ride still needs work.',
                  },
                ],
            affectedNodeIds: [],
            affectedSectionIds: addedSectionIds,
            minPlotMarginM: null,
            generatedLengthM: null,
            addedNodeCount: plan.value.project.track.nodes.length - this.project.track.nodes.length,
          });
        })
        .catch(() => {
          if (this.disposed) return;
          this.finishChecking = false;
          this.showOperationFailure('The autocomplete route could not complete its physics check.');
        });
      return;
    }
    const result = assessFinishToStation(this.project);
    if (!result.ok) {
      this.showOperationFailure(result.message);
      return;
    }
    const revision = this.session.snapshot().revision;
    const token = ++this.finishPreviewToken;
    this.finishChecking = true;
    this.status = 'Checking whether the train can complete this finishâ€¦';
    this.emit();
    void validateFullRide(result.project)
      .then((validation) => {
        if (token !== this.finishPreviewToken || this.disposed) return;
        if (revision !== this.session.snapshot().revision) {
          this.finishChecking = false;
          this.emit();
          return;
        }
        this.finishChecking = false;
        if (validation.metrics.outcome !== 'completed') {
          const failure = validation.issues.find(
            (issue) =>
              issue.code === 'ride_stall' ||
              issue.code === 'ride_arrival_failed' ||
              issue.code === 'ride_timed_out',
          );
          const addedSections = new Set(result.addedSectionIds ?? []);
          const failureIsOnExistingTrack =
            result.mode === 'modular' &&
            Boolean(failure?.sectionId) &&
            !addedSections.has(failure!.sectionId!);
          if (failureIsOnExistingTrack) {
            this.showFinishPreview(
              result,
              `Connection fits. The existing track still needs work near ${failure!.sectionId}; the train ${validation.metrics.outcome === 'stalled' ? 'stalls there' : 'cannot finish there'}.`,
            );
            return;
          }
          this.showOperationFailure(
            validation.metrics.outcome === 'stalled'
              ? 'The proposed connection would stall the train. Adjust the approach speed, then try again.'
              : 'The proposed connection would not return the train safely to the station. Adjust the approach and try again.',
          );
          return;
        }
        this.showFinishPreview(result);
      })
      .catch(() => {
        if (token !== this.finishPreviewToken || this.disposed) return;
        this.finishChecking = false;
        this.showOperationFailure(
          'The finish could not pass its test run. Adjust the approach and try again.',
        );
      });
  }

  private showFinishPreview(
    result: Extract<ReturnType<typeof assessFinishToStation>, { ok: true }>,
    existingRideIssue?: string,
  ): void {
    const beforeLength = compiledTrackLength(this.project);
    const afterLength = compiledTrackLength(result.project);
    const baseNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
    const baseSectionIds = new Set(this.project.track.sections.map((section) => section.id));
    const guidanceBefore = measureFinishToStation(this.project);
    this.showProjectPreview({
      kind: 'finish',
      presetId: null,
      label: 'Autocomplete to Station',
      command: 'Autocomplete to Station',
      acceptLabel: 'Connect Track',
      project: result.project,
      message: result.message,
      severity: 'warning',
      canAccept: true,
      inline: false,
      issues: [
        {
          code: `finish_${result.mode}`,
          severity: 'warning',
          message:
            existingRideIssue ??
            (result.mode === 'modular'
              ? 'The highlighted route uses standard modular track pieces.'
              : result.mode === 'aligned'
                ? 'The final handle will align with the station approach.'
                : 'The highlighted finish becomes ordinary editable track after acceptance.'),
        },
      ],
      affectedNodeIds: result.project.track.nodes
        .filter((node) => !baseNodeIds.has(node.id))
        .map((node) => node.id),
      affectedSectionIds: result.project.track.sections
        .filter((section) => !baseSectionIds.has(section.id))
        .map((section) => section.id),
      minPlotMarginM: null,
      finishImpact: {
        distanceBeforeM: guidanceBefore.distanceM,
        distanceAfterM: 0,
        headingBeforeDegrees: guidanceBefore.turnDegrees,
        headingAfterDegrees: 0,
        finishEligibleBefore:
          guidanceBefore.directJoinEligible || guidanceBefore.generatedFinishEligible,
        finishEligibleAfter: true,
      },
      generatedLengthM:
        beforeLength !== null && afterLength !== null
          ? Math.max(0, afterLength - beforeLength)
          : null,
      addedNodeCount: result.project.track.nodes.length - this.project.track.nodes.length,
    });
  }

  focusBuildEnd(): void {
    if (this.project.track.closed) return;
    const end = this.project.track.nodes.at(-1);
    if (!end) return;
    const nextTarget = new THREE.Vector3(end.position.x, end.position.y, end.position.z);
    const offset = this.scene.camera.position.clone().sub(this.controls.target);
    this.controls.target.copy(nextTarget);
    this.scene.camera.position.copy(nextTarget).add(offset);
    this.scene.camera.lookAt(nextTarget);
    this.controls.update();
    this.status = 'Track exit framed.';
    this.emit();
  }

  setTheme(theme: EnvironmentThemeId): void {
    if (this.project.appearance.environmentThemeId === theme) return;
    const next = cloneProject(this.project);
    next.appearance.environmentThemeId = theme;
    next.metadata.updatedAt = new Date().toISOString();
    this.session.execute('Change environment', next);
    this.carryValidationForward();
    this.scene.setTheme(theme);
    this.status = `${theme === 'park_model' ? 'Park Model' : theme === 'toyworks' ? 'Toyworks' : 'Afterglow'} theme applied.`;
    this.scheduleAutosave();
    if (this.tutorialStep === 3 && !this.project.metadata.tags.includes('started_blank')) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.status += ' First Coaster challenge complete.';
    }
    this.emit();
  }

  setAppearanceColor(slot: 'track' | 'support' | 'train', value: string): void {
    if (!/^#[0-9a-fA-F]{6}$/.test(value)) return;
    const next = cloneProject(this.project);
    if (slot === 'track') next.appearance.trackColor = value;
    if (slot === 'support') next.appearance.supportColor = value;
    if (slot === 'train') next.appearance.trainColor = value;
    next.metadata.updatedAt = new Date().toISOString();
    this.session.execute(
      `Change ${slot} color`,
      next,
      this.session.selection,
      `appearance:${slot}`,
    );
    this.carryValidationForward();
    this.applyAppearance();
    this.scheduleAutosave();
    if (this.tutorialStep === 3 && !this.project.metadata.tags.includes('started_blank')) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.status = `${slot[0]!.toUpperCase()}${slot.slice(1)} color applied. First Coaster challenge complete.`;
    }
    this.emit();
  }

  setLayerVisible(layer: 'supports' | 'scenery', visible: boolean): void {
    if (layer === 'supports') {
      this.supportsVisible = visible;
      this.trackVisual?.root.traverse((object) => {
        if (object.name === 'track-supports') object.visible = visible;
      });
    } else {
      this.sceneryVisible = visible;
      this.authoredSceneryRoot.visible = visible;
    }
    this.emit();
  }

  addScenery(assetId: 'tree' | 'hedge' | 'lamp' | 'rock'): void {
    if (this.project.scenery.length >= 128)
      return this.showOperationFailure('This scene already has the maximum 128 scenery pieces.');
    const next = cloneProject(this.project);
    const index = next.scenery.length;
    const id = newStableId('scenery');
    next.scenery.push({
      id,
      assetId,
      position: { x: -36 + (index % 8) * 10, y: 0.5, z: 28 - Math.floor(index / 8) * 8 },
      rotationDegrees: { x: 0, y: (index * 37) % 360, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      colorVariant: null,
    });
    next.metadata.updatedAt = new Date().toISOString();
    const selection: EditorSelectionState = {
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [id],
      primarySceneryId: id,
    };
    this.executeProjectOperation(
      `Add ${assetId}`,
      next,
      `${assetId[0]!.toUpperCase()}${assetId.slice(1)} added.`,
      undefined,
      selection,
    );
  }

  selectScenery(index: number): void {
    if (this.mode !== 'editor') return;
    const id = this.project.scenery[index]?.id;
    if (!id) return;
    this.session.setSelection({
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [id],
      primarySceneryId: id,
    });
    this.refreshSelectionVisuals();
    this.emit();
  }

  nudgeSelectedScenery(dx: number, dz: number): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    const target = next.scenery.find((candidate) => candidate.id === id)!;
    target.position.x = THREE.MathUtils.clamp(target.position.x + dx, -62, 62);
    target.position.z = THREE.MathUtils.clamp(target.position.z + dz, -42, 42);
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation('Move scenery', next, 'Scenery moved.', `scenery:${id}:position`);
  }

  rotateSelectedScenery(degrees: number): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    const target = next.scenery.find((candidate) => candidate.id === id)!;
    target.rotationDegrees.y = (((target.rotationDegrees.y + degrees) % 360) + 360) % 360;
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Rotate scenery',
      next,
      'Scenery rotated.',
      `scenery:${id}:rotation`,
    );
  }

  duplicateSelectedScenery(): void {
    const id = this.session.selection.primarySceneryId;
    const item = this.project.scenery.find((candidate) => candidate.id === id);
    if (!item) return this.showOperationFailure('Select a scenery piece first.');
    if (this.project.scenery.length >= 128)
      return this.showOperationFailure('This scene already has the maximum 128 scenery pieces.');
    const next = cloneProject(this.project);
    const duplicate = structuredClone(item);
    duplicate.id = newStableId('scenery');
    duplicate.position.x = THREE.MathUtils.clamp(duplicate.position.x + 3, -62, 62);
    duplicate.position.z = THREE.MathUtils.clamp(duplicate.position.z + 3, -42, 42);
    next.scenery.push(duplicate);
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation('Duplicate scenery', next, 'Scenery duplicated.', undefined, {
      nodeIds: [],
      primaryNodeId: null,
      sectionId: null,
      sceneryIds: [duplicate.id],
      primarySceneryId: duplicate.id,
    });
  }

  deleteSelectedScenery(): void {
    const selected = new Set(this.session.selection.sceneryIds);
    if (!selected.size) return this.showOperationFailure('Select a scenery piece first.');
    const next = cloneProject(this.project);
    next.scenery = next.scenery.filter((item) => !selected.has(item.id));
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Delete scenery',
      next,
      'Scenery deleted.',
      undefined,
      sectionSelection(null),
    );
  }

  resetScene(): void {
    const next = cloneProject(this.project);
    next.scenery = [];
    next.appearance = {
      ...next.appearance,
      environmentThemeId: 'park_model',
      trackColor: '#2457b2',
      supportColor: '#343a40',
      trainColor: '#d94a38',
    };
    next.metadata.updatedAt = new Date().toISOString();
    this.executeProjectOperation(
      'Reset scene',
      next,
      'Scene reset. Track work kept.',
      undefined,
      sectionSelection(null),
    );
  }

  frameHeroPreview(): void {
    const bounds = new THREE.Box3();
    if (this.trackVisual) bounds.expandByObject(this.trackVisual.root, true);
    bounds.expandByObject(this.authoredSceneryRoot, true);
    if (bounds.isEmpty()) return;
    const target = bounds.getCenter(new THREE.Vector3());
    const sphere = bounds.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(18, sphere.radius);
    const verticalFov = THREE.MathUtils.degToRad(this.scene.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * (16 / 9));
    const fitDistance = radius / Math.sin(Math.min(verticalFov, horizontalFov) / 2);
    const safeDistance = fitDistance * (1 + this.project.heroCamera.safeMargin);
    const direction = new THREE.Vector3(1, 0.62, 1).normalize();
    this.controls.target.copy(target);
    this.scene.camera.position.copy(target).addScaledVector(direction, safeDistance);
    this.scene.camera.lookAt(target);
    this.controls.update();
    this.saveCurrentHeroCamera();
    this.status = 'Hero preview framed. Orbit or zoom, then save the current view if needed.';
    this.emit();
  }

  saveCurrentHeroCamera(): void {
    const bounds = new THREE.Box3();
    if (this.trackVisual) bounds.expandByObject(this.trackVisual.root, true);
    bounds.expandByObject(this.authoredSceneryRoot, true);
    if (bounds.isEmpty()) return;
    const center = bounds.getCenter(new THREE.Vector3());
    const radius = Math.max(18, bounds.getBoundingSphere(new THREE.Sphere()).radius);
    const target = this.controls.target.clone();
    const targetOffset = target.distanceTo(center);
    const distance = this.scene.camera.position.distanceTo(target);
    if (targetOffset > radius * 0.65 || distance < radius * 0.8 || distance > radius * 8) {
      this.status = 'That view is outside the safe hero framing range.';
      this.emit();
      return;
    }
    const next = cloneProject(this.project);
    next.heroCamera.position = { ...this.scene.camera.position };
    next.heroCamera.target = { ...target };
    next.heroCamera.verticalFovDegrees = this.scene.camera.fov;
    this.session.execute('Frame hero preview', next);
    this.carryValidationForward();
    this.scheduleAutosave();
    this.status = 'Current view saved as the Preview Image.';
    this.emit();
  }

  focusIssue(issueId: string): void {
    const fastIssue = this.validationProfile.issues.find((candidate) => candidate.id === issueId);
    const fullIssue = this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    const issue = fastIssue ?? fullIssue;
    if (!issue) return;
    let position: THREE.Vector3 | null = null;
    if (fastIssue?.nodeId) {
      const index = this.project.track.nodes.findIndex((node) => node.id === fastIssue.nodeId);
      const node = this.project.track.nodes[index];
      if (node) {
        this.selectNode(index);
        position = new THREE.Vector3(node.position.x, node.position.y, node.position.z);
      }
    } else if (issue.sectionId) {
      const index = this.project.track.sections.findIndex(
        (section) => section.id === issue.sectionId,
      );
      if (index >= 0) this.selectSection(index);
      const span = this.compiledTrack?.spans[index];
      if (span && this.sampler) {
        const samplePosition = this.sampler.sampleByDistance(
          (span.startDistance + span.endDistance) * 0.5,
        ).position;
        position = new THREE.Vector3(samplePosition.x, samplePosition.y, samplePosition.z);
      }
    } else if (issue.distanceM !== undefined && this.sampler) {
      const samplePosition = this.sampler.sampleByDistance(issue.distanceM).position;
      position = new THREE.Vector3(samplePosition.x, samplePosition.y, samplePosition.z);
    }
    if (position) {
      this.controls.target.copy(position);
      this.scene.camera.position.copy(position).add(new THREE.Vector3(24, 18, 24));
      this.scene.camera.lookAt(position);
      this.controls.update();
    }
    const measured = fastIssue?.metric?.value ?? fullIssue?.measured;
    const threshold = fastIssue?.metric?.threshold ?? fullIssue?.threshold;
    const unit = fastIssue?.metric?.unit ?? fullIssue?.unit ?? '';
    const measurement =
      measured !== undefined && threshold !== undefined
        ? ` Measured ${measured.toFixed(2)}${unit ? ` ${unit}` : ''}; review line ${threshold.toFixed(2)}${unit ? ` ${unit}` : ''}.`
        : '';
    this.status = `${issue.message}${measurement}`;
    this.emit();
  }

  editIssue(issueId: string): void {
    const issue =
      this.validationProfile.issues.find((candidate) => candidate.id === issueId) ??
      this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    if (!issue) return;
    this.focusIssue(issueId);
    this.setTool('build');
    const fullIssue = this.fullValidation?.issues.find((candidate) => candidate.id === issueId);
    const code = fullIssue?.code ?? issue.code;
    const sectionIndex = issue.sectionId
      ? this.project.track.sections.findIndex((section) => section.id === issue.sectionId)
      : -1;
    const shapeCorrection = ['positive_vertical_g', 'negative_vertical_g', 'lateral_g'].includes(
      code,
    );
    if (shapeCorrection && sectionIndex >= 0) {
      const nodeCount = this.project.track.nodes.length;
      const indices = [sectionIndex - 1, sectionIndex, sectionIndex + 1, sectionIndex + 2]
        .map((index) => (this.project.track.closed ? (index + nodeCount) % nodeCount : index))
        .filter(
          (index, position, values) =>
            index >= 0 && index < nodeCount && values.indexOf(index) === position,
        );
      const nodeIds = indices.map((index) => this.project.track.nodes[index]!.id);
      this.session.setEditorMode('advanced');
      this.session.setSelectionTarget('shape');
      this.session.setSelection(nodeSelection(nodeIds, nodeIds[Math.min(2, nodeIds.length - 1)]));
      this.focusedLateralIssue =
        code === 'lateral_g'
          ? {
              revision: this.session.snapshot().revision,
              nodeIdsKey: [...nodeIds].sort().join('|'),
            }
          : null;
      if (code === 'lateral_g') this.session.setOverlay('lateral_g');
      else this.session.setOverlay('vertical_g');
      this.refreshSelectionVisuals();
      this.status =
        code === 'lateral_g'
          ? `Bank or smooth the highlighted curve. ${issue.message}`
          : `Smooth or reshape the highlighted curve. ${issue.message}`;
    } else {
      let targetSectionIndex = sectionIndex;
      if (code === 'station_arrival_overspeed' || code === 'ride_arrival_failed') {
        let brakeIndex = -1;
        for (let index = this.project.track.sections.length - 1; index >= 0; index -= 1)
          if (this.project.track.sections[index]!.behavior.kind === 'brake') {
            brakeIndex = index;
            break;
          }
        if (brakeIndex >= 0) targetSectionIndex = brakeIndex;
      }
      this.session.setSelectionTarget(targetSectionIndex >= 0 ? 'piece' : 'shape');
      if (targetSectionIndex >= 0)
        this.session.setSelection(
          sectionSelection(this.project.track.sections[targetSectionIndex]!.id),
        );
      this.refreshSelectionVisuals();
      this.status =
        code === 'station_arrival_overspeed' || code === 'ride_arrival_failed'
          ? `Strengthen the highlighted brake before the station. ${issue.message}`
          : targetSectionIndex >= 0
            ? `Tune the highlighted track piece. ${issue.message}`
            : `Reshape the highlighted track point. ${issue.message}`;
    }
    this.emit();
  }

  setTutorialVisible(visible: boolean): void {
    this.tutorialVisible = visible;
    void this.repository.setPreference('tutorial', { visible, step: this.tutorialStep });
    this.emit();
  }

  advanceTutorial(): void {
    if (this.tutorialStep === 3) {
      this.tutorialVisible = false;
      void this.repository.setPreference('tutorial', { visible: false, step: 3 });
      this.emit();
      return;
    }
    this.status =
      this.tutorialStep === 1
        ? 'Click the rails, then try a Hill, Drop, or Turn.'
        : this.tutorialOperatingPieceEdited
          ? 'Take a Test Ride to complete this step.'
          : 'Tune a lift, launch, or brake before taking a Test Ride.';
    this.emit();
  }

  private markTutorialOperatingPieceEdit(): void {
    if (this.tutorialStep !== 2 || this.project.metadata.tags.includes('started_blank')) return;
    this.tutorialOperatingPieceEdited = true;
    this.status = 'Track piece tuned. Take a Test Ride to continue.';
    this.emit();
  }

  setPublishingDetails(details: PublishingDetails): void {
    const description = details.description.trim().slice(0, 500);
    const tags = [...new Set(details.tags.map((tag) => tag.trim().toLowerCase()).filter(Boolean))]
      .slice(0, 10)
      .map((tag) => tag.slice(0, 32));
    const next = cloneProject(this.project);
    next.metadata.description = description;
    next.metadata.tags = tags;
    next.permissions.allowReadOnlyShare = details.allowReadOnlyShare;
    next.permissions.allowRemix = details.allowRemix;
    next.metadata.updatedAt = new Date().toISOString();
    this.session.execute('Update publishing details', next);
    this.carryValidationForward();
    this.scheduleAutosave();
    this.status = 'Details applied. Save Draft to keep them.';
    this.emit();
  }

  setMediaShareContext(context: MediaShareContext): void {
    this.mediaShareContext = {
      creatorDisplayName: context.creatorDisplayName || this.project.metadata.creator.displayName,
      publicCoasterId: context.publicCoasterId,
      publicUrl: context.publicUrl,
    };
    if (this.mediaTimeline)
      this.mediaTimeline = {
        ...this.mediaTimeline,
        attribution: {
          ...this.mediaTimeline.attribution,
          ...this.mediaShareContext,
          coasterTitle: this.project.metadata.title,
        },
      };
    this.emit();
  }

  async openMediaStudio(): Promise<void> {
    if (this.mediaRequiresNewRide) {
      this.mediaMessage = 'Ride files deleted. Complete another ride to create new ones.';
      this.emit();
      return;
    }
    this.mediaOpen = true;
    this.mediaShareOutcome = 'idle';
    this.mediaAnalytics.emit({
      name: 'media_studio_opened',
      ...(this.mediaShareContext.publicCoasterId
        ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
        : {}),
    });
    if (!this.mediaTimeline && this.lastRideSummary?.outcome === 'completed')
      await this.prepareMediaTimeline(this.lastRideSummary);
    if (!this.mediaTimeline || !this.mediaEdits) {
      this.mediaStatus = 'error';
      this.mediaMessage = 'Complete the ride before creating media.';
      this.emit();
      return;
    }
    if (this.mediaCapabilities.staticOnlyReason) {
      this.mediaStatus = 'static_only';
      this.mediaMessage = this.mediaCapabilities.staticOnlyReason;
    } else {
      this.mediaStatus = 'preparing';
      this.mediaMessage = 'Preparing your Photo choices…';
    }
    this.emit();
    if (
      this.currentMediaArtifact('photo') &&
      this.currentMediaArtifact('share_card') &&
      this.mediaPosterThumbnailUrls.size > 0
    ) {
      const retainedVideos = [...this.mediaArtifacts.keys()].filter((key) =>
        key.startsWith('highlight_video:'),
      ).length;
      const retainedLoops = [...this.mediaArtifacts.keys()].filter((key) =>
        key.startsWith('animated_loop:'),
      ).length;
      const retainedCount = retainedVideos + retainedLoops;
      this.mediaStatus = retainedCount
        ? 'ready'
        : this.mediaCapabilities.preferredRecorder
          ? 'idle'
          : 'static_only';
      this.mediaMessage = retainedCount
        ? `${retainedCount} generated ride file${retainedCount === 1 ? '' : 's'} remained ready in this coaster session.`
        : this.mediaCapabilities.preferredRecorder
          ? 'Your Photo is ready. Generate a video when you want it.'
          : (this.mediaCapabilities.staticOnlyReason ?? 'Your Photo is ready.');
      this.emit();
      return;
    }
    try {
      const renderer = await import('../media/render.ts');
      const source = this.mediaRenderSource();
      if (!source) throw new Error('The coaster scene is not ready for media.');
      this.clearPosterThumbnails();
      const thumbnails = await renderer.renderPosterThumbnails(
        source,
        source.edit.posterCandidates,
      );
      for (const thumbnail of thumbnails)
        this.mediaPosterThumbnailUrls.set(
          thumbnail.candidateId,
          URL.createObjectURL(thumbnail.blob),
        );
      const [photo, card] = await Promise.all([
        renderer.renderRidePhoto(source),
        renderer.renderShareCard(source),
      ]);
      this.setMediaArtifact(photo);
      this.setMediaArtifact(card);
      this.mediaAnalytics.emit({
        name: 'photo_generated',
        artifactType: 'photo',
        ...(this.mediaShareContext.publicCoasterId
          ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
          : {}),
      });
      this.mediaStatus = this.mediaCapabilities.preferredRecorder ? 'idle' : 'static_only';
      this.mediaMessage = this.mediaCapabilities.preferredRecorder
        ? 'Your Photo is ready. Generate a video when you want it.'
        : (this.mediaCapabilities.staticOnlyReason ?? 'Your Photo is ready.');
    } catch (cause) {
      this.mediaStatus = 'error';
      this.mediaMessage =
        cause instanceof Error ? cause.message : 'The Photo could not be prepared.';
    }
    this.emit();
  }

  closeMediaStudio(): void {
    this.cancelMediaExport();
    this.mediaOpen = false;
    if (this.mediaStatus === 'cancelled') this.mediaStatus = 'idle';
    this.emit();
  }

  selectHighlightAlternative(value: 0 | 1 | 2): void {
    if (!this.mediaEdits?.[value]) return;
    this.mediaSelectedAlternative = value;
    const retained = [
      this.currentMediaArtifact('highlight_video') ? 'video' : null,
      this.currentMediaArtifact('animated_loop') ? 'loop' : null,
    ].filter((item): item is string => item !== null);
    this.mediaStatus = retained.length
      ? 'ready'
      : this.mediaCapabilities.preferredRecorder
        ? 'idle'
        : 'static_only';
    this.mediaMessage = retained.length
      ? `${this.mediaEdits[value].label} selected. Its ${retained.join(' and ')} ${retained.length === 1 ? 'is' : 'are'} still ready.`
      : `${this.mediaEdits[value].label} selected. Your Photo stays the same; generate this edit when you want it.`;
    this.mediaAnalytics.emit({
      name: 'highlight_selected',
      ...(this.mediaShareContext.publicCoasterId
        ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
        : {}),
    });
    this.emit();
  }

  async selectPosterCandidate(candidateId: string): Promise<void> {
    const edit = this.mediaEdits?.[this.mediaSelectedAlternative];
    const candidate = edit?.posterCandidates.find((item) => item.id === candidateId);
    if (!candidate || candidate.id === this.mediaSelectedPosterId) return;
    this.mediaSelectedPosterId = candidate.id;
    this.mediaPosterGenerationToken += 1;
    const token = this.mediaPosterGenerationToken;
    this.clearMediaArtifact('photo');
    this.mediaStatus = 'preparing';
    this.mediaMessage = `Creating ${candidate.label} Photo…`;
    this.emit();
    try {
      const renderer = await import('../media/render.ts');
      const source = this.mediaRenderSource();
      if (!source) throw new Error('The selected Photo is unavailable.');
      const poster = await renderer.renderRidePhoto(source);
      if (token !== this.mediaPosterGenerationToken) return;
      this.setMediaArtifact(poster);
      this.mediaStatus = this.mediaCapabilities.preferredRecorder ? 'idle' : 'static_only';
      this.mediaMessage = `${candidate.label} Photo is ready.`;
    } catch (cause) {
      if (token !== this.mediaPosterGenerationToken) return;
      this.mediaStatus = 'error';
      this.mediaMessage =
        cause instanceof Error ? cause.message : 'The Photo could not be created.';
    }
    this.emit();
  }

  setMediaAudioEnabled(value: boolean): void {
    this.mediaIncludeAudio = value;
    const retained = this.currentMediaArtifact('highlight_video');
    this.mediaStatus = retained
      ? 'ready'
      : this.mediaCapabilities.preferredRecorder
        ? 'idle'
        : 'static_only';
    this.mediaMessage = retained
      ? `The ${value ? 'sound-on' : 'silent'} version is already ready.`
      : `Sound ${value ? 'enabled' : 'disabled'}. Generate this version when you want it.`;
    this.emit();
  }

  async generateHighlightVideo(): Promise<void> {
    await this.generateMediaVideo('highlight_video');
  }

  async generateAnimatedLoop(): Promise<void> {
    await this.generateMediaVideo('animated_loop');
  }

  cancelMediaExport(): void {
    if (!this.mediaExportAbort) return;
    this.mediaExportAbort.abort();
    this.mediaExportAbort = null;
    this.mediaStatus = 'cancelled';
    this.mediaProgress = 0;
    this.mediaMessage = 'Video generation cancelled. Your Photo is still ready.';
    this.emit();
  }

  async copyMediaImage(kind: MediaArtifactKind): Promise<boolean> {
    if (kind !== 'photo' && kind !== 'share_card') return false;
    const artifact = this.currentMediaArtifact(kind);
    if (!artifact) return false;
    if (!imageClipboardAvailable()) {
      this.mediaMessage = 'This browser cannot copy images directly. Use Download instead.';
      this.mediaShareOutcome = 'failed';
      this.emit();
      return false;
    }
    this.mediaShareOutcome = 'idle';
    try {
      const png = await imageArtifactAsPng(artifact);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
      this.mediaAnalytics.emit({
        name: 'media_image_copied',
        artifactType: kind,
        ...(this.mediaShareContext.publicCoasterId
          ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
          : {}),
      });
      this.mediaShareOutcome = 'completed';
      this.mediaMessage = 'Image copied. Paste it into your social post or message.';
      this.emit();
      return true;
    } catch {
      this.mediaShareOutcome = 'failed';
      this.mediaAnalytics.emit({
        name: 'media_image_copy_failed',
        artifactType: kind,
        ...(this.mediaShareContext.publicCoasterId
          ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
          : {}),
      });
      this.mediaMessage = 'The image could not be copied. Use Download instead.';
      this.emit();
      return false;
    }
  }

  downloadMedia(kind: MediaArtifactKind): boolean {
    const artifact = this.currentMediaArtifact(kind);
    if (!artifact) return false;
    downloadArtifact(artifact);
    this.mediaAnalytics.emit({
      name: 'media_downloaded',
      artifactType: kind,
      ...(this.mediaShareContext.publicCoasterId
        ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
        : {}),
    });
    this.mediaMessage = 'Download started.';
    this.emit();
    return true;
  }

  async copyMediaRideLink(): Promise<boolean> {
    if (!this.mediaShareContext.publicUrl) {
      this.mediaMessage = 'A public ride link becomes available after publishing.';
      this.emit();
      return false;
    }
    try {
      await navigator.clipboard.writeText(this.mediaShareContext.publicUrl);
      this.mediaAnalytics.emit({
        name: 'ride_link_copied',
        ...(this.mediaShareContext.publicCoasterId
          ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
          : {}),
      });
      this.mediaMessage = 'Public ride link copied.';
      this.emit();
      return true;
    } catch {
      this.mediaMessage = 'The ride link could not be copied.';
      this.emit();
      return false;
    }
  }

  async deleteRideMedia(): Promise<void> {
    this.cancelMediaExport();
    await this.discardReactionRecording();
    const timeline = this.mediaTimeline;
    this.mediaGenerationToken += 1;
    this.mediaRestoreToken += 1;
    if (timeline) {
      try {
        await this.queueMediaPersistence((repository) =>
          repository.delete(timeline.projectId, timeline.revisionId),
        );
      } catch {
        this.mediaStatus = 'error';
        this.mediaMessage = 'Ride files could not be deleted from this browser. Try again.';
        this.emit();
        return;
      }
    }
    this.clearAllMediaArtifacts();
    this.clearPosterThumbnails();
    this.mediaTimeline = null;
    this.mediaTimelineSource = null;
    this.mediaEdits = null;
    this.mediaSelectedPosterId = null;
    this.lastRideSummary = null;
    this.replayElapsedSeconds = 0;
    this.replayPaused = false;
    this.mediaRequiresNewRide = true;
    this.mediaOpen = false;
    this.mediaStatus = 'idle';
    this.mediaProgress = 0;
    this.mediaMessage =
      'Ride files deleted. Complete another ride to create new ones. The coaster was not changed.';
    this.emit();
  }

  exportProject(): Blob {
    return new Blob([serializeCoasterProject(this.project)], { type: 'application/json' });
  }

  getProjectSnapshot(): CoasterProject {
    return cloneProject(this.project);
  }

  async loadProject(projectValue: CoasterProject, intent: ProjectLoadIntent): Promise<void> {
    const loaded = loadCoasterProject(projectValue);
    if (!loaded.ok) return this.showOperationFailure(loaded.error.message);
    const topology = validateTrackTopology(loaded.project);
    if (topology.status === 'invalid')
      return this.showOperationFailure(
        topology.issues[0]?.message ?? 'That file does not contain one ordered track.',
      );
    if (loaded.project.track.nodes.length >= 4) {
      try {
        compileProjectTrackResult(loaded.project, new TrackCompiler());
      } catch {
        return this.showOperationFailure('That file could not produce one continuous track.');
      }
    }
    const readOnly = intent === 'readonly_public' || intent === 'readonly_private';
    this.persistenceMode = readOnly ? 'readonly' : 'editable';
    this.recoveredDraft = null;
    this.replaceProject(loaded.project);
    if (readOnly) {
      this.status = 'Coaster ready to ride.';
      this.session.setSaveState('saved');
      this.emit();
      return;
    }
    this.status =
      intent === 'editable_remix'
        ? `Saving ${loaded.project.metadata.title} as an independent remix…`
        : intent === 'editable_cloud'
          ? 'Saving this account draft on this device…'
          : loaded.migratedFrom === null
            ? 'Coaster imported.'
            : 'Coaster imported and upgraded.';
    this.session.setSaveState('editing');
    this.status =
      intent === 'editable_remix'
        ? `${loaded.project.metadata.title} is ready as an unsaved remix.`
        : intent === 'editable_cloud'
          ? `${loaded.project.metadata.title} is ready. Save Draft to keep it on this device.`
          : loaded.migratedFrom === null
            ? 'Coaster imported. Save Draft to keep it.'
            : 'Coaster imported and upgraded. Save Draft to keep it.';
    this.emit();
  }

  importProject(json: string): void {
    const loaded = loadCoasterProject(json);
    if (!loaded.ok) return this.showOperationFailure(loaded.error.message);
    void this.loadProject(loaded.project, 'editable_import');
  }

  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    this.debugRoot.visible = visible;
    if (visible && location.hash !== '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}#/debug`);
    if (!visible && location.hash === '#/debug')
      history.replaceState(null, '', `${location.pathname}${location.search}`);
    this.emit();
  }

  async captureEvidence(): Promise<Blob> {
    this.scene.render();
    const blob = await new Promise<Blob | null>((resolve) =>
      this.canvas.toBlob(resolve, 'image/png'),
    );
    if (!blob) throw new Error('The WebGL evidence frame could not be encoded.');
    return blob;
  }

  async captureHeroAssets(): Promise<HeroCaptureBundle> {
    const previousPosition = this.scene.camera.position.clone();
    const previousTarget = this.controls.target.clone();
    const previousAspect = this.scene.camera.aspect;
    const previousFov = this.scene.camera.fov;
    const previousNear = this.scene.camera.near;
    const previousFar = this.scene.camera.far;
    const previousHandleVisibility = this.handleRoot.visible;
    const previousDebugVisibility = this.debugRoot.visible;
    const previousValidationVisibility = this.validationRoot.visible;
    const previousGuideVisibility = this.buildGuideRoot.visible;
    const previousPreviewVisibility = this.presetPreviewVisual?.root.visible;
    const rect = this.canvas.getBoundingClientRect();
    const camera = cloneProject(this.project).heroCamera;
    try {
      this.handleRoot.visible = false;
      this.debugRoot.visible = false;
      this.validationRoot.visible = false;
      this.buildGuideRoot.visible = false;
      if (this.presetPreviewVisual) this.presetPreviewVisual.root.visible = false;
      this.scene.camera.position.set(camera.position.x, camera.position.y, camera.position.z);
      this.controls.target.set(camera.target.x, camera.target.y, camera.target.z);
      this.scene.camera.fov = camera.verticalFovDegrees;
      this.scene.camera.aspect = HERO_MASTER_SIZE.width / HERO_MASTER_SIZE.height;
      this.scene.camera.near = camera.nearMeters;
      this.scene.camera.far = camera.farMeters;
      this.scene.camera.updateProjectionMatrix();
      this.scene.camera.lookAt(this.controls.target);
      this.scene.renderer.setPixelRatio(1);
      this.scene.renderer.setSize(HERO_MASTER_SIZE.width, HERO_MASTER_SIZE.height, false);
      this.scene.render();
      const master =
        (await canvasBlob(this.canvas, 'image/webp', 0.92)) ??
        (await canvasBlob(this.canvas, 'image/png'));
      if (!master) throw new Error('The hero frame could not be encoded.');
      const [hero16x9, openGraph] = await Promise.all([
        renderHeroDerivative(master, HERO_CARD_SIZE.width, HERO_CARD_SIZE.height),
        renderHeroDerivative(master, HERO_OPEN_GRAPH_SIZE.width, HERO_OPEN_GRAPH_SIZE.height),
      ]);
      return { hero16x9, openGraph, camera };
    } finally {
      this.scene.camera.position.copy(previousPosition);
      this.controls.target.copy(previousTarget);
      this.scene.camera.aspect = previousAspect;
      this.scene.camera.fov = previousFov;
      this.scene.camera.near = previousNear;
      this.scene.camera.far = previousFar;
      this.scene.camera.updateProjectionMatrix();
      this.handleRoot.visible = previousHandleVisibility;
      this.debugRoot.visible = previousDebugVisibility;
      this.validationRoot.visible = previousValidationVisibility;
      this.buildGuideRoot.visible = previousGuideVisibility;
      if (this.presetPreviewVisual && previousPreviewVisibility !== undefined)
        this.presetPreviewVisual.root.visible = previousPreviewVisibility;
      this.scene.resize(rect.width, rect.height);
      this.scene.render();
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelMediaExport();
    this.clearAllMediaArtifacts();
    this.clearPosterThumbnails();
    void this.discardReactionSession();
    this.lastRideSummary = null;
    cancelAnimationFrame(this.animationFrame);
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    if (this.editCommitTimer !== null) window.clearTimeout(this.editCommitTimer);
    if (this.fullValidationTimer !== null) window.clearTimeout(this.fullValidationTimer);
    this.routeGuideToken += 1;
    if (this.routeGuideTimer !== null) window.clearTimeout(this.routeGuideTimer);
    this.validationCoordinator.cancel();
    this.fullValidationCoordinator.dispose();
    this.autoBankValidationCoordinator.dispose();
    this.audio.dispose();
    this.resizeObserver.disconnect();
    this.pointerEditor.dispose();
    this.controls.dispose();
    this.clearHandles();
    this.trackVisual?.dispose();
    this.clearPresetPreviewVisual();
    this.trainVisual.dispose();
    disposeMaterialSet(this.coasterMaterials);
    disposeMaterialSet(this.ghostMaterials);
    this.scene.dispose();
    this.listeners.clear();
  }

  private selectedNode(): TrackNode | null {
    return this.project.track.nodes.find((node) => node.id === this.selectedNodeId) ?? null;
  }

  private clearPresetPreviewVisual(): void {
    if (!this.presetPreviewVisual) return;
    this.content.remove(this.presetPreviewVisual.root);
    this.presetPreviewVisual.dispose();
    this.presetPreviewVisual = null;
  }

  private setGhostSeverity(severity: RuntimePreviewState['severity']): void {
    const color = severity === 'blocker' ? 0xf26c6c : severity === 'warning' ? 0xf1bd58 : 0x7ef5e0;
    Object.values(this.ghostMaterials).forEach((material) => {
      if ('color' in material) material.color.setHex(color);
      if ('emissive' in material)
        material.emissive.setHex(
          severity === 'blocker' ? 0x641f2c : severity === 'warning' ? 0x5d3f12 : 0x1f665d,
        );
    });
  }

  private clearBuildGuides(): void {
    this.buildGuideRoot.traverse((child) => {
      if (
        child instanceof THREE.Mesh ||
        child instanceof THREE.Line ||
        child instanceof THREE.LineLoop
      )
        child.geometry.dispose();
      if (!('material' in child)) return;
      const material = child.material as THREE.Material | THREE.Material[];
      (Array.isArray(material) ? material : [material]).forEach((item) => item.dispose());
    });
    this.buildGuideRoot.clear();
  }

  private rebuildBuildGuides(): void {
    this.clearBuildGuides();
    if (this.mode !== 'editor' || this.project.track.closed || !this.project.track.nodes.length)
      return;
    const bounds = projectPlotBounds(this.project);
    const boundaryGeometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(bounds.minX, this.project.plot.groundElevationMeters + 0.18, bounds.minZ),
      new THREE.Vector3(bounds.maxX, this.project.plot.groundElevationMeters + 0.18, bounds.minZ),
      new THREE.Vector3(bounds.maxX, this.project.plot.groundElevationMeters + 0.18, bounds.maxZ),
      new THREE.Vector3(bounds.minX, this.project.plot.groundElevationMeters + 0.18, bounds.maxZ),
    ]);
    const boundary = new THREE.LineLoop(
      boundaryGeometry,
      new THREE.LineBasicMaterial({
        color: this.presetPreview?.severity === 'blocker' ? 0xf26c6c : 0x75d7ce,
        transparent: true,
        opacity: this.presetPreview ? 0.88 : 0.42,
        depthTest: false,
      }),
    );
    boundary.name = 'build-plot-boundary';
    boundary.renderOrder = 80;
    this.buildGuideRoot.add(boundary);

    const first = this.project.track.nodes[0]!;
    const last = this.project.track.nodes.at(-1)!;
    const guide = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(last.position.x, last.position.y + 0.4, last.position.z),
        new THREE.Vector3(first.position.x, first.position.y + 0.4, first.position.z),
      ]),
      new THREE.LineDashedMaterial({
        color: 0xf1bd58,
        dashSize: 1.2,
        gapSize: 0.8,
        transparent: true,
        opacity: 0.72,
        depthTest: false,
      }),
    );
    guide.computeLineDistances();
    guide.name = 'finish-direction-guide';
    guide.renderOrder = 79;
    this.buildGuideRoot.add(guide);

    const endPose = modularEndPose(this.project);
    const stationPose = this.project.editor.pieceGroups[0]?.entryPose ?? null;
    if (endPose && stationPose) {
      const addPoseArrow = (pose: typeof endPose, color: number, name: string) => {
        const forward = poseForward(pose);
        const arrow = new THREE.ArrowHelper(
          new THREE.Vector3(forward.x, 0, forward.z).normalize(),
          new THREE.Vector3(pose.position.x, pose.position.y + 0.8, pose.position.z),
          6,
          color,
          1.4,
          0.8,
        );
        arrow.name = name;
        arrow.traverse((child) => {
          child.renderOrder = 82;
        });
        this.buildGuideRoot.add(arrow);
      };
      addPoseArrow(endPose, 0x73e6cf, 'track-exit-arrow');
      addPoseArrow(stationPose, 0xf5c451, 'station-entry-arrow');
      for (const [pose, name] of [
        [endPose, 'exit-grid'],
        [stationPose, 'station-grid'],
      ] as const) {
        const grid = new THREE.GridHelper(16, 4, 0x58c9e8, 0x2c4c5b);
        grid.name = name;
        grid.position.set(
          pose.position.x,
          this.project.plot.groundElevationMeters + 0.16,
          pose.position.z,
        );
        grid.material.transparent = true;
        grid.material.opacity = 0.38;
        grid.material.depthTest = false;
        grid.renderOrder = 78;
        this.buildGuideRoot.add(grid);
      }

      const routePlan = this.routeGuidePlanForRevision();
      if (routePlan?.labels.length) {
        const addedNodes = routePlan.project.track.nodes.slice(this.project.track.nodes.length);
        const routePoints = [
          new THREE.Vector3(endPose.position.x, endPose.position.y + 0.65, endPose.position.z),
          ...addedNodes.map(
            (node) => new THREE.Vector3(node.position.x, node.position.y + 0.65, node.position.z),
          ),
          new THREE.Vector3(
            stationPose.position.x,
            stationPose.position.y + 0.65,
            stationPose.position.z,
          ),
        ];
        const corridor = new THREE.Line(
          new THREE.BufferGeometry().setFromPoints(routePoints),
          new THREE.LineDashedMaterial({
            color: 0x58c9e8,
            dashSize: 1.6,
            gapSize: 0.55,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
          }),
        );
        corridor.computeLineDistances();
        corridor.name = 'route-guide-corridor';
        corridor.renderOrder = 83;
        this.buildGuideRoot.add(corridor);
      }
    }
  }

  private showProjectPreview(preview: ProjectPreview): void {
    this.clearPresetPreviewVisual();
    this.presetPreview = preview;
    this.setGhostSeverity(preview.severity);
    try {
      const compiled = compileProjectTrackResult(preview.project, new TrackCompiler());
      this.presetPreviewVisual = new TrackVisual(
        compiled,
        this.ghostMaterials,
        createTrackSupportContext(preview.project),
      );
      this.presetPreviewVisual.root.name = 'project-ghost-preview';
      this.presetPreviewVisual.root.traverse((object) => {
        object.renderOrder = 70;
      });
      if (preview.kind === 'piece' && preview.affectedSectionIds.length) {
        const affected = new Set(preview.affectedSectionIds);
        this.presetPreviewVisual.root.children.forEach((child) => {
          child.visible = affected.has(String(child.userData.sectionId));
        });
      }
      this.content.add(this.presetPreviewVisual.root);
    } catch {
      this.presetPreview = null;
      return this.showOperationFailure('That preview could not produce one continuous track.');
    }
    this.rebuildBuildGuides();
    this.status = preview.canAccept
      ? `${preview.label} preview ready.`
      : (preview.issues.find((issue) => issue.severity === 'blocker')?.message ??
        `${preview.label} cannot be placed here.`);
    this.emit();
  }

  private selectNodeById(nodeId: string | null): void {
    this.session.setSelection(nodeSelection(nodeId ? [nodeId] : []));
    this.refreshSelectionVisuals();
    this.emit();
  }

  private refreshSelectionVisuals(): void {
    const selectedIds = new Set(this.session.selection.nodeIds);
    this.handleRoot.children.forEach((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const selected = selectedIds.has(String(child.userData.nodeId));
      const buildEnd = child.userData.isBuildEnd === true;
      const material = child.material as THREE.MeshStandardMaterial;
      material.color.setHex(selected ? 0xc8fff1 : buildEnd ? 0x7ef5e0 : 0x5bd5ee);
      material.emissive.setHex(selected || buildEnd ? 0x2b8f82 : 0x123b4a);
      child.scale.setScalar(buildEnd ? (selected ? 1.55 : 1.35) : selected ? 1.25 : 1);
    });
    const selectedSceneryIds = new Set(this.session.selection.sceneryIds);
    this.authoredSceneryRoot.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      const selected = selectedSceneryIds.has(String(object.userData.sceneryId));
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        if (material instanceof THREE.MeshStandardMaterial)
          material.emissive.setHex(selected ? 0x214f45 : 0x000000);
      });
    });
    const editor = this.session.snapshot();
    const showPieceEmphasis =
      editor.activeTool === 'build' &&
      (editor.editorMode === 'advanced' || editor.selectionTarget === 'piece');
    this.trackVisual?.setSectionEmphasis(
      showPieceEmphasis ? editor.selection.sectionId : null,
      showPieceEmphasis && this.hoveredEditorTarget?.kind === 'section'
        ? this.hoveredEditorTarget.id
        : null,
    );
  }

  private cancelEdit(): void {
    if (this.editCommitTimer !== null) {
      window.clearTimeout(this.editCommitTimer);
      this.editCommitTimer = null;
    }
    if (this.gestureOpen) {
      this.session.cancelGesture();
      this.gestureOpen = false;
      this.rebuildProjectVisual();
    }
  }

  private async mediaStore(): Promise<ReplayArtifactRepository> {
    if (this.mediaRepository) return this.mediaRepository;
    const { createReplayArtifactRepository } = await import('../media/repository.ts');
    const repository = createReplayArtifactRepository(this.mediaPersistenceScope);
    await repository.initialize();
    this.mediaRepository = repository;
    return repository;
  }

  private async ensureReactionController(): Promise<ReactionCaptureController> {
    if (this.reactionController) return this.reactionController;
    const { ReactionCaptureController } = await import('../reaction/capture.ts');
    this.reactionController = new ReactionCaptureController({
      synthetic: this.reactionSynthetic,
      onInterrupted: (reason) => {
        this.reactionPreviewStream = null;
        this.reactionPhase = 'interrupted';
        this.reactionInterruptionReason = reason;
        this.reactionMessage = `${reason} The coaster remains available without Reaction Cam.`;
        this.emitReactionAnalytics({
          name: 'reaction_recording_interrupted',
          failureCode: 'source_interrupted',
        });
        this.emit();
      },
    });
    return this.reactionController;
  }

  private reactionAvailableForExport(): boolean {
    return Boolean(
      this.reactionRecording &&
      !this.reactionRecording.interrupted &&
      this.reactionRecording.rideOutcome === 'completed' &&
      this.reactionRecording.projectId === this.project.projectId &&
      this.reactionRecording.revisionId === this.project.revisionId &&
      this.reactionRecording.durationSeconds >= 3 &&
      this.mediaTimeline,
    );
  }

  private reactionRideSpeedLocked(): boolean {
    return (
      this.reactionPhase === 'countdown' ||
      this.reactionPhase === 'recording' ||
      this.reactionPhase === 'finalizing'
    );
  }

  private clearReactionRecording(): void {
    this.reactionCaptureToken += 1;
    if (this.reactionRawUrl) URL.revokeObjectURL(this.reactionRawUrl);
    this.reactionRawUrl = null;
    this.reactionRecording = null;
    this.reactionIncludeInExport = true;
    this.reactionIncludeMicrophoneInExport = true;
  }

  private clearReactionMediaArtifacts(): void {
    for (const key of [...this.mediaArtifactUrls.keys()]) {
      if (key.includes(':reaction-on:')) this.clearMediaArtifactKey(key);
    }
  }

  private async discardReactionSession(): Promise<void> {
    this.reactionCountdownToken += 1;
    await this.reactionController?.dispose();
    this.reactionController = null;
    this.reactionPreviewStream = null;
    this.reactionDevices = [];
    this.reactionSelectedDeviceId = null;
    this.reactionMicrophoneIncluded = false;
    this.reactionSetupOpen = false;
    this.reactionCountdown = 0;
    this.reactionPhase = 'idle';
    this.clearReactionRecording();
    this.clearReactionMediaArtifacts();
  }

  private async finalizeReactionCapture(outcome: RuntimeRideResult['outcome']): Promise<void> {
    const controller = this.reactionController;
    if (!controller || this.reactionPhase !== 'recording') return;
    const token = this.reactionCaptureToken;
    this.reactionPhase = 'finalizing';
    this.reactionMessage = 'Finishing local Reaction Cam recording…';
    this.emit();
    try {
      const recording = await controller.finalize({
        projectId: this.project.projectId,
        revisionId: this.project.revisionId,
        captureOptions: {
          ...this.reactionOptions,
          includeMicrophone: this.reactionMicrophoneIncluded,
        },
        outcome,
      });
      if (token !== this.reactionCaptureToken) return;
      this.clearReactionRecording();
      this.reactionRecording = recording;
      this.reactionRawUrl = URL.createObjectURL(recording.blob);
      this.reactionPreviewStream = null;
      this.reactionPhase = 'ready';
      this.reactionIncludeInExport = outcome === 'completed';
      this.reactionIncludeMicrophoneInExport = recording.microphoneIncluded;
      this.reactionMessage =
        outcome === 'completed'
          ? 'Reaction Cam is ready to review in Share Your Ride.'
          : 'Reaction recorded locally. Complete the coaster to create a combined highlight.';
      this.emitReactionAnalytics({
        name: 'reaction_recording_completed',
        codec: recording.mimeType,
        durationSeconds: recording.durationSeconds,
        microphoneIncluded: recording.microphoneIncluded,
      });
    } catch (cause) {
      this.reactionPreviewStream = null;
      this.reactionPhase = 'error';
      this.reactionMessage =
        cause instanceof Error
          ? `${cause.message} Your regular ride files are still available.`
          : 'Reaction Cam could not finish. Your regular ride files are still available.';
      this.emitReactionAnalytics({
        name: 'reaction_recording_interrupted',
        failureCode: cause instanceof Error ? cause.name : 'finalize_failed',
      });
    }
    this.emit();
  }

  private queueMediaPersistence(
    operation: (repository: ReplayArtifactRepository) => Promise<void>,
  ): Promise<void> {
    const queued = this.mediaPersistenceQueue
      .catch(() => undefined)
      .then(async () => operation(await this.mediaStore()));
    this.mediaPersistenceQueue = queued.catch(() => undefined);
    return queued;
  }

  private mediaRenderSource() {
    const edit = this.mediaEdits?.[this.mediaSelectedAlternative];
    const posterCandidate =
      edit?.posterCandidates.find((candidate) => candidate.id === this.mediaSelectedPosterId) ??
      edit?.posterCandidates[0];
    if (!this.mediaTimeline || !edit || !posterCandidate || !this.sampler) return null;
    return {
      contentRoot: this.content,
      sampler: this.sampler,
      project: cloneProject(this.project),
      timeline: this.mediaTimeline,
      edit,
      posterCandidate,
      reducedMotion: this.cameraController.snapshot.reducedMotion,
      reaction:
        this.reactionIncludeInExport && this.reactionAvailableForExport() && this.reactionRecording
          ? {
              recording: this.reactionRecording,
              options: {
                includeReaction: true,
                includeMicrophone:
                  this.reactionIncludeMicrophoneInExport &&
                  this.reactionRecording.microphoneIncluded,
                placement: this.reactionOptions.placement,
                mirrored: this.reactionOptions.exportMirrored,
              },
            }
          : null,
    };
  }

  private mediaArtifactKey(kind: MediaArtifactKind): string {
    if (kind === 'photo' || kind === 'share_card') return kind;
    const editId = this.mediaEdits?.[this.mediaSelectedAlternative]?.id ?? 'unavailable';
    const reactionKey =
      this.reactionIncludeInExport && this.reactionAvailableForExport()
        ? `reaction-on:mic-${this.reactionIncludeMicrophoneInExport ? 'on' : 'off'}:mirror-${this.reactionOptions.exportMirrored ? 'on' : 'off'}:place-${this.reactionOptions.placement}`
        : 'reaction-off';
    return kind === 'highlight_video'
      ? `${kind}:${editId}:audio-${this.mediaIncludeAudio ? 'on' : 'off'}:${reactionKey}`
      : `${kind}:${editId}:${reactionKey}`;
  }

  private currentMediaArtifact(kind: MediaArtifactKind): MediaArtifact | null {
    return this.mediaArtifacts.get(this.mediaArtifactKey(kind)) ?? null;
  }

  private currentMediaArtifactUrl(kind: MediaArtifactKind): string | null {
    return this.mediaArtifactUrls.get(this.mediaArtifactKey(kind)) ?? null;
  }

  private setMediaArtifact(artifact: MediaArtifact): void {
    const key = this.mediaArtifactKey(artifact.kind);
    this.clearMediaArtifactKey(key);
    this.mediaArtifacts.set(key, artifact);
    this.mediaArtifactUrls.set(key, URL.createObjectURL(artifact.blob));
  }

  private clearMediaArtifact(kind: MediaArtifactKind): void {
    this.clearMediaArtifactKey(this.mediaArtifactKey(kind));
  }

  private clearMediaArtifactKey(key: string): void {
    const url = this.mediaArtifactUrls.get(key);
    if (url) URL.revokeObjectURL(url);
    this.mediaArtifactUrls.delete(key);
    this.mediaArtifacts.delete(key);
  }

  private clearAllMediaArtifacts(): void {
    for (const key of [...this.mediaArtifactUrls.keys()]) this.clearMediaArtifactKey(key);
  }

  private clearPosterThumbnails(): void {
    for (const url of this.mediaPosterThumbnailUrls.values()) URL.revokeObjectURL(url);
    this.mediaPosterThumbnailUrls.clear();
  }

  private async generateMediaVideo(kind: 'highlight_video' | 'animated_loop'): Promise<void> {
    const source = this.mediaRenderSource();
    const preferred = this.mediaCapabilities.preferredRecorder;
    const fallback = this.mediaCapabilities.fallbackRecorder;
    if (source && source.edit.shots.filter((shot) => shot.id.includes(':action:')).length < 3) {
      this.mediaStatus = 'static_only';
      this.mediaMessage =
        'This ride needs more moving action to build a good video. Your Photo and Preview Image are ready.';
      this.emit();
      return;
    }
    if (!source || !preferred || !fallback) {
      this.mediaStatus = 'static_only';
      this.mediaMessage =
        this.mediaCapabilities.staticOnlyReason ??
        'Video export is unavailable. Your Photo is still ready.';
      this.emit();
      return;
    }
    this.cancelMediaExport();
    this.mediaExportAbort = new AbortController();
    this.mediaStatus = 'encoding';
    this.mediaProgress = 0;
    this.mediaMessage =
      kind === 'animated_loop' ? 'Creating a three-second loop…' : 'Creating your 15-second video…';
    this.mediaAnalytics.emit({
      name: 'video_export_started',
      artifactType: kind,
      codec: preferred.mimeType,
      quality: 'default',
      durationSeconds: kind === 'animated_loop' ? 3 : 15,
      ...(this.mediaShareContext.publicCoasterId
        ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
        : {}),
    });
    this.emit();
    const attempts =
      kind === 'animated_loop'
        ? [
            {
              profile: preferred,
              includeAudio: false,
              includeReactionMicrophone: false,
              quality: 'default' as const,
            },
            {
              profile: fallback,
              includeAudio: false,
              includeReactionMicrophone: false,
              quality: 'fallback' as const,
            },
          ]
        : [
            {
              profile: preferred,
              includeAudio: this.mediaIncludeAudio,
              includeReactionMicrophone: this.reactionIncludeMicrophoneInExport,
              quality: 'default' as const,
            },
            {
              profile: fallback,
              includeAudio: this.mediaIncludeAudio,
              includeReactionMicrophone: this.reactionIncludeMicrophoneInExport,
              quality: 'fallback' as const,
            },
            ...(this.reactionIncludeMicrophoneInExport
              ? [
                  {
                    profile: fallback,
                    includeAudio: this.mediaIncludeAudio,
                    includeReactionMicrophone: false,
                    quality: 'fallback' as const,
                  },
                ]
              : []),
            ...(this.mediaIncludeAudio
              ? [
                  {
                    profile: fallback,
                    includeAudio: false,
                    includeReactionMicrophone: false,
                    quality: 'fallback' as const,
                  },
                ]
              : []),
          ];
    let failure: unknown = null;
    try {
      const renderer = await import('../media/render.ts');
      for (const attempt of attempts) {
        try {
          const artifact = await renderer.renderRideVideo({
            ...source,
            reaction: source.reaction
              ? {
                  ...source.reaction,
                  options: {
                    ...source.reaction.options,
                    includeMicrophone: attempt.includeReactionMicrophone,
                  },
                }
              : null,
            profile: attempt.profile,
            includeAudio: attempt.includeAudio,
            durationSeconds: kind === 'animated_loop' ? 3 : 15,
            kind,
            signal: this.mediaExportAbort.signal,
            onProgress: (progress) => {
              this.mediaProgress = progress;
              this.emit();
            },
          });
          this.setMediaArtifact(artifact);
          this.mediaStatus = 'ready';
          this.mediaProgress = 1;
          this.mediaMessage =
            kind === 'animated_loop'
              ? 'Your loop is ready to review.'
              : attempt.includeAudio || attempt.includeReactionMicrophone
                ? 'Your highlight is ready to review.'
                : 'Your highlight is ready without sound.';
          this.mediaAnalytics.emit({
            name: 'video_export_completed',
            artifactType: kind,
            codec: artifact.mimeType,
            quality: attempt.quality,
            durationSeconds: artifact.durationSeconds,
            ...(this.mediaShareContext.publicCoasterId
              ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
              : {}),
          });
          if (source.reaction) {
            this.emitReactionAnalytics({
              name: 'reaction_export_completed',
              codec: artifact.mimeType,
              durationSeconds: artifact.durationSeconds,
              microphoneIncluded: attempt.includeReactionMicrophone,
            });
          }
          return;
        } catch (cause) {
          if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
          failure = cause;
          this.mediaProgress = 0;
          this.mediaMessage =
            attempt.quality === 'default'
              ? 'Trying a lighter video profile…'
              : attempt.includeAudio || attempt.includeReactionMicrophone
                ? 'Trying once more without sound…'
                : 'Video export did not complete.';
          this.emit();
        }
      }
      throw failure ?? new Error('Video export did not complete.');
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === 'AbortError') {
        this.mediaStatus = 'cancelled';
        this.mediaMessage = 'Video generation cancelled. Your Photo is still ready.';
      } else {
        this.mediaStatus = 'error';
        this.mediaMessage = 'Video export failed. Your Photo and Preview Image are still ready.';
        this.mediaAnalytics.emit({
          name: 'video_export_failed',
          artifactType: kind,
          failureCode: cause instanceof Error ? cause.name : 'unknown',
          ...(this.mediaShareContext.publicCoasterId
            ? { publicCoasterId: this.mediaShareContext.publicCoasterId }
            : {}),
        });
        if (source.reaction)
          this.emitReactionAnalytics({
            name: 'reaction_export_failed',
            failureCode: cause instanceof Error ? cause.name : 'unknown',
          });
      }
    } finally {
      this.mediaExportAbort = null;
      this.emit();
    }
  }

  private async prepareMediaTimeline(summary: RideSummary): Promise<void> {
    if (summary.outcome !== 'completed') return;
    const token = ++this.mediaGenerationToken;
    try {
      const [{ createRideReplayTimeline, verifyTimelineAgainstRide }, { createHighlightEdits }] =
        await Promise.all([import('../media/timeline.ts'), import('../media/highlight.ts')]);
      const timeline = await createRideReplayTimeline({
        project: cloneProject(this.project),
        summary,
        shareContext: this.mediaShareContext,
      });
      const verification = verifyTimelineAgainstRide(timeline, summary);
      if (!verification.ok) throw new Error(verification.reason ?? 'Replay verification failed.');
      if (token !== this.mediaGenerationToken || this.mediaRequiresNewRide) return;
      this.clearAllMediaArtifacts();
      this.mediaTimeline = timeline;
      this.mediaTimelineSource = 'live';
      this.mediaEdits = createHighlightEdits(timeline, {
        sampler: this.sampler!,
        reducedMotion: this.cameraController.snapshot.reducedMotion,
      });
      this.mediaSelectedAlternative = 0;
      this.mediaSelectedPosterId = this.mediaEdits[0].posterCandidates[0]?.id ?? null;
      this.mediaStatus = this.mediaCapabilities.preferredRecorder ? 'idle' : 'static_only';
      this.mediaMessage = 'Ride complete. Your Photo and highlight are ready to create.';
      const savedLocally = this.drafts.some((draft) => draft.projectId === timeline.projectId);
      if (savedLocally || this.mediaShareContext.publicCoasterId)
        await this.queueMediaPersistence((repository) => {
          if (token !== this.mediaGenerationToken || this.mediaRequiresNewRide)
            return Promise.resolve();
          return repository.save(timeline);
        }).catch(() => undefined);
    } catch (cause) {
      if (token !== this.mediaGenerationToken) return;
      this.mediaTimeline = null;
      this.mediaTimelineSource = null;
      this.mediaEdits = null;
      this.mediaStatus = 'error';
      this.mediaMessage =
        cause instanceof Error ? cause.message : 'Replay media could not be prepared.';
    }
    this.emit();
  }

  private async restoreMediaTimeline(project: CoasterProject): Promise<void> {
    const token = ++this.mediaRestoreToken;
    const timeline = await this.mediaStore()
      .then((repository) => repository.load(project.projectId, project.revisionId))
      .catch(() => null);
    if (token !== this.mediaRestoreToken || this.project.projectId !== project.projectId) return;
    if (!timeline) return;
    const fingerprint = await createVisualProjectFingerprint(project);
    if (token !== this.mediaRestoreToken || timeline.projectFingerprint !== fingerprint) return;
    this.mediaTimeline = {
      ...timeline,
      attribution: {
        ...timeline.attribution,
        coasterTitle: project.metadata.title,
        creatorDisplayName:
          this.mediaShareContext.creatorDisplayName || project.metadata.creator.displayName,
        publicCoasterId: this.mediaShareContext.publicCoasterId,
        publicUrl: this.mediaShareContext.publicUrl,
      },
    };
    this.mediaTimelineSource = 'restored';
    this.mediaRequiresNewRide = false;
    const { createHighlightEdits } = await import('../media/highlight.ts');
    this.mediaEdits = createHighlightEdits(this.mediaTimeline, {
      sampler: this.sampler!,
      reducedMotion: this.cameraController.snapshot.reducedMotion,
    });
    this.mediaSelectedPosterId = this.mediaEdits[0].posterCandidates[0]?.id ?? null;
    this.mediaStatus = this.mediaCapabilities.preferredRecorder ? 'idle' : 'static_only';
    this.mediaMessage = 'The last completed ride was restored for this coaster.';
    this.emit();
  }

  private resetMediaForProject(project: CoasterProject): void {
    this.mediaRestoreToken += 1;
    this.mediaGenerationToken += 1;
    this.cancelMediaExport();
    this.clearAllMediaArtifacts();
    this.mediaTimeline = null;
    this.mediaTimelineSource = null;
    this.mediaEdits = null;
    this.mediaOpen = false;
    this.mediaSelectedAlternative = 0;
    this.mediaSelectedPosterId = null;
    this.clearPosterThumbnails();
    this.mediaPosterGenerationToken += 1;
    this.mediaShareOutcome = 'idle';
    this.mediaStatus = 'idle';
    this.mediaProgress = 0;
    this.mediaRequiresNewRide = false;
    this.mediaMessage = 'Complete a ride to create a highlight.';
    this.mediaShareContext = initialMediaShareContext(project);
    void this.discardReactionSession();
  }

  private replaceProject(project: CoasterProject): void {
    if (this.stationPlacementActive) {
      this.stationPlacementActive = false;
      this.controls.enabled = true;
      this.canvas.classList.remove('is-placing-station');
    }
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    this.lastPlacedPresetId = null;
    this.lastPlacementRevision = null;
    this.finishPreviewToken += 1;
    this.finishChecking = false;
    this.buildSelection = {
      shapeId: 'straight',
      parameters: { length: 'medium' },
      trackType: 'normal',
    };
    this.buildOptions = {
      ...this.buildOptions,
      length: 'medium',
      turnDegrees: 45,
      radius: 'medium',
      pitchTargetDegrees: 30,
      bankTargetDegrees: -30,
    };
    if (this.pendingRebuild !== null) {
      cancelAnimationFrame(this.pendingRebuild);
      this.pendingRebuild = null;
    }
    const currentProject = cloneProject(project);
    if (currentProject.track.nodes.length === 0) {
      this.stationPlacementPosition = { x: -32, y: 4, z: 16 };
      this.stationPlacementHeading = 0;
    }
    this.resetMediaForProject(currentProject);
    this.session.replaceProject(currentProject);
    this.rebuildProjectVisual();
    this.lastValidProject = cloneProject(currentProject);
    void this.restoreMediaTimeline(currentProject);
  }

  private moveNode(nodeId: string, position: THREE.Vector3): void {
    const node = this.project.track.nodes.find((candidate) => candidate.id === nodeId);
    if (!node) return;
    const bounds = projectPlotBounds(this.project);
    const result = moveNodes(
      this.project,
      this.session.selection.nodeIds.length ? this.session.selection.nodeIds : [nodeId],
      {
        x: THREE.MathUtils.clamp(position.x, bounds.minX, bounds.maxX) - node.position.x,
        y: 0,
        z: THREE.MathUtils.clamp(position.z, bounds.minZ, bounds.maxZ) - node.position.z,
      },
      this.snappingEnabled,
    );
    if (result.ok) this.previewProject(result.project);
  }

  private markInteraction(): void {
    this.pendingInputStartedAt = performance.now();
  }

  private previewProject(project: CoasterProject, refreshHandles = true): void {
    const editor = this.session.snapshot();
    const candidate =
      editor.editorMode === 'advanced' && editor.selectionTarget === 'shape'
        ? markSectionsCustom(
            project,
            project.track.sections
              .filter(
                (section) =>
                  editor.selection.nodeIds.includes(section.fromNodeId) ||
                  editor.selection.nodeIds.includes(section.toNodeId),
              )
              .map((section) => section.id),
          )
        : project;
    this.session.preview(candidate);
    this.pendingPreviewRefreshHandles ||= refreshHandles;
    if (refreshHandles) {
      this.rebuildHandles();
      this.refreshSelectionVisuals();
      this.rebuildBuildGuides();
    }
    if (this.pendingRebuild !== null) cancelAnimationFrame(this.pendingRebuild);
    this.pendingRebuild = requestAnimationFrame(() => {
      this.pendingRebuild = null;
      const started = performance.now();
      try {
        this.rebuildProjectVisual(false, this.pendingPreviewRefreshHandles, false);
        this.pendingPreviewRefreshHandles = false;
        this.rebuildMs = performance.now() - started;
        this.lastValidProject = cloneProject(this.project);
        this.status = 'Track updated.';
      } catch {
        this.session.cancelGesture();
        this.gestureOpen = false;
        this.rebuildProjectVisual();
        this.status = "That edit couldn't produce a continuous track. Restored the last version.";
      }
      this.emit();
    });
  }

  private executeProjectOperation(
    label: string,
    project: CoasterProject,
    message: string,
    mergeKey?: string,
    selectionAfter: EditorSelectionState = this.session.selection,
  ): void {
    this.cancelAutoBankAnalysis();
    this.focusedLateralIssue = null;
    const priorSectionCount = this.project.track.sections.length;
    if (this.presetPreview) {
      this.presetPreview = null;
      this.clearPresetPreviewVisual();
    }
    const editor = this.session.snapshot();
    const candidate =
      editor.editorMode === 'advanced' &&
      editor.selectionTarget === 'shape' &&
      editor.selection.nodeIds.length
        ? markSectionsCustom(
            project,
            project.track.sections
              .filter(
                (section) =>
                  editor.selection.nodeIds.includes(section.fromNodeId) ||
                  editor.selection.nodeIds.includes(section.toNodeId),
              )
              .map((section) => section.id),
          )
        : project;
    const topology = validateTrackTopology(candidate);
    if (topology.status === 'invalid') {
      this.showOperationFailure(
        topology.issues[0]?.message ?? 'That change would break the track.',
      );
      return;
    }
    if (candidate.track.nodes.length >= 4) {
      try {
        const preflight = compileProjectTrackResult(candidate, new TrackCompiler());
        const visual = new TrackVisual(
          preflight,
          this.coasterMaterials,
          createTrackSupportContext(candidate),
        );
        visual.dispose();
      } catch {
        this.showOperationFailure('That change could not produce one continuous track.');
        return;
      }
    }
    if (!this.session.execute(label, candidate, selectionAfter, mergeKey)) return;
    this.invalidateRouteGuide();
    if (
      candidate.track.geometryKind === 'authored_grid_v2' &&
      candidate.track.sections.length !== priorSectionCount
    )
      // Piecewise samples store global-u values. A structural authored-grid
      // change intentionally invalidates the shared render compiler cache.
      this.compiler.clear();
    const rebuildStarted = performance.now();
    this.rebuildProjectVisual();
    this.rebuildMs = performance.now() - rebuildStarted;
    this.lastValidProject = cloneProject(candidate);
    this.status = message;
    this.scheduleAutosave();
    this.emit();
  }

  private showOperationFailure(message: string): void {
    this.status = message;
    this.emit();
  }

  private rebuildProjectVisual(
    runValidation = true,
    refreshHandles = true,
    refreshScenery = true,
  ): void {
    const topology = validateTrackTopology(this.project);
    if (topology.status === 'invalid')
      throw new Error(topology.issues[0]?.message ?? 'Invalid track');
    const previousVisual = this.trackVisual;
    const minimumRenderableNodes =
      this.project.track.geometryKind === 'legacy_cubic_bspline' ? 4 : 2;
    if (this.project.track.nodes.length < minimumRenderableNodes) {
      if (previousVisual) {
        this.content.remove(previousVisual.root);
        previousVisual.dispose();
      }
      this.trackVisual = null;
      this.compiledTrack = null;
      this.sampler = null;
      this.trainVisual.root.visible = false;
      this.compiler.clear();
    } else {
      const compiled = this.compileTrack([]);
      if (previousVisual) {
        this.changedSpans = previousVisual.rebuild(
          compiled,
          createTrackSupportContext(this.project),
        );
        this.trackVisual = previousVisual;
      } else {
        const nextVisual = new TrackVisual(
          compiled,
          this.coasterMaterials,
          createTrackSupportContext(this.project),
        );
        this.trackVisual = nextVisual;
        this.content.add(nextVisual.root);
        this.changedSpans = compiled.spans.length;
      }
      this.acceptCompiledTrack(compiled);
      this.trainVisual.root.visible = true;
      this.placeTrain(initialTrainReferenceDistance(this.project, compiled.sampler));
      this.geometryRevision += 1;
    }
    this.applyAppearance();
    if (refreshHandles) this.rebuildHandles();
    this.refreshSelectionVisuals();
    if (refreshScenery) this.rebuildAuthoredScenery();
    this.rebuildDebugFrames();
    if (runValidation) this.scheduleValidation();
  }

  private applyAppearance(): void {
    this.coasterMaterials.track.color.set(this.project.appearance.trackColor);
    this.coasterMaterials.support.color.set(this.project.appearance.supportColor);
    this.coasterMaterials.train.color.set(this.project.appearance.trainColor);
    this.coasterMaterials.station.color.set(this.project.appearance.stationColor);
    const theme = this.project.appearance.environmentThemeId as EnvironmentThemeId;
    if (this.scene.theme.id !== theme) this.scene.setTheme(theme);
    this.setLayerVisible('supports', this.supportsVisible);
  }

  private rebuildAuthoredScenery(): void {
    while (this.authoredSceneryRoot.children.length) {
      const child = this.authoredSceneryRoot.children.pop();
      child?.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        if (Array.isArray(object.material))
          object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      });
    }
    const material = (color: number) =>
      new THREE.MeshStandardMaterial({ color, roughness: 0.82, envMapIntensity: 0.32 });
    for (const item of this.project.scenery) {
      const root = new THREE.Group();
      root.name = `scene-item:${item.id}`;
      root.userData.sceneryId = item.id;
      if (item.assetId === 'tree') {
        const trunk = new THREE.Mesh(
          new THREE.CylinderGeometry(0.28, 0.4, 3, 8),
          material(0x624737),
        );
        trunk.position.y = 1.5;
        const crown = new THREE.Mesh(new THREE.ConeGeometry(1.7, 4.5, 9), material(0x39775a));
        crown.position.y = 4.4;
        root.add(trunk, crown);
      } else if (item.assetId === 'hedge') {
        const hedge = new THREE.Mesh(new THREE.BoxGeometry(3.5, 1.8, 1.4), material(0x4e8a62));
        hedge.position.y = 0.9;
        root.add(hedge);
      } else if (item.assetId === 'lamp') {
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.1, 0.16, 3.5, 8),
          material(0x56636c),
        );
        post.position.y = 1.75;
        const globe = new THREE.Mesh(
          new THREE.SphereGeometry(0.32, 10, 8),
          new THREE.MeshStandardMaterial({
            color: 0xffdc91,
            emissive: 0xffb64a,
            emissiveIntensity: 0.6,
          }),
        );
        globe.position.y = 3.65;
        root.add(post, globe);
      } else {
        const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(1.25), material(0x727b79));
        rock.position.y = 0.8;
        root.add(rock);
      }
      root.position.set(item.position.x, item.position.y, item.position.z);
      root.userData.sceneryId = item.id;
      root.rotation.set(
        THREE.MathUtils.degToRad(item.rotationDegrees.x),
        THREE.MathUtils.degToRad(item.rotationDegrees.y),
        THREE.MathUtils.degToRad(item.rotationDegrees.z),
      );
      root.scale.set(item.scale.x, item.scale.y, item.scale.z);
      root.traverse((object) => {
        object.userData.sceneryId = item.id;
        if (!(object instanceof THREE.Mesh)) return;
        object.castShadow = true;
        object.receiveShadow = true;
      });
      this.authoredSceneryRoot.add(root);
    }
    this.authoredSceneryRoot.visible = this.sceneryVisible;
    this.refreshSelectionVisuals();
  }

  private scheduleValidation(): void {
    const revision = this.session.snapshot().revision;
    const fullValidationToken = ++this.fullValidationToken;
    this.fullValidationCoordinator.cancel();
    if (this.fullValidationTimer !== null) window.clearTimeout(this.fullValidationTimer);
    this.fullValidation = null;
    this.fullValidationRevision = -1;
    this.fullValidationRunAt = null;
    this.fullValidationPending = false;
    void this.validationCoordinator
      .analyze(cloneProject(this.project), revision)
      .then((profile) => {
        if (!profile || this.session.snapshot().revision !== revision) return;
        this.validationProfile = profile;
        this.rebuildValidationOverlay();
        if (profile.testAllowed && this.project.track.closed) {
          this.fullValidationPending = true;
          this.emit();
          this.fullValidationTimer = window.setTimeout(() => {
            this.fullValidationTimer = null;
            void this.fullValidationCoordinator
              .validate(cloneProject(this.project))
              .then((result) => {
                if (this.fullValidationToken !== fullValidationToken) return;
                this.fullValidation = result;
                this.fullValidationRevision = this.session.snapshot().revision;
                this.fullValidationRunAt = new Date().toISOString();
                if (result.metrics.outcome === 'completed') {
                  const issues = this.validationProfile.issues.filter(
                    (issue) => issue.code !== 'stall',
                  );
                  const blockerCount = issues.filter(
                    (issue) => issue.severity === 'blocker',
                  ).length;
                  const warningCount = issues.filter(
                    (issue) => issue.severity === 'warning',
                  ).length;
                  this.validationProfile = {
                    ...this.validationProfile,
                    issues,
                    blockerCount,
                    warningCount,
                    testAllowed: blockerCount === 0,
                    summary: blockerCount
                      ? `${blockerCount} issue${blockerCount === 1 ? '' : 's'} must be fixed`
                      : warningCount
                        ? `Ride complete · ${warningCount} build warning${warningCount === 1 ? '' : 's'}`
                        : `Ride complete · ${result.intensity.label} intensity`,
                  };
                }
                this.fullValidationPending = false;
                this.emit();
              })
              .catch(() => {
                if (this.fullValidationToken !== fullValidationToken) return;
                this.fullValidationPending = false;
                this.emit();
              });
          }, 450);
        }
        this.emit();
      });
  }

  private carryValidationForward(): void {
    this.validationCoordinator.cancel();
    if (this.fullValidation) this.fullValidationRevision = this.session.snapshot().revision;
    this.validationProfile = {
      ...this.validationProfile,
      revision: this.session.snapshot().revision,
    };
  }

  private rebuildValidationOverlay(): void {
    while (this.validationRoot.children.length) {
      const child = this.validationRoot.children.pop();
      child?.traverse((object) => {
        if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
        object.geometry.dispose();
        if (Array.isArray(object.material))
          object.material.forEach((material) => material.dispose());
        else object.material.dispose();
      });
    }
    const overlay = this.session.snapshot().activeOverlay;
    if (overlay === 'none' || !this.sampler || !this.validationProfile.samples.length) return;
    const points: THREE.Vector3[] = [];
    const colors: number[] = [];
    const values = this.validationProfile.samples.map((sample) => {
      if (overlay === 'speed') return sample.speedMps;
      if (overlay === 'vertical_g') return Math.abs(sample.verticalG);
      if (overlay === 'lateral_g') return Math.abs(sample.lateralG);
      if (overlay === 'curvature') return sample.curvature;
      return Number.isFinite(sample.clearanceM) ? Math.max(0, 6 - sample.clearanceM) : 0;
    });
    const maximum = Math.max(0.001, ...values);
    this.validationProfile.samples.forEach((sample, index) => {
      const trackSample = this.sampler!.sampleByDistance(sample.distanceM);
      points.push(
        new THREE.Vector3(
          trackSample.position.x,
          trackSample.position.y + 0.55,
          trackSample.position.z,
        ),
      );
      const ratio = Math.min(1, values[index]! / maximum);
      const color = new THREE.Color().setHSL((1 - ratio) * 0.34, 0.82, 0.56);
      colors.push(color.r, color.g, color.b);
    });
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const line = new THREE.Line(
      geometry,
      new THREE.LineBasicMaterial({
        vertexColors: true,
        depthTest: false,
        transparent: true,
        opacity: 0.95,
      }),
    );
    line.renderOrder = 80;
    this.validationRoot.add(line);
  }

  private compileTrack(changedIndices: readonly number[]): CompiledProjectTrack {
    return compileProjectTrackResult(this.project, this.compiler, changedIndices);
  }

  private acceptCompiledTrack(compiled: CompiledProjectTrack): void {
    this.compiledTrack = compiled;
    this.sampler = compiled.sampler;
  }

  private disposeHandle(handle: THREE.Mesh): void {
    [...handle.children].forEach((child) => {
      if (!(child instanceof THREE.Sprite)) return;
      handle.remove(child);
      const material = child.material as THREE.SpriteMaterial;
      material.map?.dispose();
      material.dispose();
    });
    handle.geometry.dispose();
    const materials = Array.isArray(handle.material) ? handle.material : [handle.material];
    materials.forEach((material) => material.dispose());
  }

  private clearHandles(): void {
    [...this.handleRoot.children].forEach((child) => {
      this.handleRoot.remove(child);
      if (child instanceof THREE.Mesh) this.disposeHandle(child);
    });
  }

  private syncPointLabel(handle: THREE.Mesh, labelText: string | null, isBuildEnd: boolean): void {
    const currentText = String(handle.userData.pointLabelText ?? '');
    const desiredText = labelText ?? '';
    if (currentText === desiredText) return;
    [...handle.children].forEach((child) => {
      if (!(child instanceof THREE.Sprite)) return;
      handle.remove(child);
      const material = child.material as THREE.SpriteMaterial;
      material.map?.dispose();
      material.dispose();
    });
    if (labelText) handle.add(this.createPointLabel(labelText, isBuildEnd));
    handle.userData.pointLabelText = desiredText;
  }

  private rebuildHandles(): void {
    const existingById = new Map<string, THREE.Mesh>();
    this.handleRoot.children.forEach((child) => {
      if (child instanceof THREE.Mesh) existingById.set(String(child.userData.nodeId), child);
    });
    const advanced = this.session.snapshot().editorMode === 'advanced';
    const buildEndId = this.project.track.closed ? null : this.project.track.nodes.at(-1)?.id;
    const liveNodeIds = new Set(this.project.track.nodes.map((node) => node.id));
    existingById.forEach((handle, nodeId) => {
      if (liveNodeIds.has(nodeId)) return;
      this.handleRoot.remove(handle);
      this.disposeHandle(handle);
      existingById.delete(nodeId);
    });
    for (const [index, node] of this.project.track.nodes.entries()) {
      const isBuildEnd = node.id === buildEndId;
      const previous = existingById.get(node.id);
      const handle =
        previous && previous.userData.isBuildEnd === isBuildEnd
          ? previous
          : new THREE.Mesh(
              new THREE.SphereGeometry(isBuildEnd ? 1.5 : 1.25, 16, 10),
              new THREE.MeshStandardMaterial({
                color: isBuildEnd ? 0x7ef5e0 : 0x5bd5ee,
                emissive: isBuildEnd ? 0x2b8f82 : 0x123b4a,
                roughness: 0.25,
              }),
            );
      if (previous && previous !== handle) {
        this.handleRoot.remove(previous);
        this.disposeHandle(previous);
      }
      handle.position.set(node.position.x, node.position.y, node.position.z);
      handle.userData.nodeId = node.id;
      handle.userData.nodeIndex = index;
      handle.userData.isBuildEnd = isBuildEnd;
      handle.castShadow = false;
      this.syncPointLabel(
        handle,
        advanced || isBuildEnd ? (isBuildEnd && !advanced ? 'END' : String(index + 1)) : null,
        isBuildEnd,
      );
      // Re-adding an existing child keeps the raycast order aligned with authoritative node order.
      this.handleRoot.add(handle);
    }
  }

  private createPointLabel(text: string, buildEnd: boolean): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = '#07131a';
      context.beginPath();
      context.arc(48, 48, 38, 0, Math.PI * 2);
      context.fill();
      context.lineWidth = 7;
      context.strokeStyle = buildEnd ? '#7ef5e0' : '#c8fff1';
      context.stroke();
      context.fillStyle = '#ffffff';
      context.font =
        text.length > 2 ? '800 27px system-ui, sans-serif' : '700 46px system-ui, sans-serif';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, 48, 50);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const label = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,
        depthWrite: false,
      }),
    );
    label.position.set(0, 2.7, 0);
    label.scale.set(buildEnd ? 4.2 : 3.5, buildEnd ? 4.2 : 3.5, 1);
    label.renderOrder = 50;
    return label;
  }

  private rebuildDebugFrames(): void {
    while (this.debugRoot.children.length) {
      const child = this.debugRoot.children[0]!;
      this.debugRoot.remove(child);
      child.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
          object.geometry.dispose();
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          materials.forEach((material) => material.dispose());
        }
      });
    }
    if (!this.sampler) return;
    const frameCount = 18;
    for (let index = 0; index < frameCount; index += 1) {
      const sample = this.sampler.sampleByDistance((index / frameCount) * this.sampler.length);
      const origin = new THREE.Vector3(sample.position.x, sample.position.y, sample.position.z);
      this.debugRoot.add(
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.up.x, sample.up.y, sample.up.z),
          origin,
          2.4,
          0x78e3cc,
          0.4,
          0.2,
        ),
        new THREE.ArrowHelper(
          new THREE.Vector3(sample.right.x, sample.right.y, sample.right.z),
          origin,
          1.8,
          0xf2c868,
          0.35,
          0.18,
        ),
      );
    }
    this.debugRoot.visible = this.debugVisible;
  }

  private placeTrain(distance: number): void {
    if (!this.sampler) return;
    this.trainVisual?.place(this.sampler, distance, this.project.train.carSpacingMeters);
  }

  private setSceneCameraFov(fov: number): void {
    if (Math.abs(this.scene.camera.fov - fov) < 0.01) return;
    this.scene.camera.fov = fov;
    this.scene.camera.updateProjectionMatrix();
  }

  private rideProgressPercent(): number {
    if (this.ridePhase === 'completed') return 100;
    if (!this.sampler?.length) return 0;
    return THREE.MathUtils.clamp((this.rideDistanceM / this.sampler.length) * 100, 0, 100);
  }

  private dispatchSecondsRemaining(): number {
    const station = this.simulationState.sectionRuntime.station as
      { enteredAt?: unknown; released?: unknown } | undefined;
    if (station?.released !== false || typeof station.enteredAt !== 'number' || !this.sampler)
      return 0;
    const section = this.sampler.sampleByDistance(this.simulationState.sRef).section;
    if (section?.type !== 'station') return 0;
    const rawHold = section.parameters?.holdSeconds;
    const holdSeconds = typeof rawHold === 'number' && Number.isFinite(rawHold) ? rawHold : 0;
    return Math.max(0, station.enteredAt + holdSeconds - this.simulationState.time);
  }

  private finishRideResult(outcome: RuntimeRideResult['outcome']): void {
    const summary = this.lastRideSummary;
    const authoritativeIntensity = this.fullValidation?.intensity;
    const trackLength = this.sampler?.length ?? 0;
    const progressAt = (distance: number) =>
      trackLength > 0 ? THREE.MathUtils.clamp((distance / trackLength) * 100, 0, 100) : 0;
    this.lastRide = {
      outcome,
      progressPercent: this.rideProgressPercent(),
      elapsedSeconds: summary?.durationSeconds ?? this.rideElapsedSeconds,
      peakSpeedMph: (summary?.topSpeed.value ?? this.peakSpeedMps) * MPH_PER_MPS,
      peakVerticalG: summary?.maxVerticalG.value ?? this.peakVerticalG,
      peakVerticalProgressPercent: summary
        ? progressAt(summary.maxVerticalG.distance)
        : this.peakVerticalProgressPercent,
      minVerticalG: summary?.minVerticalG.value ?? this.minVerticalG,
      minVerticalProgressPercent: summary
        ? progressAt(summary.minVerticalG.distance)
        : this.minVerticalProgressPercent,
      peakLateralG: summary ? Math.abs(summary.maxAbsLateralG.value) : this.peakLateralG,
      peakLateralProgressPercent: summary
        ? progressAt(summary.maxAbsLateralG.distance)
        : this.peakLateralProgressPercent,
      intensity: authoritativeIntensity
        ? {
            level: authoritativeIntensity.level,
            label: authoritativeIntensity.label,
            message: authoritativeIntensity.message,
            progressPercent: progressAt(authoritativeIntensity.strongestDistanceM),
          }
        : this.peakIntensity,
      visitedSectionTypes: [...this.visitedSectionTypes],
    };
    if (summary) {
      this.peakSpeedMps = summary.topSpeed.value;
      this.peakVerticalG = summary.maxVerticalG.value;
      this.peakVerticalProgressPercent = progressAt(summary.maxVerticalG.distance);
      this.minVerticalG = summary.minVerticalG.value;
      this.minVerticalProgressPercent = progressAt(summary.minVerticalG.distance);
      this.peakLateralG = Math.abs(summary.maxAbsLateralG.value);
      this.peakLateralProgressPercent = progressAt(summary.maxAbsLateralG.distance);
      this.rideElapsedSeconds = summary.durationSeconds;
      this.peakIntensity = this.lastRide.intensity;
    }
  }

  private endRide(outcome: 'completed' | 'stalled' | 'arrival_failed'): void {
    if (this.ridePhase !== 'running') return;
    this.ridePhase = outcome;
    this.finishRideResult(outcome);
    this.status =
      outcome === 'completed'
        ? 'Track complete! Your train finished the circuit and reached the station.'
        : outcome === 'arrival_failed'
          ? 'Station arrival failed. Add stronger brakes before the station.'
          : 'Ride stalled. Lower the next hill or add more speed.';
    if (outcome === 'completed' && this.lastRideSummary) {
      this.mediaRequiresNewRide = false;
      void this.prepareMediaTimeline(this.lastRideSummary);
    }
    if (this.reactionPhase === 'recording') void this.finalizeReactionCapture(outcome);
    this.emit();
  }

  private updateRideMetrics(frame: SimulationFrame): void {
    const sampler = this.sampler;
    if (!sampler) return;
    const next = frame.referenceState;
    this.rideDistanceM = (this.rideRunner?.progressPercent ?? 0) * sampler.length * 0.01;
    this.rideElapsedSeconds = next.time;
    const activeSection = sampler.sampleByDistance(next.sRef).section?.type ?? null;
    if (activeSection) this.visitedSectionTypes.add(activeSection);
    for (const force of frame.forces) {
      const windowedForce = force.windows['200'] ?? force;
      const progressPercent = THREE.MathUtils.clamp(
        (force.distance / sampler.length) * 100,
        0,
        100,
      );
      if (Math.abs(windowedForce.vertical) > this.peakVerticalG) {
        this.peakVerticalG = Math.abs(windowedForce.vertical);
        this.peakVerticalProgressPercent = progressPercent;
      }
      if (windowedForce.vertical < this.minVerticalG) {
        this.minVerticalG = windowedForce.vertical;
        this.minVerticalProgressPercent = progressPercent;
      }
      if (Math.abs(windowedForce.lateral) > this.peakLateralG) {
        this.peakLateralG = Math.abs(windowedForce.lateral);
        this.peakLateralProgressPercent = progressPercent;
      }
      const intensityScore = simulatedIntensityScore(windowedForce);
      if (intensityScore > this.peakIntensityScore) {
        const intensity: SimulatedIntensitySummary = classifySimulatedIntensity(windowedForce);
        this.peakIntensityScore = intensityScore;
        this.peakIntensity = { ...intensity, progressPercent };
        const ranks: Record<SimulatedIntensityLevel, number> = {
          comfortable: 0,
          noticeable: 1,
          intense: 2,
          extreme: 3,
          absurd: 4,
        };
        if (ranks[intensity.level] > ranks[this.audioIntensityLevel]) {
          this.audioIntensityLevel = intensity.level;
          if (intensity.level === 'noticeable') this.audio.emit({ type: 'intensity_warning' });
          else if (intensity.level === 'intense') this.audio.emit({ type: 'intensity_severe' });
          else if (intensity.level === 'extreme' || intensity.level === 'absurd')
            this.audio.emit({ type: 'intensity_block' });
        }
      }
    }
  }

  private syncRideEvents(): void {
    const runner = this.rideRunner;
    if (!runner) return;
    for (const event of runner.eventsSince(this.rideEventCursor)) {
      if (event.kind === 'station_dispatch') this.audio.emit({ type: 'station_dispatch' });
      else if (event.kind === 'photo') {
        this.photoFlashUntil =
          performance.now() + (this.cameraController.snapshot.reducedMotion ? 260 : 520);
        this.audio.emit({ type: 'photo_marker', sectionId: event.sectionId });
      } else if (event.kind === 'stall') this.audio.emit({ type: 'stall' });
      else if (event.kind === 'complete') {
        this.audio.emit({ type: 'station_arrival' });
        this.audio.emit({ type: 'ride_complete' });
      } else if (event.kind === 'section_enter') {
        if (this.audioSectionType === 'lift')
          this.audio.emit({ type: 'lift_exit', sectionId: this.audioSectionId });
        else if (this.audioSectionType === 'launch')
          this.audio.emit({ type: 'launch_exit', sectionId: this.audioSectionId });
        else if (this.audioSectionType === 'brake')
          this.audio.emit({ type: 'brake_exit', sectionId: this.audioSectionId });
        this.audioSectionType = event.sectionType ?? null;
        this.audioSectionId = event.sectionId;
        if (event.sectionType === 'lift')
          this.audio.emit({ type: 'lift_enter', sectionId: event.sectionId });
        else if (event.sectionType === 'launch')
          this.audio.emit({ type: 'launch_enter', sectionId: event.sectionId });
        else if (event.sectionType === 'brake')
          this.audio.emit({ type: 'brake_enter', sectionId: event.sectionId });
      }
    }
    this.rideEventCursor = runner.eventCount;
  }

  private telemetry(): RuntimeTelemetry {
    const force = this.simulationFrame?.forces[0];
    const speed = this.simulationFrame?.referenceState.velocity ?? 0;
    const activeSectionType =
      this.simulationFrame && this.sampler
        ? (this.sampler.sampleByDistance(this.simulationFrame.referenceState.sRef).section?.type ??
          null)
        : null;
    return {
      speedMps: speed,
      speedMph: speed * MPH_PER_MPS,
      verticalG: force?.vertical ?? 1,
      lateralG: force?.lateral ?? 0,
      longitudinalG: force?.longitudinal ?? 0,
      peakSpeedMph: this.peakSpeedMps * MPH_PER_MPS,
      activeSectionType,
      activeSectionLabel: sectionLabel(activeSectionType),
    };
  }

  private diagnostics(): RuntimeDiagnostics {
    const renderer = this.scene.renderer;
    const info = renderer instanceof THREE.WebGLRenderer ? renderer.info : null;
    const continuity = this.trackVisual?.continuity() ?? {
      maxRailGapMeters: 0,
      maxRailNormalAngleDegrees: 0,
      maxTieSpacingMeters: 0,
    };
    return {
      qualityTier: this.scene.quality.tier,
      frameMsP50: percentile(this.frameTimes, 0.5),
      frameMsP95: percentile(this.frameTimes, 0.95),
      inputMsP50: percentile(this.inputLatencies, 0.5),
      inputMsP95: percentile(this.inputLatencies, 0.95),
      rebuildMs: this.rebuildMs,
      changedSpans: this.changedSpans,
      geometryRevision: this.geometryRevision,
      trackLengthM: this.sampler?.length ?? 0,
      sampleCount: this.sampler?.table.count ?? 0,
      rendererGeometries: info?.memory.geometries ?? 0,
      rendererTextures: info?.memory.textures ?? 0,
      rideCycles: this.rideCycles,
      retainedTraceSamples: this.lastRideSummary?.trace.length ?? 0,
      retainedMediaSamples: this.mediaTimeline?.trace.length ?? 0,
      mediaArtifactUrls: this.mediaArtifactUrls.size,
      mediaExportActive: Boolean(this.mediaExportAbort),
      reactionActiveTracks: this.reactionController?.diagnostics.activeTracks ?? 0,
      reactionRecorderActive: this.reactionController?.diagnostics.recorderActive ?? false,
      reactionRetainedBytes:
        this.reactionRecording?.blob.size ??
        this.reactionController?.diagnostics.retainedBytes ??
        0,
      validationActive:
        this.fullValidationCoordinator.diagnostics.active +
        this.autoBankValidationCoordinator.diagnostics.active,
      validationQueued:
        this.fullValidationCoordinator.diagnostics.queued +
        this.autoBankValidationCoordinator.diagnostics.queued,
      audioActiveVoices: this.audio.snapshot.activeVoices,
      listenerCount: this.listeners.size,
      maxRailSeamGapM: continuity.maxRailGapMeters,
      maxRailSeamAngleDegrees: continuity.maxRailNormalAngleDegrees,
      maxTieSpacingM: continuity.maxTieSpacingMeters,
    };
  }

  private readonly animate = (time: number): void => {
    if (this.disposed) return;
    const frameMs = Math.min(100, time - this.previousTime);
    this.previousTime = time;
    this.frameTimes.push(frameMs);
    if (this.frameTimes.length > 600) this.frameTimes.shift();
    if (
      (this.mode === 'ride-first-person' || this.mode === 'ride-third-person') &&
      this.ridePhase === 'running' &&
      !this.diagnosticRideCompletionActive &&
      this.sampler
    ) {
      this.accumulator = Math.min(0.25, this.accumulator + (frameMs / 1000) * this.testSpeed);
      while (this.accumulator >= FIXED_PHYSICS_STEP) {
        if (!this.rideRunner) break;
        this.simulationFrame = this.rideRunner.step(FIXED_PHYSICS_STEP);
        this.simulationState = this.simulationFrame.referenceState;
        this.peakSpeedMps = Math.max(this.peakSpeedMps, this.simulationState.velocity);
        this.updateRideMetrics(this.simulationFrame);
        this.syncRideEvents();
        this.accumulator -= FIXED_PHYSICS_STEP;
        if (this.rideRunner.result !== 'running') {
          this.lastRideSummary = this.rideRunner.summary();
          this.endRide(
            this.rideRunner.result === 'completed'
              ? 'completed'
              : this.rideRunner.result === 'arrival_failed'
                ? 'arrival_failed'
                : 'stalled',
          );
        }
        if (this.ridePhase !== 'running') break;
      }
      if (this.simulationFrame) {
        this.trainVisual.placeFrame(this.simulationFrame);
        this.cameraController.updateRide(
          this.simulationFrame,
          this.sampler,
          this.mode === 'ride-first-person' ? 'first-person' : 'third-person',
        );
        const sectionType =
          this.sampler.sampleByDistance(this.simulationFrame.referenceState.sRef).section?.type ??
          null;
        this.audio.update({
          speedMetersPerSecond: this.simulationFrame.referenceState.velocity,
          sectionType,
          running: this.ridePhase === 'running',
        });
      }
    } else if (this.mode === 'replay' && this.sampler) {
      const replay = this.lastRideSummary ?? this.mediaTimeline;
      if (!replay?.trace.length) {
        this.openEditor();
        this.animationFrame = requestAnimationFrame(this.animate);
        return;
      }
      if (!this.replayPaused)
        this.replayElapsedSeconds = Math.min(
          replay.durationSeconds,
          this.replayElapsedSeconds + (frameMs / 1000) * this.testSpeed,
        );
      const sample = sampleReplayTraceAtTime(replay.trace, this.replayElapsedSeconds, {
        trackLength: this.sampler.length,
        closed: this.sampler.closed,
      });
      if (!sample) {
        this.openEditor();
        this.animationFrame = requestAnimationFrame(this.animate);
        return;
      }
      this.placeTrain(sample.distance);
      this.cameraController.updateCinematic(
        this.sampler,
        sample.distance,
        this.replayElapsedSeconds,
        frameMs / 1000,
      );
      this.audio.update({
        speedMetersPerSecond: sample.speed,
        sectionType: sample.sectionType,
        running: !this.replayPaused,
      });
      if (this.replayElapsedSeconds >= replay.durationSeconds) this.replayPaused = true;
    } else {
      this.controls.update();
    }
    this.scene.render();
    if (this.pendingInputStartedAt !== null) {
      this.inputLatencies.push(Math.max(0, performance.now() - this.pendingInputStartedAt));
      if (this.inputLatencies.length > 120) this.inputLatencies.shift();
      this.pendingInputStartedAt = null;
    }
    if (Math.floor(time / 100) !== Math.floor((time - frameMs) / 100)) this.emit();
    this.animationFrame = requestAnimationFrame(this.animate);
  };

  private resize(): void {
    const bounds = this.canvas.getBoundingClientRect();
    this.scene.resize(bounds.width, bounds.height);
  }

  private async initializeRepository(): Promise<void> {
    const initialized = await this.repository.initialize();
    this.persistenceAvailable = initialized.ok;
    if (!initialized.ok) this.status = 'Saving is off. You can still build and ride.';
    const lastOpened = await this.repository.getPreference<string>('lastOpenedProjectId');
    if (lastOpened.ok) this.lastOpenedProjectId = lastOpened.value;
    const tutorial = await this.repository.getPreference<{ visible: boolean; step: 1 | 2 | 3 }>(
      'tutorial',
    );
    if (tutorial.ok && tutorial.value) {
      this.tutorialVisible = tutorial.value.visible;
      this.tutorialStep = tutorial.value.step;
    }
    const rideSettings = await this.repository.getPreference<{
      cameraFov: number;
      cameraShake: number;
      reducedMotion: boolean;
      masterVolume: number;
      muted: boolean;
      testSpeed?: TestSpeed;
    }>('rideSettings');
    if (rideSettings.ok && rideSettings.value) {
      this.cameraController.setSettings({
        fieldOfView: rideSettings.value.cameraFov,
        shake: rideSettings.value.cameraShake,
        reducedMotion: rideSettings.value.reducedMotion,
      });
      this.audio.setSettings({
        masterVolume: rideSettings.value.masterVolume,
        muted: rideSettings.value.muted,
      });
      if ([0.5, 1, 2, 4].includes(rideSettings.value.testSpeed ?? 1))
        this.testSpeed = rideSettings.value.testSpeed ?? 1;
    }
    // QA browser sessions stay quiet without changing a creator's saved sound preference.
    if (this.debugVisible) this.audio.setSettings({ muted: true });
    await this.refreshDrafts();
    if (
      this.lastOpenedProjectId &&
      !this.drafts.some((draft) => draft.projectId === this.lastOpenedProjectId && !draft.corrupt)
    ) {
      this.lastOpenedProjectId = null;
      await this.repository.setPreference('lastOpenedProjectId', null);
    }
    if (initialized.ok && initialized.value.importedLegacyDrafts)
      this.status = `${initialized.value.importedLegacyDrafts} earlier draft${initialized.value.importedLegacyDrafts === 1 ? '' : 's'} upgraded.`;
    this.emit();
  }

  private async refreshDrafts(): Promise<void> {
    const listed = await this.repository.list();
    if (listed.ok) this.drafts = listed.value;
    else if (listed.error.code === 'unavailable') this.persistenceAvailable = false;
  }

  private persistRideSettings(): void {
    const camera = this.cameraController.snapshot;
    const audio = this.audio.snapshot.settings;
    void this.repository.setPreference('rideSettings', {
      cameraFov: camera.fieldOfView,
      cameraShake: camera.shake,
      reducedMotion: camera.reducedMotion,
      masterVolume: audio.masterVolume,
      muted: audio.muted,
      testSpeed: this.testSpeed,
    });
  }

  private scheduleAutosave(): void {
    if (this.persistenceMode === 'readonly') return;
    this.session.setSaveState('editing');
    this.emit();
  }

  private async flushAutosave(): Promise<boolean> {
    if (this.persistenceMode === 'readonly') return false;
    const project = cloneProject(this.project);
    const normalizedTitle = normalizedDraftTitle(project.metadata.title);
    const existingDrafts = await this.repository.list();
    if (existingDrafts.ok) {
      const collision = existingDrafts.value.find(
        (draft) =>
          draft.projectId !== project.projectId &&
          normalizedDraftTitle(draft.title) === normalizedTitle,
      );
      if (collision) {
        const base = project.metadata.title.replace(/\s+\d+\s*$/, '').trim() || 'Coaster';
        const suggestion = nextNumberedTitle(base, existingDrafts.value);
        this.session.setSaveState('error');
        this.status = `That name is already used. Try “${suggestion}”.`;
        this.emit();
        return false;
      }
    }
    if (
      project.schemaVersion === 7 &&
      this.fullValidation &&
      this.fullValidationRevision === this.session.snapshot().revision
    )
      project.validation = toAuthoritativeValidationSummary(
        project,
        this.fullValidation,
        this.fullValidationRunAt ?? new Date().toISOString(),
      );
    const sequence = ++this.saveSequence;
    this.session.setSaveState('saving');
    this.emit();
    this.saveQueue = this.saveQueue.then(async () => {
      const saved = await this.repository.save(project);
      if (sequence !== this.saveSequence) return;
      this.persistenceAvailable = saved.ok || saved.error.code !== 'unavailable';
      if (!saved.ok) {
        this.session.setSaveState('error');
        this.status = saved.error.message;
        this.emit();
        return;
      }
      const topology = validateTrackTopology(project);
      if (topology.status !== 'invalid' && project.track.nodes.length >= 4) {
        try {
          compileProjectTrackResult(project, new TrackCompiler());
          await this.repository.saveRecovery(project);
        } catch {
          // Keep the previous last-valid checkpoint when compilation fails.
        }
      }
      this.lastOpenedProjectId = project.projectId;
      await this.repository.setPreference('lastOpenedProjectId', project.projectId);
      await this.refreshDrafts();
      this.recoveredDraft = recoveredDraftIdentity(saved.value);
      this.session.setSaveState('saved');
      this.status = 'Draft saved on this device.';
      this.emit();
    });
    await this.saveQueue;
    return this.session.snapshot().saveState === 'saved';
  }

  private emitReactionAnalytics(event: ReactionAnalyticsEvent): void {
    this.reactionAnalyticsEvents.push(Object.freeze({ ...event }));
    if (this.reactionAnalyticsEvents.length > 100) this.reactionAnalyticsEvents.shift();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }
}

export function createCoasterRuntime(
  canvas: HTMLCanvasElement,
  options: {
    persistenceScope?: DraftPersistenceScope;
    reactionCamPlatformAvailable?: boolean;
    reactionCamEligibleAccount?: boolean;
    reactionCamSynthetic?: boolean;
  } = {},
): CoasterRuntime {
  return new ProductionCoasterRuntime(canvas, options);
}
